import React, { useState, useMemo, useEffect } from 'react';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Plus, 
  Check, 
  MessageCircle, 
  ArrowLeft, 
  Zap, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  Layers, 
  MapPin, 
  Navigation, 
  ExternalLink, 
  Copy, 
  Send, 
  X, 
  Bot, 
  FileText, 
  DollarSign, 
  Repeat, 
  Tag, 
  Trash2, 
  Edit3, 
  AlertCircle, 
  BellRing, 
  CheckSquare, 
  Lock, 
  Unlock, 
  ShieldAlert, 
  Coffee, 
  HeartPulse, 
  Wrench, 
  Palmtree, 
  Users, 
  Percent, 
  Split, 
  Flame, 
  LayoutGrid, 
  List, 
  Phone, 
  Scissors,
  Share2,
  Smartphone,
  Settings,
  ShieldCheck,
  BookOpen,
  RotateCw,
  Eye,
  EyeOff,
  SlidersHorizontal
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { consumeAppointmentDraft, BRIDGE_EVENTS } from '../utils/commandBridge';
import { 
  Appointment, 
  AppointmentStatus, 
  AppointmentHistoryItem, 
  CalendarServiceItem, 
  StaffMember, 
  SubscriptionClubPlan, 
  StaffCommissionReport, 
  ServicePromotion, 
  StaffPermissions 
} from '../types';
import { formatCurrencyBRL, maskPhone, formatPhone } from '../../../utils/formatUtils';
import { 
  calculateDiscountPercent, 
  calculateDiscountAmount, 
  isPromoActive, 
  getEffectivePrice, 
  getPromoUrgencyText 
} from '../../../utils/promoUtils';
import BarberQuickPosModal from './quick-pos/BarberQuickPosModal';
import { safeStorage } from '../../../utils/safeStorage';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';
import { 
  EnterpriseVerticalId, 
  ENTERPRISE_VERTICALS 
} from '../enterprise/config/enterpriseVerticals';
import { SimpleService } from '../../../types';
import { getTutorialPreset } from '../demo/tutorialDemoPresets';
import { useServiceStore } from '../../../store/useServiceStore';
import {
  WeeklyBusinessHours,
  DayKey,
  DaySchedule,
  DAY_KEYS_ORDERED,
  DAY_LABELS,
  DEFAULT_SALON_HOURS,
  parseBusinessHours,
  serializeBusinessHours,
  generateHumanSummary,
  getDaySchedule,
  generateDaySlots,
  generateDynamicDaySlots,
  getDayKeyFromDate,
  timeStringToMinutes,
  minutesToTimeString
} from '../utils/businessHoursUtils';
import {
  isStaffEligibleForService,
  filterStaffForServices,
  getStaffServiceNames
} from '../utils/staffServiceUtils';
import { getLocalDateString } from '../utils/calendarUtils';

interface AppointmentCalendarProps {
  providerName: string;
  providerPixKey?: string;
  providerCity?: string;
  logoUrl?: string;
  accentColor?: string;
  slug?: string;
  vertical?: EnterpriseVerticalId;
  appointments: Appointment[];
  staffMembers?: StaffMember[];
  subscriptionClubs?: SubscriptionClubPlan[];
  services?: SimpleService[];
  businessHours?: string;
  onSaveBusinessHours?: (hours: string) => Promise<void> | void;
  onSaveAppointment: (appointment: Appointment) => Promise<void> | void;
  onUpdateStatus: (
    appointmentId: string, 
    status: AppointmentStatus, 
    extra?: { 
      paymentStatus?: 'pending' | 'paid' | 'refunded'; 
      paymentMethod?: 'pix' | 'dinheiro' | 'cartao_credito' | 'cartao_debito' | 'outro' | null; 
      paidAt?: string | null; 
      executedAt?: string | null;
      price?: number;
      notes?: string;
      servicesList?: any[];
    }
  ) => Promise<void> | void;
  onDeductPackageSession: (appointmentId: string) => Promise<void> | void;
  onAddHistoryNote?: (appointmentId: string, note: string) => Promise<void> | void;
  onSaveStaffMember?: (staff: StaffMember) => Promise<void> | void;
  onDeleteStaffMember?: (id: string) => Promise<void> | void;
  onSaveSubscriptionClub?: (club: SubscriptionClubPlan) => Promise<void> | void;
  onDeleteSubscriptionClub?: (id: string) => Promise<void> | void;
  onOpenReceipt?: (appointment: Appointment) => void;
  onOpenDirectBooking?: () => void;
  onRefreshAppointments?: () => Promise<Appointment[] | undefined | void> | void;
  onBack: () => void;
}

const PRESET_BLOCK_REASONS = [
  { label: 'Folga / Descanso Pessoal', icon: '🌴', isAllDay: true, defaultDuration: 1440 },
  { label: 'Consulta Médica / Dentista', icon: '🩺', isAllDay: false, defaultDuration: 120 },
  { label: 'Compromisso Pessoal / Família', icon: '🏠', isAllDay: false, defaultDuration: 180 },
  { label: 'Intervalo de Almoço / Lanche', icon: '🍽️', isAllDay: false, defaultDuration: 60 },
  { label: 'Treinamento / Curso Externo', icon: '📚', isAllDay: false, defaultDuration: 240 },
  { label: 'Manutenção / Reforma no Espaço', icon: '🔧', isAllDay: true, defaultDuration: 1440 }
];

const TIME_SLOTS_30MIN = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
  '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
  '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  '17:00', '17:30', '18:00', '18:30', '19:00', '19:30', '20:00'
];

const DAYS_OF_WEEK_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const DURATION_PRESETS = [
  { label: '15m', value: '15' },
  { label: '30m', value: '30' },
  { label: '45m', value: '45' },
  { label: '1h (60m)', value: '60' },
  { label: '1h30 (90m)', value: '90' },
  { label: '2h (120m)', value: '120' }
];

const PRICE_PRESETS = [30, 35, 40, 45, 50, 60, 80, 100];

const SERVICE_NAME_SUGGESTIONS = [
  'Corte de Cabelo',
  'Corte e Barba',
  'Barba Terapia',
  'Escova e Tratamento',
  'Manicure e Pedicure',
  'Design de Sobrancelhas'
];

const timeToMinutes = (timeStr?: string): number => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
};

const calcEndTime = (startTime: string, durationMinutes: number) => {
  const total = timeToMinutes(startTime) + durationMinutes;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const minutesToTime = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isSlotCoveredBy = (appt: Appointment, slot: string): boolean => {
  if (appt.allDay || appt.durationMinutes >= 720) return true;
  const slotMin = timeToMinutes(slot);
  const startMin = timeToMinutes(appt.scheduledTime);
  const endMin = startMin + (appt.durationMinutes || 30);
  return slotMin >= startMin && slotMin < endMin;
};

export default function AppointmentCalendar({
  providerName,
  providerPixKey = '',
  providerCity = 'São Paulo',
  logoUrl,
  accentColor,
  slug: propSlug,
  vertical = 'general_corporate',
  appointments,
  staffMembers = [],
  subscriptionClubs = [],
  services,
  businessHours = 'Terça a Sábado: 08:00 às 19:00',
  onSaveBusinessHours,
  onSaveAppointment,
  onUpdateStatus,
  onDeductPackageSession,
  onAddHistoryNote,
  onSaveStaffMember,
  onDeleteStaffMember,
  onSaveSubscriptionClub,
  onDeleteSubscriptionClub,
  onOpenReceipt,
  onOpenDirectBooking,
  onRefreshAppointments,
  onBack
}: AppointmentCalendarProps) {
  const { session } = useAuth();
  const globalServices = useServiceStore(state => state.services);
  const setGlobalServices = useServiceStore(state => state.setServices);
  const saveService = useServiceStore(state => state.saveService);
  const deleteService = useServiceStore(state => state.deleteService);
  const isSavingService = useServiceStore(state => state.isSaving);
  const [isSubmittingService, setIsSubmittingService] = useState(false);
  const [copiedDirectLink, setCopiedDirectLink] = useState(false);
  const [activeTab, setActiveTab] = useState<'calendar' | 'recurring' | 'staff' | 'services' | 'commissions' | 'club' | 'link'>('calendar');
  const [isManagementDrawerOpen, setIsManagementDrawerOpen] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState<'timeline' | 'columns'>('timeline'); 
  const [selectedStaffFilter, setSelectedStaffFilter] = useState<string>('all');
  const [selectedDate, setSelectedDate] = useState<string>(getLocalDateString());
  const [dateRangeScope, setDateRangeScope] = useState<'week' | 'fortnight' | 'month'>('week');

  // Slug seguro baseado no nome do próprio negócio do usuário (nunca fixa 'studio-bella')
  const effectiveSlug = propSlug || (
    providerName 
      ? providerName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') 
      : 'minha-agenda'
  );
  const slug = effectiveSlug;

  // Equipe de Atendentes: Padrão 100% Limpo (Clean Slate para novos clientes)
  const staffList = staffMembers;

  const [isNewStaffModalOpen, setIsNewStaffModalOpen] = useState(false);
  const [newStaffName, setNewStaffName] = useState('');
  const [newStaffRole, setNewStaffRole] = useState('Profissional / Atendente');
  const [newStaffCompensationType, setNewStaffCompensationType] = useState<'commission' | 'owner_revenue' | 'fixed_salary'>('commission');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [newStaffCommission, setNewStaffCommission] = useState<number | string>(50);
  const [newStaffPin, setNewStaffPin] = useState('1234');
  const [newStaffPermissions, setNewStaffPermissions] = useState<StaffPermissions>({
    canCreateAppointments: true,
    canBlockSlots: true,
    canComplete: true,
    showClientPhone: true,
    showCommission: true
  });
  const [newStaffServiceIds, setNewStaffServiceIds] = useState<string[]>([]);

  const [editingStaff, setEditingStaff] = useState<StaffMember | null>(null);
  const [copiedStaffId, setCopiedStaffId] = useState<string | null>(null);
  const [revealedPins, setRevealedPins] = useState<Record<string, boolean>>({});
  
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // Estados de Baixa no Caixa / Anti-Fraude
  const [checkoutAppointment, setCheckoutAppointment] = useState<Appointment | null>(null);
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState<'pix' | 'dinheiro' | 'cartao_debito' | 'cartao_credito'>('pix');

  // Estados de Comanda Rápida & PDV
  const [isQuickPosOpen, setIsQuickPosOpen] = useState(false);
  const [selectedPosAppointment, setSelectedPosAppointment] = useState<Appointment | null>(null);

  // Estados de Live Sync / Atualização Automática
  const [isSyncingLive, setIsSyncingLive] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date>(new Date());
  const [newAppointmentNotification, setNewAppointmentNotification] = useState<{ clientName: string; serviceName?: string; scheduledTime?: string } | null>(null);
  const prevAppointmentIdsRef = React.useRef<Set<string>>(new Set(appointments.map(a => a.id)));

  const handleSilentRefresh = React.useCallback(async (silent = true) => {
    if (!silent) setIsSyncingLive(true);
    try {
      if (onRefreshAppointments) {
        const refreshed = await onRefreshAppointments();
        if (Array.isArray(refreshed)) {
          const currentIds = prevAppointmentIdsRef.current;
          const newlyAdded = refreshed.find(a => !currentIds.has(a.id) && a.status !== 'blocked');
          if (newlyAdded && currentIds.size > 0) {
            setNewAppointmentNotification({
              clientName: newlyAdded.clientName,
              serviceName: newlyAdded.serviceName,
              scheduledTime: newlyAdded.scheduledTime
            });
          }
          prevAppointmentIdsRef.current = new Set(refreshed.map(a => a.id));
        }
      }
      setLastSyncTime(new Date());
    } catch (err) {
      console.warn('R MAX: Erro no auto-sync da agenda:', err);
    } finally {
      if (!silent) {
        setTimeout(() => setIsSyncingLive(false), 400);
      }
    }
  }, [onRefreshAppointments]);

  // Atualiza referências quando a lista externa de agendamentos mudar
  useEffect(() => {
    prevAppointmentIdsRef.current = new Set(appointments.map(a => a.id));
  }, [appointments]);

  // Auto-dismiss do banner de novo agendamento após 8 segundos
  useEffect(() => {
    if (newAppointmentNotification) {
      const timer = setTimeout(() => setNewAppointmentNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [newAppointmentNotification]);

  // Ciclos de Live Sync: Polling Inteligente + Retorno à Aba + Supabase Realtime Isolado por Tenant
  useEffect(() => {
    const activeUserId = session?.user?.id || appointments[0]?.userId;
    if (!activeUserId) return;

    let isRealtimeSubscribed = false;

    // 1. Polling defensivo de fallback (aumentado para 90s para economizar 50% de conexões)
    // Só é acionado se a aba estiver visível e o Realtime não estiver conectado
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !isRealtimeSubscribed) {
        handleSilentRefresh(true);
      }
    }, 90000);

    // 2. Detecção de foco / visibilidade de aba com debounce (1s)
    let visibilityTimer: any = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(visibilityTimer);
        visibilityTimer = setTimeout(() => {
          handleSilentRefresh(true);
        }, 1000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);

    // 3. Supabase Realtime Channel Isolado por Tenant (com filtro user_id)
    let channel: any = null;
    try {
      channel = supabase
        .channel(`rmax_appts_${activeUserId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'appointments',
            filter: `user_id=eq.${activeUserId}`
          },
          () => {
            handleSilentRefresh(true);
          }
        )
        .subscribe((status: string) => {
          if (status === 'SUBSCRIBED') {
            isRealtimeSubscribed = true;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            isRealtimeSubscribed = false;
            // Se o Supabase rejeitar (ex: tabela sem publication de Realtime ativa), desinscreve para estancar o loop de retentativas a cada 1.8s
            if (channel) {
              supabase.removeChannel(channel);
              channel = null;
            }
          } else if (status === 'CLOSED') {
            isRealtimeSubscribed = false;
          }
        });
    } catch (realtimeErr) {
      console.warn('R MAX: Realtime indisponível:', realtimeErr);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(visibilityTimer);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [handleSilentRefresh, session?.user?.id]);

  // Estados de Horário de Atendimento do Salão (Flexível por Dia da Semana)
  const [weeklyHours, setWeeklyHours] = useState<WeeklyBusinessHours>(() => parseBusinessHours(businessHours));
  const [currentBusinessHours, setCurrentBusinessHours] = useState<string>(() => parseBusinessHours(businessHours).summary);
  const [isBusinessHoursModalOpen, setIsBusinessHoursModalOpen] = useState(false);
  const [modalHoursTab, setModalHoursTab] = useState<'grouped' | 'detailed'>('grouped');
  const [tempWeeklyHours, setTempWeeklyHours] = useState<WeeklyBusinessHours>(() => parseBusinessHours(businessHours));
  const [manualSummaryInput, setManualSummaryInput] = useState<string>('');
  const [isSavingHours, setIsSavingHours] = useState(false);

  useEffect(() => {
    if (businessHours) {
      const parsed = parseBusinessHours(businessHours);
      setWeeklyHours(parsed);
      setCurrentBusinessHours(parsed.summary);
    }
  }, [businessHours]);

  const openBusinessHoursModal = () => {
    const current = parseBusinessHours(businessHours || weeklyHours.summary);
    setTempWeeklyHours(JSON.parse(JSON.stringify(current)));
    setManualSummaryInput(current.summary);
    setIsBusinessHoursModalOpen(true);
  };

  const handleSaveWeeklyHours = async (scheduleToSave: WeeklyBusinessHours, customSummary?: string) => {
    setIsSavingHours(true);
    try {
      const finalSummary = customSummary?.trim() || generateHumanSummary(scheduleToSave.days);
      const updated: WeeklyBusinessHours = {
        ...scheduleToSave,
        summary: finalSummary
      };
      setWeeklyHours(updated);
      setCurrentBusinessHours(finalSummary);
      const serialized = serializeBusinessHours(updated);
      if (onSaveBusinessHours) {
        await onSaveBusinessHours(serialized);
      }
      setIsBusinessHoursModalOpen(false);
    } catch (err) {
      console.error('R MAX: Erro ao salvar horários:', err);
    } finally {
      setIsSavingHours(false);
    }
  };

  // Programação e slots específicos do dia selecionado
  const currentDaySchedule = useMemo(() => {
    return getDaySchedule(weeklyHours, selectedDate);
  }, [weeklyHours, selectedDate]);


  // Estados de Clubes de Assinatura: Padrão 100% Limpo (Clean Slate)
  const subscriptionPlans = subscriptionClubs;

  // Catálogo de Serviços da Agenda: Sincronizado com os serviços reais do usuário ou com persistência local
  const userServicesAsCalendarItems: CalendarServiceItem[] = useMemo(() => {
    if (globalServices.length > 0) {
      return globalServices.map((srv, idx) => {
        const matchMin = srv.description?.match(/(\d+)\s*min/);
        const parsedDuration = matchMin ? parseInt(matchMin[1], 10) : 60;
        return {
          id: srv.id || `cs_${idx + 1}`,
          name: srv.name,
          category: srv.type === 'hourly' ? 'Serviços por Hora' : 'Tabela de Serviços',
          durationMinutes: parsedDuration,
          price: Number(srv.value || 0),
          commissionPercent: 50,
          allowedStaffIds: srv.allowedStaffIds,
          acceptOnlineBooking: true,
          promo: srv.promo
        };
      });
    }
    return [];
  }, [globalServices]);

  const [calendarServices, setCalendarServices] = useState<CalendarServiceItem[]>(() => {
    if (userServicesAsCalendarItems.length > 0) return userServicesAsCalendarItems;
    try {
      const raw = safeStorage.getItem('rmax_calendar_services');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return [];
  });
  const [selectedVerticalCategory, setSelectedVerticalCategory] = useState<string>('all');

  useEffect(() => {
    if (services) setGlobalServices(services);
  }, [services, setGlobalServices]);

  useEffect(() => {
    setCalendarServices(userServicesAsCalendarItems);
  }, [userServicesAsCalendarItems]);

  // Carregador de cenário para vídeos e gravações de tutoriais (Sob Demanda)
  const handleLoadTutorialScenario = (scenarioKey?: string) => {
    const preset = getTutorialPreset(scenarioKey || vertical || 'salao');
    setCalendarServices(preset.services);
    if (onSaveStaffMember && staffMembers.length === 0) {
      preset.staff.forEach(st => onSaveStaffMember(st));
    }
    if (onSaveSubscriptionClub && subscriptionClubs.length === 0) {
      preset.subscriptionClubs.forEach(sub => onSaveSubscriptionClub(sub));
    }
  };

  const [isNewServiceModalOpen, setIsNewServiceModalOpen] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState<string | null>(null);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceDuration, setNewServiceDuration] = useState<string>('30');
  const [newServicePrice, setNewServicePrice] = useState<string>('');
  const [newServiceCommission, setNewServiceCommission] = useState<number>(50);
  const [newServiceIsPromo, setNewServiceIsPromo] = useState(false);
  const [newServicePromoPrice, setNewServicePromoPrice] = useState<number>(80);
  const [newServicePromoTitle, setNewServicePromoTitle] = useState('Promoção da Semana');
  const [newServicePromoLimitType, setNewServicePromoLimitType] = useState<'none' | 'quantity' | 'date'>('none');
  const [newServicePromoMaxUsage, setNewServicePromoMaxUsage] = useState<number>(10);
  const [newServicePromoEndDate, setNewServicePromoEndDate] = useState<string>('');
  const [newServiceAllowedStaffIds, setNewServiceAllowedStaffIds] = useState<string[]>([]);

  // Formulário de Novo Agendamento
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState(staffList[0]?.id || '');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [scheduledTime, setScheduledTime] = useState('14:00');
  const [price, setPrice] = useState(0);
  const [notes, setNotes] = useState('');

  // Ponte Omnibar -> Agenda: consome o rascunho de agendamento e abre o modal pré-preenchido
  useEffect(() => {
    const checkDraft = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'guest';
        const draft = consumeAppointmentDraft(userId);
        if (draft && draft.clientName) {
          if (draft.scheduledDate) setSelectedDate(draft.scheduledDate);
          if (draft.scheduledTime) setScheduledTime(draft.scheduledTime);
          setClientName(draft.clientName);
          if (draft.clientPhone) setClientPhone(draft.clientPhone);
          setServiceName(draft.serviceName || '');
          if (draft.price && draft.price > 0) setPrice(draft.price);
          if (draft.durationMinutes && draft.durationMinutes > 0) setDurationMinutes(draft.durationMinutes);
          if (draft.notes) setNotes(`[Comando] ${draft.notes}`);
          setIsNewModalOpen(true);
        }
      } catch (err) {
        console.warn('[AppointmentCalendar] Erro ao consumir rascunho de agendamento:', err);
      }
    };

    checkDraft();
    window.addEventListener(BRIDGE_EVENTS.appointments, checkDraft);
    return () => window.removeEventListener(BRIDGE_EVENTS.appointments, checkDraft);
  }, []);

  // Recorrência
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [recurrenceWeeksCount, setRecurrenceWeeksCount] = useState<number>(4);

  // Bloqueio de Horário
  const [blockDate, setBlockDate] = useState(selectedDate);
  const [blockPeriodType, setBlockPeriodType] = useState<'all_day' | 'specific_time'>('all_day');
  const [blockTime, setBlockTime] = useState('14:00');
  const [blockDurationMinutes, setBlockDurationMinutes] = useState<number>(60);
  const [blockStaffId, setBlockStaffId] = useState('st_1');
  const [blockReason, setBlockReason] = useState('Folga / Descanso Pessoal');
  const [isBlockRecurring, setIsBlockRecurring] = useState(false);
  const [blockRecurrenceWeeks, setBlockRecurrenceWeeks] = useState<number>(8);

  // Filtros de Agendamento do Dia
  const dayAppointments = useMemo(() => {
    return appointments
      .filter(a => a.scheduledDate === selectedDate)
      .sort((a, b) => a.scheduledTime.localeCompare(b.scheduledTime));
  }, [appointments, selectedDate]);

  const filteredAppointments = useMemo(() => {
    let list = dayAppointments;
    if (selectedStaffFilter !== 'all') {
      list = list.filter(a => a.staffMemberId === selectedStaffFilter || !a.staffMemberId);
    }
    if (selectedVerticalCategory !== 'all') {
      list = list.filter(a => {
        const srv = (a.serviceName || '').toLowerCase();
        if (selectedVerticalCategory === 'banho_tosa') return srv.includes('banho') || srv.includes('tosa') || srv.includes('groom');
        if (selectedVerticalCategory === 'vacina') return srv.includes('vacin') || srv.includes('imuniz');
        if (selectedVerticalCategory === 'clinica') return srv.includes('consult') || srv.includes('exame') || srv.includes('cirurg') || srv.includes('retorno');
        if (selectedVerticalCategory === 'seguros') return srv.includes('segur') || srv.includes('sinistro') || srv.includes('vistor');
        if (selectedVerticalCategory === 'oficina') return srv.includes('mecanic') || srv.includes('funilar') || srv.includes('peça') || srv.includes('check');
        return true;
      });
    }
    return list;
  }, [dayAppointments, selectedStaffFilter, selectedVerticalCategory]);

  // Slots do dia recalculados dinamicamente com base nos términos de agendamentos (próximo horário vago imediato)
  const currentDaySlots = useMemo(() => {
    return generateDynamicDaySlots(weeklyHours, selectedDate, dayAppointments, 30);
  }, [weeklyHours, selectedDate, dayAppointments]);

  useEffect(() => {
    if (currentDaySlots.length > 0 && !currentDaySlots.includes(scheduledTime)) {
      setScheduledTime(currentDaySlots[0]);
    }
  }, [currentDaySlots]);


  // Atendimentos que aguardam conferência e baixa no caixa (Anti-Fraude)
  const pendingCheckoutAppts = useMemo(() => {
    return dayAppointments.filter(a => 
      a.status !== 'canceled' && 
      a.status !== 'blocked' && 
      a.paymentStatus !== 'paid' && 
      (a.status === 'completed' || a.scheduledDate < getLocalDateString() || (a.scheduledDate === getLocalDateString() && timeToMinutes(a.scheduledTime) + a.durationMinutes <= (new Date().getHours() * 60 + new Date().getMinutes())))
    );
  }, [dayAppointments]);

  // Recorrentes
  const recurringAppointments = useMemo(() => {
    return appointments.filter(a => a.isRecurring || (a.packageTotalSessions && a.packageTotalSessions > 1));
  }, [appointments]);

  const recurringClientsMap = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    recurringAppointments.forEach(appt => {
      const key = `${appt.clientName}_${appt.scheduledTime}_${appt.staffMemberId || 'all'}`;
      const list = map.get(key) || [];
      list.push(appt);
      map.set(key, list);
    });
    return map;
  }, [recurringAppointments]);

  // Gerador de Dias da Semana para o Seletor Rápido (7 dias)
  const weekDays = useMemo(() => {
    const base = new Date(selectedDate + 'T12:00:00');
    const result = [];
    for (let i = -3; i <= 3; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const iso = getLocalDateString(d);
      const dayOfWeek = DAYS_OF_WEEK_SHORT[d.getDay()];
      const dayNum = d.getDate();
      const monthShort = MONTHS_SHORT[d.getMonth()];
      const dayAppts = appointments.filter(a => a.scheduledDate === iso && a.status !== 'canceled');
      const isToday = iso === getLocalDateString();
      result.push({
        date: iso,
        dayOfWeek,
        dayNum,
        monthShort,
        hasAppts: dayAppts.length > 0,
        apptsCount: dayAppts.length,
        isToday,
        isSelected: iso === selectedDate
      });
    }
    return result;
  }, [selectedDate, appointments]);

  // Gerador de Dias para a Quinzena (15 dias)
  const fortnightDays = useMemo(() => {
    const base = new Date(selectedDate + 'T12:00:00');
    const result = [];
    for (let i = -2; i <= 12; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      const iso = getLocalDateString(d);
      const dayOfWeek = DAYS_OF_WEEK_SHORT[d.getDay()];
      const dayNum = d.getDate();
      const monthShort = MONTHS_SHORT[d.getMonth()];
      const dayAppts = appointments.filter(a => a.scheduledDate === iso && a.status !== 'canceled');
      const isToday = iso === getLocalDateString();
      result.push({
        date: iso,
        dayOfWeek,
        dayNum,
        monthShort,
        hasAppts: dayAppts.length > 0,
        apptsCount: dayAppts.length,
        isToday,
        isSelected: iso === selectedDate
      });
    }
    return result;
  }, [selectedDate, appointments]);

  // Total de agendamentos na quinzena
  const totalFortnightAppts = useMemo(() => {
    const dates = new Set(fortnightDays.map(f => f.date));
    return appointments.filter(a => a.scheduledDate && dates.has(a.scheduledDate) && a.status !== 'canceled').length;
  }, [fortnightDays, appointments]);

  // Gerador do Calendário Mensal Completo (Grade 7x5 / 7x6)
  const monthCalendarData = useMemo(() => {
    const base = new Date(selectedDate + 'T12:00:00');
    const year = base.getFullYear();
    const month = base.getMonth();
    const monthName = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(base);
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    const firstDay = new Date(year, month, 1, 12, 0, 0);
    const firstDayWeekIndex = firstDay.getDay(); // 0 = Dom, 1 = Seg...
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevMonthLastDate = new Date(year, month, 0).getDate();

    const days = [];

    // Preenche dias do mês anterior
    for (let i = firstDayWeekIndex - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDate - i, 12, 0, 0);
      const iso = getLocalDateString(d);
      const dayAppts = appointments.filter(a => a.scheduledDate === iso && a.status !== 'canceled');
      days.push({
        date: iso,
        dayNum: d.getDate(),
        isCurrentMonth: false,
        isToday: iso === getLocalDateString(),
        isSelected: iso === selectedDate,
        hasAppts: dayAppts.length > 0,
        apptsCount: dayAppts.length
      });
    }

    // Dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i, 12, 0, 0);
      const iso = getLocalDateString(d);
      const dayAppts = appointments.filter(a => a.scheduledDate === iso && a.status !== 'canceled');
      days.push({
        date: iso,
        dayNum: i,
        isCurrentMonth: true,
        isToday: iso === getLocalDateString(),
        isSelected: iso === selectedDate,
        hasAppts: dayAppts.length > 0,
        apptsCount: dayAppts.length
      });
    }

    // Completa os dias finais da última semana para múltiplos de 7
    const remainder = days.length % 7;
    if (remainder > 0) {
      const fillCount = 7 - remainder;
      for (let i = 1; i <= fillCount; i++) {
        const d = new Date(year, month + 1, i, 12, 0, 0);
        const iso = getLocalDateString(d);
        const dayAppts = appointments.filter(a => a.scheduledDate === iso && a.status !== 'canceled');
        days.push({
          date: iso,
          dayNum: i,
          isCurrentMonth: false,
          isToday: iso === getLocalDateString(),
          isSelected: iso === selectedDate,
          hasAppts: dayAppts.length > 0,
          apptsCount: dayAppts.length
        });
      }
    }

    const monthPrefix = `${year}-${String(month + 1).padStart(2, '0')}`;
    const totalMonthAppts = appointments.filter(a => a.scheduledDate && a.scheduledDate.startsWith(monthPrefix) && a.status !== 'canceled').length;

    return {
      year,
      month,
      monthName: capitalizedMonth,
      days,
      totalMonthAppts
    };
  }, [selectedDate, appointments]);

  // Navegação de Datas
  const handleShiftDate = (days: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setDate(d.getDate() + days);
    setSelectedDate(getLocalDateString(d));
  };

  const handleShiftMonth = (months: number) => {
    const d = new Date(selectedDate + 'T12:00:00');
    d.setMonth(d.getMonth() + months);
    setSelectedDate(getLocalDateString(d));
  };

  const handleShiftPeriod = (direction: -1 | 1) => {
    if (dateRangeScope === 'month') {
      handleShiftMonth(direction);
    } else if (dateRangeScope === 'fortnight') {
      handleShiftDate(direction * 7);
    } else {
      handleShiftDate(direction);
    }
  };

  const handleSelectServiceFromCatalog = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    const found = calendarServices.find(s => s.id === serviceId);
    if (found) {
      setServiceName(found.name);
      setDurationMinutes(found.durationMinutes);
      const effectivePrice = getEffectivePrice(found.price, found.promo, selectedDate);
      setPrice(effectivePrice);
    }
  };

  const openNewForSlot = (time: string, staffId?: string) => {
    setScheduledTime(time);
    if (staffId && staffId !== 'all') {
      setSelectedStaffId(staffId);
    }
    setIsNewModalOpen(true);
  };

  const handleCreateAppointment = async () => {
    if (!clientName.trim() || !serviceName.trim()) {
      alert('Por favor, informe o nome do cliente e o serviço.');
      return;
    }

    const assignedStaff = staffList.find(s => s.id === selectedStaffId) || staffList[0];
    const recurringGroupId = isRecurring ? `rec_group_${Date.now()}` : undefined;
    const totalSessions = isRecurring ? recurrenceWeeksCount : 1;

    if (isRecurring) {
      const baseDate = new Date(selectedDate + 'T12:00:00');
      const stepDays = recurrenceFrequency === 'weekly' ? 7 : recurrenceFrequency === 'biweekly' ? 14 : 28;

      for (let i = 0; i < recurrenceWeeksCount; i++) {
        const currentDate = new Date(baseDate);
        currentDate.setDate(baseDate.getDate() + (i * stepDays));
        const formattedDate = getLocalDateString(currentDate);

        const newAppt: Appointment = {
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `appt_${Date.now()}_${i}`,
          userId: session?.user?.id || 'guest',
          clientName: clientName.trim(),
          clientPhone: clientPhone.trim(),
          clientAddress: clientAddress.trim() || undefined,
          serviceName: serviceName.trim(),
          serviceId: selectedServiceId || undefined,
          staffMemberId: assignedStaff.id,
          staffMemberName: assignedStaff.name,
          commissionPercent: (assignedStaff.commissionPercent !== undefined && assignedStaff.commissionPercent !== null) ? assignedStaff.commissionPercent : 50,
          durationMinutes,
          scheduledDate: formattedDate,
          scheduledTime,
          price,
          status: 'confirmed',
          packageTotalSessions: totalSessions,
          packageCurrentSession: i + 1,
          isRecurring: true,
          recurringRule: `weekly_${scheduledTime}`,
          recurringGroupId,
          notes: notes.trim() || undefined,
          historyTimeline: [
            {
              id: `hist_${Date.now()}_${i}`,
              timestamp: new Date().toISOString(),
              type: 'note',
              text: `Horário Fixo com ${assignedStaff.name} / Sessão ${i + 1} de ${totalSessions} agendada para ${formattedDate} às ${scheduledTime}.`
            }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        await onSaveAppointment(newAppt);
      }

      alert(`Horário fixo criado com sucesso para ${clientName} com ${assignedStaff.name} (${recurrenceWeeksCount} semanas às ${scheduledTime})!`);
    } else {
      // Verificação de conflito / encaixe para o atendente
      const slotStart = timeToMinutes(scheduledTime);
      const slotEnd = slotStart + durationMinutes;
      const conflictingAppt = appointments.find(a => {
        if (a.scheduledDate !== selectedDate || a.status === 'canceled') return false;
        if (a.staffMemberId && a.staffMemberId !== assignedStaff.id) return false;
        if (a.allDay || (a.durationMinutes && a.durationMinutes >= 720)) return true;
        const aStart = timeToMinutes(a.scheduledTime);
        const aEnd = aStart + (a.durationMinutes || 60);
        return Math.max(slotStart, aStart) < Math.min(slotEnd, aEnd);
      });

      if (conflictingAppt) {
        const isBlocked = conflictingAppt.status === 'blocked';
        const confirmOverlap = window.confirm(
          isBlocked
            ? `Atenção: ${assignedStaff.name} possui um BLOQUEIO (${conflictingAppt.serviceName}) neste horário (${conflictingAppt.scheduledTime} às ${calcEndTime(conflictingAppt.scheduledTime, conflictingAppt.durationMinutes)}).\n\nDeseja realizar o agendamento mesmo assim?`
            : `Atenção: ${assignedStaff.name} já possui um agendamento com "${conflictingAppt.clientName}" às ${conflictingAppt.scheduledTime} (${conflictingAppt.durationMinutes} min).\n\nDeseja realizar o encaixe mesmo assim?`
        );
        if (!confirmOverlap) return;
      }

      const newAppt: Appointment = {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `appt_${Date.now()}`,
        userId: session?.user?.id || 'guest',
        clientName: clientName.trim(),
        clientPhone: clientPhone.trim(),
        clientAddress: clientAddress.trim() || undefined,
        serviceName: serviceName.trim(),
        serviceId: selectedServiceId || undefined,
        staffMemberId: assignedStaff.id,
        staffMemberName: assignedStaff.name,
        commissionPercent: (assignedStaff.commissionPercent !== undefined && assignedStaff.commissionPercent !== null) ? assignedStaff.commissionPercent : 50,
        durationMinutes,
        scheduledDate: selectedDate,
        scheduledTime,
        price,
        status: 'confirmed',
        notes: notes.trim() || undefined,
        historyTimeline: [
          {
            id: `hist_${Date.now()}`,
            timestamp: new Date().toISOString(),
            type: 'note',
            text: `Agendamento criado com ${assignedStaff.name} para ${serviceName.trim()} às ${scheduledTime}.`
          }
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onSaveAppointment(newAppt);
    }

    setIsNewModalOpen(false);
    setClientName('');
    setClientPhone('');
    setServiceName('');
    setPrice(0);
    setDurationMinutes(60);
    setNotes('');
    setRecurrenceFrequency('weekly');
    setRecurrenceWeeksCount(4);
    setIsRecurring(false);
  };

  // Tratamento para bloquear horários do atendente (Folgas, Consultas, Almoço)
  const handleCreateBlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!blockReason.trim()) return;

    const assignedStaff = staffList.find(s => s.id === blockStaffId) || staffList[0];
    const weeksCount = isBlockRecurring ? blockRecurrenceWeeks : 1;
    const baseDate = new Date(blockDate + 'T12:00:00');
    const recurringGroupId = isBlockRecurring ? `block_group_${Date.now()}` : undefined;
    const isAllDay = blockPeriodType === 'all_day';

    for (let i = 0; i < weeksCount; i++) {
      const currentDate = new Date(baseDate);
      currentDate.setDate(baseDate.getDate() + (i * 7));
      const formattedDate = getLocalDateString(currentDate);

      const blockedAppt: Appointment = {
        id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `block_${Date.now()}_${i}`,
        userId: session?.user?.id || 'guest',
        clientName: `🔒 ${isAllDay ? 'Folga' : 'Bloqueio'}: ${assignedStaff.name}`,
        serviceName: blockReason.trim(),
        staffMemberId: assignedStaff.id,
        staffMemberName: assignedStaff.name,
        allDay: isAllDay,
        durationMinutes: isAllDay ? 1440 : blockDurationMinutes,
        scheduledDate: formattedDate,
        scheduledTime: isAllDay ? '08:00' : blockTime,
        price: 0,
        status: 'blocked',
        isRecurring: isBlockRecurring,
        recurringRule: isBlockRecurring ? (isAllDay ? 'weekly_allday' : `weekly_${blockTime}`) : undefined,
        recurringGroupId,
        notes: `${isAllDay ? 'Folga / Ausência o Dia Todo' : 'Bloqueio de Horário'}: ${assignedStaff.name} - ${blockReason.trim()}${isBlockRecurring ? ` (Folga semanal fixa ${i + 1}/${weeksCount})` : ''}. Indisponível no Chatbot.`,
        historyTimeline: [
          {
            id: `hist_${Date.now()}_${i}`,
            timestamp: new Date().toISOString(),
            type: 'note',
            text: isAllDay 
              ? `Folga o Dia Todo registrada para ${assignedStaff.name}: "${blockReason.trim()}" na data ${formattedDate}${isBlockRecurring ? ` - Semana ${i + 1} de ${weeksCount}` : ''}.`
              : `Horário de ${assignedStaff.name} bloqueado: "${blockReason.trim()}" das ${blockTime} às ${calcEndTime(blockTime, blockDurationMinutes)} (${blockDurationMinutes} min)${isBlockRecurring ? ` - Semana ${i + 1} de ${weeksCount}` : ''}.`
          }
        ],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await onSaveAppointment(blockedAppt);
    }

    setIsBlockModalOpen(false);
    setIsBlockRecurring(false);
  };

  const handleAddStaffMember = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStaffName.trim()) return;

    const colors = ['rose', 'purple', 'amber', 'emerald', 'blue', 'teal'];
    const randomColor = colors[staffList.length % colors.length];
    const generatedToken = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID().replace(/-/g, '') 
      : Math.random().toString(36).substring(2) + Date.now().toString(36);

    const compType = newStaffCompensationType || 'commission';
    const parsedCommission = typeof newStaffCommission === 'number' ? newStaffCommission : (Number(newStaffCommission) || 0);
    const finalCommission = compType === 'commission' ? Math.min(100, Math.max(0, parsedCommission)) : 0;

    const generatedId = typeof crypto !== 'undefined' && crypto.randomUUID 
      ? crypto.randomUUID() 
      : `st_${Date.now()}`;

    const newStaff: StaffMember = {
      id: generatedId,
      name: newStaffName.trim(),
      role: newStaffRole.trim() || (compType === 'owner_revenue' ? 'Proprietário / Dono' : 'Profissional Parceiro'),
      compensationType: compType,
      color: randomColor,
      commissionPercent: finalCommission,
      phone: newStaffPhone.trim() || undefined,
      accessToken: generatedToken,
      pinCode: newStaffPin.trim() || '1234',
      permissions: newStaffPermissions,
      serviceIds: newStaffServiceIds.length > 0 ? newStaffServiceIds : undefined,
      active: true
    };

    if (onSaveStaffMember) {
      onSaveStaffMember(newStaff);
    }
    const currentListRaw = safeStorage.getItem('rmax_local_staff_members');
    const currentList: StaffMember[] = currentListRaw ? JSON.parse(currentListRaw) : staffList;
    const updatedList = [...currentList.filter(s => s.id !== newStaff.id), newStaff];
    safeStorage.setItem('rmax_local_staff_members', JSON.stringify(updatedList));

    setIsNewStaffModalOpen(false);
    setNewStaffName('');
    setNewStaffPhone('');
    setNewStaffCompensationType('commission');
    setNewStaffCommission(50);
    setNewStaffServiceIds([]);
  };

  const handleSaveEditedStaff = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStaff || !editingStaff.name.trim()) return;

    const compType = editingStaff.compensationType || (editingStaff.commissionPercent === 0 ? 'owner_revenue' : 'commission');
    const finalComm = compType === 'commission' ? Math.min(100, Math.max(0, (editingStaff.commissionPercent ?? 0))) : 0;
    const normalizedEditingStaff: StaffMember = {
      ...editingStaff,
      compensationType: compType,
      commissionPercent: finalComm
    };

    if (onSaveStaffMember) {
      onSaveStaffMember(normalizedEditingStaff);
    }
    const currentListRaw = safeStorage.getItem('rmax_local_staff_members');
    const currentList: StaffMember[] = currentListRaw ? JSON.parse(currentListRaw) : staffList;
    const updatedList = currentList.map(s => s.id === normalizedEditingStaff.id ? normalizedEditingStaff : s);
    safeStorage.setItem('rmax_local_staff_members', JSON.stringify(updatedList));

    setEditingStaff(null);
  };

  const handleOpenNewService = () => {
    setEditingServiceId(null);
    setNewServiceName('');
    setNewServiceDuration('30');
    setNewServicePrice('');
    setNewServiceCommission(50);
    setNewServiceIsPromo(false);
    setNewServiceAllowedStaffIds([]);
    setIsNewServiceModalOpen(true);
  };

  const handleOpenEditService = (srv: CalendarServiceItem) => {
    setEditingServiceId(srv.id);
    setNewServiceName(srv.name);
    setNewServiceDuration(String(srv.durationMinutes || 30));
    setNewServicePrice(srv.price ? String(srv.price) : '');
    setNewServiceCommission(srv.commissionPercent ?? 50);
    setNewServiceIsPromo(!!srv.promo?.active);
    setNewServiceAllowedStaffIds(srv.allowedStaffIds || []);
    if (srv.promo) {
      setNewServicePromoPrice(srv.promo.promoPrice || 0);
      setNewServicePromoTitle(srv.promo.title || 'Promoção da Semana');
      setNewServicePromoLimitType(srv.promo.limitType || 'none');
      setNewServicePromoMaxUsage(srv.promo.maxUsage || 10);
      setNewServicePromoEndDate(srv.promo.endDate || '');
    }
    setIsNewServiceModalOpen(true);
  };

  const handleAddServiceToCatalog = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newServiceName.trim()) return;

    const parsedDuration = parseInt(newServiceDuration, 10) || 30;
    const parsedPrice = parseFloat(String(newServicePrice).replace(',', '.')) || 0;

    const targetId = editingServiceId || (
      (typeof crypto !== 'undefined' && crypto.randomUUID) 
        ? crypto.randomUUID() 
        : `cs_${Date.now()}`
    );

    const newSrv: CalendarServiceItem = {
      id: targetId,
      name: newServiceName.trim(),
      durationMinutes: parsedDuration,
      price: parsedPrice,
      commissionPercent: newServiceCommission,
      allowedStaffIds: newServiceAllowedStaffIds.length > 0 ? newServiceAllowedStaffIds : undefined,
      acceptOnlineBooking: true,
      promo: newServiceIsPromo ? {
        active: true,
        promoPrice: newServicePromoPrice,
        title: newServicePromoTitle,
        limitType: newServicePromoLimitType,
        maxUsage: newServicePromoMaxUsage,
        currentUsage: 0,
        endDate: newServicePromoEndDate
      } : undefined
    };

    try {
      setIsSubmittingService(true);
      await saveService({
        id: newSrv.id,
        name: newSrv.name,
        value: newSrv.price,
        type: 'fixed',
        description: `${newSrv.durationMinutes} min`,
        promo: newSrv.promo,
        allowedStaffIds: newSrv.allowedStaffIds,
        createdAt: new Date().toISOString()
      });

      // Atualiza lista local imediatamente (offline-first)
      setCalendarServices(prev => {
        const idx = prev.findIndex(s => s.id === targetId);
        let updated: CalendarServiceItem[];
        if (idx >= 0) {
          updated = [...prev];
          updated[idx] = newSrv;
        } else {
          updated = [...prev, newSrv];
        }
        safeStorage.setItem('rmax_calendar_services', JSON.stringify(updated));
        return updated;
      });

      // Sincroniza também os atendentes selecionados bidirecionalmente na equipe
      if (onSaveStaffMember && staffList.length > 0 && newServiceAllowedStaffIds.length > 0) {
        staffList.forEach(staff => {
          if (staff.id === 'st_any') return;
          const isSelected = newServiceAllowedStaffIds.includes(staff.id);
          const currentServices = staff.serviceIds || [];
          if (isSelected && !currentServices.includes(targetId)) {
            onSaveStaffMember({
              ...staff,
              serviceIds: [...currentServices, targetId]
            });
          }
        });
      }

      setIsNewServiceModalOpen(false);
      setEditingServiceId(null);
      setNewServiceName('');
      setNewServiceDuration('30');
      setNewServicePrice('');
      setNewServiceIsPromo(false);
      setNewServiceAllowedStaffIds([]);
    } catch (saveErr) {
      alert(`Falha ao gravar serviço: ${saveErr instanceof Error ? saveErr.message : 'Erro de conexão com o banco de dados.'}`);
    } finally {
      setIsSubmittingService(false);
    }
  };

  const handleDeleteCalendarService = async (serviceId: string) => {
    try {
      await deleteService(serviceId);
    } catch (delErr) {
      alert(`Falha ao excluir serviço: ${delErr instanceof Error ? delErr.message : 'Erro de conexão com o banco de dados.'}`);
    }
  };

  const formattedDateTitle = useMemo(() => {
    const d = new Date(selectedDate + 'T12:00:00');
    const dayName = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'][d.getDay()];
    const dayNum = d.getDate();
    const month = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][d.getMonth()];
    const isToday = selectedDate === getLocalDateString();
    return `${isToday ? 'Hoje • ' : ''}${dayName}, ${dayNum} de ${month}`;
  }, [selectedDate]);

  return (
    <div className="min-h-screen bg-zinc-50/80 text-zinc-900 pb-24 font-sans selection:bg-brand/10">
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* 1. HEADER SLIM & MODERNO (MOBILE-FIRST)                                  */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-3.5 sm:px-6 py-2.5 shadow-2xs">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-2">
          {/* Lado Esquerdo: Voltar + Logo + Título e Subtítulo */}
          <div className="flex items-center gap-2.5 min-w-0">
            <button
              type="button"
              onClick={onBack}
              className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
              title="Voltar ao Painel"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {/* Logotipo do Estabelecimento / Avatar Oficial */}
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={providerName || 'Logo'}
                className="w-9 h-9 rounded-xl object-cover border border-zinc-200 shadow-2xs shrink-0"
              />
            ) : (
              <div 
                className="w-9 h-9 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-700 font-black text-xs shrink-0"
                style={accentColor ? { color: accentColor, borderColor: `${accentColor}40` } : undefined}
              >
                {(providerName || 'R')[0]?.toUpperCase()}
              </div>
            )}

            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
                  {providerName || (vertical === 'pet_vet' 
                    ? 'Agenda da Clínica & Pet Shop' 
                    : vertical === 'medical' 
                    ? 'Agenda de Consultas' 
                    : vertical === 'technical_service' 
                    ? 'Agenda de Serviços & Vistorias' 
                    : 'Agenda & Atendimentos')}
                </h1>
                <span className="hidden sm:inline-flex text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                  {staffList.length} profissionais
                </span>
              </div>
              <p className="text-[11px] text-zinc-500 truncate font-medium">
                {formattedDateTitle}
              </p>
            </div>
          </div>

          {/* Lado Direito: Live Sync + Tour + Novo */}
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Botão e Indicador de Live Sync / Atualização Automática */}
            <button
              type="button"
              onClick={() => handleSilentRefresh(false)}
              disabled={isSyncingLive}
              className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl border border-emerald-200/80 bg-emerald-50/80 hover:bg-emerald-100 text-emerald-800 text-[11px] font-black transition-all cursor-pointer active:scale-95 shadow-2xs"
              title={`Sincronização em tempo real ativa. Última checagem às ${lastSyncTime.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}. Clique para sincronizar agora.`}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <RotateCw className={`h-3.5 w-3.5 text-emerald-700 transition-transform ${isSyncingLive ? 'animate-spin' : ''}`} />
            </button>

            <ScreenTourButton screenKey="calendar" />

            <button
              type="button"
              onClick={() => setIsManagementDrawerOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-zinc-200/90 bg-white hover:bg-zinc-50 text-zinc-800 text-xs font-black transition-all cursor-pointer active:scale-95 shadow-2xs"
              title="Menu de Gestão do Salão (Serviços, Equipe, Horários, Comissões)"
            >
              <SlidersHorizontal className="h-4 w-4 text-zinc-700" />
              <span className="hidden sm:inline">Gestão</span>
            </button>

            <button
              type="button"
              onClick={() => setIsNewModalOpen(true)}
              className="px-3.5 py-2 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer active:scale-95"
              style={{ backgroundColor: accentColor || '#059669' }}
              title="Novo Agendamento"
            >
              <Plus className="h-4 w-4 stroke-3" />
              <span className="font-extrabold">Novo</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-3.5 sm:px-6 py-4 space-y-3">
        {/* Banner de Novo Agendamento Recebido em Segundo Plano */}
        {newAppointmentNotification && (
          <div className="bg-emerald-700 text-white px-4 py-3 rounded-2xl shadow-md flex items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-300 border border-emerald-600">
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="p-1.5 bg-white/20 rounded-xl">
                <Zap className="h-4 w-4 text-white" />
              </span>
              <div className="text-xs">
                <span className="font-black uppercase tracking-wider text-emerald-200 block text-[10px]">
                  Novo Agendamento Recebido
                </span>
                <span className="font-bold truncate text-white text-xs">
                  {newAppointmentNotification.clientName} {newAppointmentNotification.serviceName ? `— ${newAppointmentNotification.serviceName}` : ''} {newAppointmentNotification.scheduledTime ? `às ${newAppointmentNotification.scheduledTime}` : ''}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setNewAppointmentNotification(null)}
              className="p-1.5 rounded-lg hover:bg-white/20 text-white cursor-pointer transition-colors"
              title="Fechar aviso"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        {/* Mensagem Toast de confirmação quando copiar o link */}
        {copiedDirectLink && (
          <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-[11px] font-bold flex items-center gap-2 animate-in fade-in duration-200">
            <Check className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>Link da sua agenda copiado com sucesso! Agora você pode colar no WhatsApp do seu cliente para ele agendar.</span>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* BARRA DE NAVEGAÇÃO DE RETORNO (APENAS NAS ABAS DE GESTÃO DO ESCRITÓRIO) */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab !== 'calendar' && (
          <div className="flex items-center justify-between bg-white border border-zinc-200/90 rounded-2xl p-3 sm:p-3.5 shadow-2xs animate-in fade-in duration-150">
            <button
              type="button"
              onClick={() => setActiveTab('calendar')}
              className="flex items-center gap-2 text-xs font-black text-zinc-700 hover:text-zinc-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" style={{ color: accentColor || '#059669' }} />
              <span>Voltar para a Agenda Diária</span>
            </button>
            <span className="text-xs font-black text-zinc-900 uppercase tracking-wider hidden sm:inline">
              {activeTab === 'services' && `Serviços (${calendarServices.length})`}
              {activeTab === 'staff' && `Equipe (${staffList.length})`}
              {activeTab === 'commissions' && 'Extrato de Comissões'}
              {activeTab === 'recurring' && 'Horários Fixos'}
              {activeTab === 'club' && 'Clubes VIP & Mensalistas'}
              {activeTab === 'link' && 'Link do Cliente'}
            </span>
            <button
              type="button"
              onClick={() => setIsManagementDrawerOpen(true)}
              className="px-2.5 py-1.5 text-xs font-bold text-zinc-700 hover:text-zinc-900 bg-zinc-100 hover:bg-zinc-200/80 rounded-xl transition-colors cursor-pointer flex items-center gap-1.5"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>Menu Gestão</span>
            </button>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 1: GRADE DO DIA COM NAVEGADOR DE DATAS E TIMELINE                    */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'calendar' && (
          <div className="space-y-3.5">
            {/* Filtros Especializados por Vertical da Empresa */}
            {vertical === 'pet_vet' && (
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 hide-scrollbar">
                <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider shrink-0 mr-1">
                  Filtrar Atendimento:
                </span>
                {[
                  { id: 'all', label: '🐾 Todos' },
                  { id: 'clinica', label: '🩺 Consultas com Veterinário' },
                  { id: 'vacina', label: '💉 Vacinação' },
                  { id: 'banho_tosa', label: '🛁 Banho & Tosa' }
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSelectedVerticalCategory(f.id)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 transition-all cursor-pointer ${
                      selectedVerticalCategory === f.id
                        ? 'bg-blue-600 text-white shadow-xs font-black'
                        : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {vertical === 'medical' && (
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 hide-scrollbar">
                <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider shrink-0 mr-1">
                  Atendimento Clínico:
                </span>
                {[
                  { id: 'all', label: '🩺 Todas as Consultas' },
                  { id: 'clinica', label: '📋 Consultas Médicas' },
                  { id: 'retorno', label: '🔄 Retornos' },
                  { id: 'exames', label: '🔬 Exames' }
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSelectedVerticalCategory(f.id)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 transition-all cursor-pointer ${
                      selectedVerticalCategory === f.id
                        ? 'bg-blue-600 text-white shadow-xs font-black'
                        : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {vertical === 'technical_service' && (
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 hide-scrollbar">
                <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider shrink-0 mr-1">
                  Atendimento de Pátio / Oficina:
                </span>
                {[
                  { id: 'all', label: '🔧 Todas as Entradas' },
                  { id: 'seguros', label: '🛡️ Vistoria de Seguradora' },
                  { id: 'oficina', label: '⚙️ Execução de Oficina' }
                ].map(f => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setSelectedVerticalCategory(f.id)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold shrink-0 transition-all cursor-pointer ${
                      selectedVerticalCategory === f.id
                        ? 'bg-blue-600 text-white shadow-xs font-black'
                        : 'bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            )}

            {/* Card de Controle: Navegador de Datas 7 Dias + Atalhos */}
            <div className="bg-white rounded-3xl border border-zinc-200/90 p-3.5 sm:p-4 shadow-2xs space-y-3">
              {/* Linha Superior: Navegador Anterior/Próximo + Input de Data + Hoje */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleShiftPeriod(-1)}
                    className="p-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-colors cursor-pointer"
                    title={dateRangeScope === 'month' ? 'Mês Anterior' : dateRangeScope === 'fortnight' ? '7 Dias Antes' : 'Dia Anterior'}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  <div className="relative flex items-center">
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={e => setSelectedDate(e.target.value)}
                      className="font-black text-xs sm:text-sm text-zinc-900 bg-zinc-50 hover:bg-zinc-100/80 px-3 py-1.5 rounded-xl border border-zinc-200 focus:outline-none focus:border-brand cursor-pointer transition-colors"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleShiftPeriod(1)}
                    className="p-2 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-colors cursor-pointer"
                    title={dateRangeScope === 'month' ? 'Próximo Mês' : dateRangeScope === 'fortnight' ? 'Próximos 7 Dias' : 'Próximo Dia'}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  {selectedDate !== getLocalDateString() && (
                    <button
                      type="button"
                      onClick={() => setSelectedDate(getLocalDateString())}
                      className="px-2.5 py-1 text-[11px] font-bold text-brand bg-emerald-50 hover:bg-emerald-100/80 rounded-lg border border-emerald-200/60 transition-colors cursor-pointer"
                    >
                      Hoje
                    </button>
                  )}
                </div>

                {/* Alternador de Visão: Linha do Tempo (Timeline) vs Colunas */}
                <div className="flex bg-zinc-100 p-1 rounded-xl border border-zinc-200 text-xs">
                  <button
                    type="button"
                    onClick={() => setCalendarViewMode('timeline')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all flex items-center gap-1 cursor-pointer ${
                      calendarViewMode === 'timeline'
                        ? 'bg-white text-zinc-900 shadow-2xs font-black'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                    title="Visão Linha do Tempo por Horários"
                  >
                    <List className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Linha do Tempo</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCalendarViewMode('columns')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all flex items-center gap-1 cursor-pointer ${
                      calendarViewMode === 'columns'
                        ? 'bg-white text-zinc-900 shadow-2xs font-black'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                    title="Visão Lado a Lado por Atendente"
                  >
                    <LayoutGrid className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Colunas Salão</span>
                  </button>
                </div>
              </div>

              {/* Seletor de Escopo de Período (Semana, Quinzena, Mês) */}
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-zinc-100 flex-wrap sm:flex-nowrap">
                <div className="flex bg-zinc-100 p-1 rounded-xl border border-zinc-200 text-xs">
                  <button
                    type="button"
                    onClick={() => setDateRangeScope('week')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      dateRangeScope === 'week'
                        ? 'bg-white text-zinc-900 shadow-2xs font-black'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                  >
                    Semana
                  </button>

                  <button
                    type="button"
                    onClick={() => setDateRangeScope('fortnight')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      dateRangeScope === 'fortnight'
                        ? 'bg-white text-zinc-900 shadow-2xs font-black'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                  >
                    Quinzena
                  </button>

                  <button
                    type="button"
                    onClick={() => setDateRangeScope('month')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      dateRangeScope === 'month'
                        ? 'bg-white text-zinc-900 shadow-2xs font-black'
                        : 'text-zinc-500 hover:text-zinc-800'
                    }`}
                  >
                    Mês
                  </button>
                </div>

                {/* Resumo sóbrio de agendamentos no período selecionado */}
                <div className="text-[11px] text-zinc-500 font-medium">
                  {dateRangeScope === 'week' && (
                    <span><strong>{weekDays.reduce((acc, d) => acc + d.apptsCount, 0)}</strong> agendamentos nesta semana</span>
                  )}
                  {dateRangeScope === 'fortnight' && (
                    <span><strong>{totalFortnightAppts}</strong> agendamentos nesta quinzena</span>
                  )}
                  {dateRangeScope === 'month' && (
                    <span><strong>{monthCalendarData.totalMonthAppts}</strong> agendamentos em {monthCalendarData.monthName}</span>
                  )}
                </div>
              </div>

              {/* 1. Visão: Semana (7 Dias) */}
              {dateRangeScope === 'week' && (
                <div className="grid grid-cols-7 gap-1 sm:gap-2 pt-1 border-t border-zinc-100 animate-in fade-in duration-150">
                  {weekDays.map(item => (
                    <button
                      key={item.date}
                      type="button"
                      onClick={() => setSelectedDate(item.date)}
                      className={`py-2 px-1 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer select-none relative ${
                        item.isSelected
                          ? 'text-white shadow-sm ring-2 ring-zinc-900/10 font-bold'
                          : item.isToday
                            ? 'bg-emerald-50 text-emerald-900 border border-emerald-200/80 hover:bg-emerald-100/60'
                            : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-100'
                      }`}
                      style={item.isSelected ? { backgroundColor: accentColor || '#18181b' } : undefined}
                    >
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${item.isSelected ? 'text-white/80' : item.isToday ? 'text-emerald-700' : 'text-zinc-400'}`}>
                        {item.isToday ? 'HOJE' : item.dayOfWeek}
                      </span>
                      <span className="text-xs sm:text-sm font-black mt-0.5">
                        {item.dayNum}
                      </span>
                      {item.hasAppts && (
                        <span className={`w-1.5 h-1.5 rounded-full mt-1 ${item.isSelected ? 'bg-white' : 'bg-brand'}`} />
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* 2. Visão: Quinzena (15 Dias) */}
              {dateRangeScope === 'fortnight' && (
                <div className="pt-1 border-t border-zinc-100 animate-in fade-in duration-150">
                  {/* Mobile: Fita deslizável com scroll snap */}
                  <div className="flex sm:hidden overflow-x-auto gap-1.5 pb-2 pt-1 snap-x hide-scrollbar overscroll-contain">
                    {fortnightDays.map(item => (
                      <button
                        key={item.date}
                        type="button"
                        onClick={() => setSelectedDate(item.date)}
                        className={`shrink-0 min-w-14 py-2 px-1.5 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer select-none snap-start relative ${
                          item.isSelected
                            ? 'text-white shadow-sm ring-2 ring-zinc-900/10 font-bold'
                            : item.isToday
                              ? 'bg-emerald-50 text-emerald-900 border border-emerald-200/80'
                              : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-100'
                        }`}
                        style={item.isSelected ? { backgroundColor: accentColor || '#18181b' } : undefined}
                      >
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${item.isSelected ? 'text-white/80' : item.isToday ? 'text-emerald-700' : 'text-zinc-400'}`}>
                          {item.isToday ? 'HOJE' : item.dayOfWeek}
                        </span>
                        <span className="text-xs font-black mt-0.5">
                          {item.dayNum}
                        </span>
                        <span className={`text-[9px] font-medium ${item.isSelected ? 'text-white/70' : 'text-zinc-400'}`}>
                          {item.monthShort}
                        </span>
                        {item.hasAppts && (
                          <div className="flex items-center gap-0.5 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${item.isSelected ? 'bg-white' : 'bg-brand'}`} />
                            <span className={`text-[9px] font-bold ${item.isSelected ? 'text-white' : 'text-zinc-500'}`}>
                              {item.apptsCount}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Tablet e PC: Grade de 2 Semanas (7 colunas x 2 linhas) */}
                  <div className="hidden sm:grid grid-cols-7 gap-1.5 sm:gap-2 pt-1">
                    {fortnightDays.slice(0, 14).map(item => (
                      <button
                        key={item.date}
                        type="button"
                        onClick={() => setSelectedDate(item.date)}
                        className={`py-2 px-1.5 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer select-none relative ${
                          item.isSelected
                            ? 'text-white shadow-sm ring-2 ring-zinc-900/10 font-bold'
                            : item.isToday
                              ? 'bg-emerald-50 text-emerald-900 border border-emerald-200/80 hover:bg-emerald-100/60'
                              : 'bg-zinc-50 text-zinc-700 hover:bg-zinc-100 border border-zinc-100'
                        }`}
                        style={item.isSelected ? { backgroundColor: accentColor || '#18181b' } : undefined}
                      >
                        <span className={`text-[10px] font-bold uppercase tracking-wider ${item.isSelected ? 'text-white/80' : item.isToday ? 'text-emerald-700' : 'text-zinc-400'}`}>
                          {item.isToday ? 'HOJE' : item.dayOfWeek}
                        </span>
                        <span className="text-xs sm:text-sm font-black mt-0.5">
                          {item.dayNum}
                        </span>
                        <span className={`text-[10px] font-medium ${item.isSelected ? 'text-white/70' : 'text-zinc-400'}`}>
                          {item.monthShort}
                        </span>
                        {item.hasAppts && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className={`w-1.5 h-1.5 rounded-full ${item.isSelected ? 'bg-emerald-400' : 'bg-brand'}`} />
                            <span className={`text-[9px] font-bold ${item.isSelected ? 'text-emerald-300' : 'text-zinc-500'}`}>
                              {item.apptsCount}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Visão: Mês (Grade Mensal Completa) */}
              {dateRangeScope === 'month' && (
                <div className="pt-2 border-t border-zinc-100 space-y-2 animate-in fade-in duration-150">
                  {/* Barra de Navegação do Mês */}
                  <div className="flex items-center justify-between px-1">
                    <button
                      type="button"
                      onClick={() => handleShiftMonth(-1)}
                      className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer flex items-center gap-1 text-xs font-bold"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Mês Anterior</span>
                    </button>
                    <span className="text-xs sm:text-sm font-black text-zinc-900">
                      {monthCalendarData.monthName} {monthCalendarData.year}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleShiftMonth(1)}
                      className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer flex items-center gap-1 text-xs font-bold"
                    >
                      <span className="hidden sm:inline">Próximo Mês</span>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Cabeçalho dos Dias da Semana */}
                  <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-black text-zinc-400 uppercase tracking-wider">
                    {DAYS_OF_WEEK_SHORT.map(d => (
                      <div key={d} className="py-1">{d}</div>
                    ))}
                  </div>

                  {/* Grade dos Dias do Mês */}
                  <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
                    {monthCalendarData.days.map((item, idx) => (
                      <button
                        key={`${item.date}_${idx}`}
                        type="button"
                        onClick={() => setSelectedDate(item.date)}
                        className={`h-10 sm:h-12 rounded-xl sm:rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer select-none relative ${
                          item.isSelected
                            ? 'bg-zinc-900 text-white font-black shadow-sm ring-2 ring-zinc-900/15'
                            : item.isToday
                              ? 'bg-emerald-50 text-emerald-950 font-black border border-emerald-300 hover:bg-emerald-100/60'
                              : item.isCurrentMonth
                                ? 'bg-zinc-50/80 text-zinc-800 hover:bg-zinc-100 border border-zinc-100 font-bold'
                                : 'bg-transparent text-zinc-300 hover:text-zinc-500 border border-transparent font-medium'
                        }`}
                      >
                        <span className="text-[11px] sm:text-xs">
                          {item.dayNum}
                        </span>
                        {item.hasAppts && (
                          <div className="flex items-center gap-0.5 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${item.isSelected ? 'bg-emerald-400' : 'bg-brand'}`} />
                            <span className={`hidden sm:inline text-[9px] font-bold ${item.isSelected ? 'text-emerald-200' : 'text-zinc-500'}`}>
                              {item.apptsCount}
                            </span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Filtro Horizontal de Atendentes (Chips com Avatar) */}
              <div className="pt-2 border-t border-zinc-100 flex items-center gap-1.5 overflow-x-auto hide-scrollbar">
                <span className="text-[11px] font-black text-zinc-400 uppercase tracking-wider shrink-0 mr-1">
                  Profissional:
                </span>

                <button
                  type="button"
                  onClick={() => setSelectedStaffFilter('all')}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                    selectedStaffFilter === 'all'
                      ? 'text-white shadow-xs font-black'
                      : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200/80'
                  }`}
                  style={selectedStaffFilter === 'all' ? { backgroundColor: accentColor || '#059669' } : undefined}
                >
                  <Users className="h-3 w-3" />
                  <span>Todos ({dayAppointments.length})</span>
                </button>

                {staffList.map(st => {
                  const count = dayAppointments.filter(a => a.staffMemberId === st.id).length;
                  const isSelected = selectedStaffFilter === st.id;

                  return (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => setSelectedStaffFilter(st.id)}
                      className={`px-3 py-1.5 rounded-xl font-bold text-xs whitespace-nowrap transition-all cursor-pointer flex items-center gap-2 border ${
                        isSelected
                          ? 'text-white shadow-xs font-black'
                          : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700 border-zinc-200/80'
                      }`}
                      style={isSelected ? { backgroundColor: accentColor || '#18181b', borderColor: accentColor || '#18181b' } : undefined}
                    >
                      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-black ${
                        isSelected ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-800'
                      }`}>
                        {st.name.charAt(0)}
                      </span>
                      <span>{st.name}</span>
                      {count > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                          isSelected ? 'bg-white/25 text-white' : 'bg-zinc-200 text-zinc-800'
                        }`}>
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ────────────────────────────────────────────────────────────────── */}
            {/* BANNER INTELIGENTE: ATENDIMENTOS AGUARDANDO BAIXA NO CAIXA (ANTI-FRAUDE) */}
            {/* ────────────────────────────────────────────────────────────────── */}
            {pendingCheckoutAppts.length > 0 && (
              <div className="bg-amber-50/90 border border-amber-200/80 rounded-2xl px-3.5 py-2 flex items-center justify-between gap-2 shadow-2xs">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
                  <span className="text-xs font-bold text-amber-950 truncate">
                    <strong>{pendingCheckoutAppts.length}</strong> {pendingCheckoutAppts.length === 1 ? 'atendimento finalizado aguardando baixa no caixa' : 'atendimentos finalizados aguardando baixa no caixa'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setCheckoutAppointment(pendingCheckoutAppts[0])}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl transition-colors cursor-pointer active:scale-95 shrink-0 flex items-center gap-1.5 shadow-2xs"
                >
                  <DollarSign className="w-3.5 h-3.5" />
                  <span>Baixar Caixa ({pendingCheckoutAppts[0].clientName})</span>
                </button>
              </div>
            )}

            {/* ────────────────────────────────────────────────────────────────── */}
            {/* MODO 1: VISÃO LINHA DO TEMPO (SLOTS DE 30 MIN COM AGENDAMENTO 1 TOQUE) */}
            {/* ────────────────────────────────────────────────────────────────── */}
            {calendarViewMode === 'timeline' && (
              <div className="bg-white rounded-3xl border border-zinc-200/90 p-4 sm:p-5 shadow-2xs space-y-3">
                <div className="flex items-center justify-between pb-2 border-b border-zinc-100">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Clock className="h-4 w-4 text-brand" />
                    <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                      Grade Horária do Dia ({filteredAppointments.length} agendamentos)
                    </h3>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                      currentDaySchedule.isOpen 
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200/60' 
                        : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
                    }`}>
                      {currentDaySchedule.isOpen ? `${currentDaySchedule.start} às ${currentDaySchedule.end}` : 'Sem horários disponíveis'}
                    </span>
                  </div>
                  <span className="text-[11px] text-zinc-400">
                    {currentDaySchedule.isOpen ? 'Toque em um horário livre para agendar' : 'Sem vagas ou expediente na grade'}
                  </span>
                </div>

                <div className="space-y-2">
                  {!currentDaySchedule.isOpen && (
                    <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-2xl text-center space-y-2 mb-3">
                      <div className="w-8 h-8 rounded-xl bg-zinc-200/70 text-zinc-700 flex items-center justify-center mx-auto">
                        <Clock className="w-4 h-4" />
                      </div>
                      <p className="text-xs font-black text-zinc-800">
                        Sem Horários Disponíveis às {DAY_LABELS[getDayKeyFromDate(selectedDate)].name}s
                      </p>
                      <p className="text-[11px] text-zinc-500 max-w-sm mx-auto font-medium">
                        A grade de atendimento está configurada sem horários livres para este dia da semana.
                      </p>
                      <button
                        type="button"
                        onClick={openBusinessHoursModal}
                        className="px-3 py-1.5 bg-white hover:bg-zinc-100 text-zinc-800 font-bold text-[11px] rounded-xl border border-zinc-200 shadow-2xs transition-all cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <Clock className="w-3 h-3 text-zinc-600" />
                        <span>Gerenciar Horários do Salão</span>
                      </button>
                    </div>
                  )}

                  {(
                    !currentDaySchedule.isOpen
                      ? Array.from(new Set(filteredAppointments.map(a => (a.scheduledTime || '').slice(0, 5)))).filter(Boolean).sort()
                      : Array.from(new Set([...currentDaySlots, ...filteredAppointments.map(a => (a.scheduledTime || '').slice(0, 5))])).filter(Boolean).sort()
                  ).map(slot => {
                    const primaryAppts = filteredAppointments.filter(a => (a.scheduledTime || '').slice(0, 5) === slot.slice(0, 5));
                    const coveringAppts = filteredAppointments.filter(a => (a.scheduledTime || '').slice(0, 5) !== slot.slice(0, 5) && isSlotCoveredBy(a, slot));
                    const allSlotAppts = [...primaryAppts, ...coveringAppts];

                    const isAllStaffFilter = selectedStaffFilter === 'all';
                    const occupiedStaffIds = new Set(allSlotAppts.map(a => a.staffMemberId).filter(Boolean));
                    const freeStaffInSlot = staffList.filter(s => s.id !== 'st_any' && !occupiedStaffIds.has(s.id));
                    const canScheduleFreeStaff = isAllStaffFilter 
                      ? (staffList.length === 0 || freeStaffInSlot.length > 0)
                      : false;

                    if (allSlotAppts.length === 0) {
                      return (
                        <div
                          key={slot}
                          onClick={() => openNewForSlot(slot, selectedStaffFilter)}
                          className="group p-2.5 rounded-2xl border border-dashed border-zinc-200/80 hover:border-brand/60 hover:bg-emerald-50/30 transition-all cursor-pointer flex items-center justify-between gap-3 text-xs"
                        >
                          <div className="flex items-center gap-3">
                            <span className="w-12 font-black text-xs text-zinc-400 group-hover:text-brand transition-colors">
                              {slot}
                            </span>
                            <span className="text-[11px] text-zinc-400 font-medium group-hover:text-emerald-700 flex items-center gap-1 transition-colors">
                              <span className="w-1.5 h-1.5 rounded-full bg-zinc-300 group-hover:bg-emerald-500" />
                              Horário livre
                            </span>
                          </div>

                          <span className="text-[11px] font-bold text-brand bg-white px-2.5 py-1 rounded-xl border border-zinc-200 group-hover:border-emerald-300 shadow-2xs opacity-70 group-hover:opacity-100 transition-all flex items-center gap-1">
                            <Plus className="h-3 w-3 stroke-3" />
                            <span>Agendar</span>
                          </span>
                        </div>
                      );
                    }

                    return (
                      <div key={slot} className="space-y-2">
                        {primaryAppts.map(appt => {
                          const isBlocked = appt.status === 'blocked';
                          const staff = staffList.find(s => s.id === appt.staffMemberId) || staffList[0];
                          const staffComm = (appt.price * (appt.commissionPercent || staff?.commissionPercent || 50)) / 100;
                          const isAllDay = appt.durationMinutes >= 420;

                          if (isBlocked) {
                            return (
                              <div
                                key={appt.id}
                                onClick={() => setSelectedAppointment(appt)}
                                className={`p-3.5 rounded-2xl cursor-pointer transition-all flex items-center justify-between gap-3 shadow-2xs ${
                                  isAllDay 
                                    ? 'bg-amber-100/80 hover:bg-amber-100 border-2 border-amber-300' 
                                    : 'bg-amber-50/70 hover:bg-amber-100/60 border border-amber-200/80'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-11 h-11 rounded-xl flex flex-col items-center justify-center shrink-0 ${
                                    isAllDay ? 'bg-amber-300 text-amber-950 font-black' : 'bg-amber-100 text-amber-900'
                                  }`}>
                                    <Lock className="h-4 w-4" />
                                    <span className="text-[10px] font-black">{appt.scheduledTime}</span>
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span className="text-[10px] font-black uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded-md">
                                        {isAllDay ? '🌴 Folga / Dia Todo' : 'Bloqueio Interno'}
                                      </span>
                                      {appt.isRecurring && (
                                        <span className="text-[9px] font-black bg-amber-200/80 text-amber-950 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          <Repeat className="h-2.5 w-2.5" />
                                          Semanal
                                        </span>
                                      )}
                                      <span className="text-xs font-bold text-amber-900">
                                        {appt.staffMemberName || staff.name}
                                      </span>
                                    </div>
                                    <p className="text-xs font-black text-amber-950 mt-0.5">{appt.serviceName}</p>
                                    <span className="text-[10px] text-amber-800 font-bold">
                                      Das {appt.scheduledTime} às {calcEndTime(appt.scheduledTime, appt.durationMinutes)} ({appt.durationMinutes} min)
                                    </span>
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  className="px-3 py-1.5 bg-white text-amber-900 border border-amber-300 font-bold text-xs rounded-xl shadow-2xs"
                                >
                                  Ver / Liberar
                                </button>
                              </div>
                            );
                          }

                          const isPaid = appt.paymentStatus === 'paid';
                          const isCompleted = appt.status === 'completed';

                          return (
                            <div
                              key={appt.id}
                              onClick={() => setSelectedAppointment(appt)}
                              className={`p-3.5 bg-white hover:bg-zinc-50 border rounded-2xl cursor-pointer transition-all shadow-2xs space-y-2.5 group ${
                                isPaid 
                                  ? 'border-emerald-200/90 hover:border-emerald-400' 
                                  : isCompleted 
                                    ? 'border-amber-300/90 bg-amber-50/20 hover:border-amber-400' 
                                    : 'border-zinc-200/90 hover:border-brand/60'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3">
                                  <div className={`w-12 h-12 rounded-2xl border flex flex-col items-center justify-center shrink-0 ${
                                    isPaid 
                                      ? 'bg-emerald-50 text-emerald-800 border-emerald-100' 
                                      : isCompleted 
                                        ? 'bg-amber-50 text-amber-900 border-amber-200' 
                                        : 'bg-zinc-50 text-zinc-800 border-zinc-200'
                                  }`}>
                                    <Clock className={`h-3.5 w-3.5 ${isPaid ? 'text-emerald-600' : isCompleted ? 'text-amber-600' : 'text-brand'}`} />
                                    <span className="text-[11px] font-black text-zinc-900">{appt.scheduledTime}</span>
                                    <span className={`text-[9px] font-bold ${isPaid ? 'text-emerald-700' : isCompleted ? 'text-amber-800' : 'text-zinc-500'}`}>{appt.durationMinutes}m</span>
                                  </div>

                                  <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-xs sm:text-sm font-black text-zinc-900 group-hover:text-brand transition-colors">
                                        {appt.clientName}
                                      </h4>
                                      {isPaid ? (
                                        <span className="text-[9px] font-black bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          <CheckCircle2 className="h-2.5 w-2.5" />
                                          Pago ({appt.paymentMethod?.toUpperCase() || 'CAIXA'})
                                        </span>
                                      ) : isCompleted ? (
                                        <span className="text-[9px] font-black bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          <Clock className="h-2.5 w-2.5" />
                                          Executado (Aguardando Caixa)
                                        </span>
                                      ) : appt.status === 'no_show' ? (
                                        <span className="text-[9px] font-black bg-rose-100 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-full">
                                          Não Compareceu
                                        </span>
                                      ) : (
                                        <span className="text-[9px] font-bold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-full">
                                          Agendado
                                        </span>
                                      )}

                                      {appt.isRecurring && (
                                        <span className="text-[9px] font-black bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          <Repeat className="h-2.5 w-2.5" />
                                          Horário Fixo ({appt.packageCurrentSession || 1}/{appt.packageTotalSessions || 4})
                                        </span>
                                      )}
                                      {primaryAppts.filter(a => a.id !== appt.id && (a.staffMemberId === appt.staffMemberId || (!a.staffMemberId && !appt.staffMemberId))).length > 0 && (
                                        <span className="text-[9px] font-black bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          <Zap className="h-2.5 w-2.5 text-amber-700" />
                                          Encaixe Simultâneo
                                        </span>

                                      )}
                                      <span className="text-[10px] font-bold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-md">
                                        👤 {appt.staffMemberName || staff.name}
                                      </span>
                                    </div>

                                    <p className="text-xs text-brand font-bold mt-0.5 flex items-center gap-1">
                                      <Scissors className="h-3.5 w-3.5" />
                                      {appt.serviceName}
                                    </p>

                                    {appt.clientPhone && (
                                      <span className="text-[11px] text-zinc-400 flex items-center gap-1 mt-0.5">
                                        <Phone className="h-3 w-3" />
                                        {formatPhone(appt.clientPhone)}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="text-right shrink-0">
                                  <span className="text-sm sm:text-base font-black text-zinc-900 block">
                                    {formatCurrencyBRL(appt.price)}
                                  </span>
                                  <span className={`text-[10px] font-bold block px-2 py-0.5 rounded-md border mt-1 ${
                                    isPaid 
                                      ? 'text-purple-700 bg-purple-50 border-purple-100' 
                                      : 'text-amber-800 bg-amber-50 border-amber-200'
                                  }`}>
                                    Repasse: {formatCurrencyBRL(staffComm)} {isPaid ? '✓' : '(Pendente)'}
                                  </span>
                                </div>
                              </div>

                              {/* Barra de Ações Rápidas no Card */}
                              <div className="pt-2 border-t border-zinc-100 flex items-center justify-between gap-2 text-xs">
                                <div className="flex items-center gap-2">
                                  {appt.clientPhone && (
                                    <a
                                      href={`https://api.whatsapp.com/send?phone=${appt.clientPhone.replace(/\D/g, '')}&text=Olá ${encodeURIComponent(appt.clientName)}! Confirmamos seu horário hoje às ${appt.scheduledTime} para ${encodeURIComponent(appt.serviceName)} no nosso espaço.`}
                                      target="_blank"
                                      rel="noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg flex items-center gap-1 font-bold text-[11px]"
                                      title="Lembrete no WhatsApp"
                                    >
                                      <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                                      <span className="hidden sm:inline">WhatsApp</span>
                                    </a>
                                  )}

                                  {!isPaid && (
                                    <button
                                      type="button"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setCheckoutAppointment(appt);
                                      }}
                                      className="p-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1 font-bold text-[11px] cursor-pointer shadow-xs active:scale-95 transition-all"
                                      title="Confirmar recebimento financeiro no caixa"
                                    >
                                      <DollarSign className="h-3.5 w-3.5" />
                                      <span>Baixar Caixa</span>
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setSelectedPosAppointment(appt);
                                      setIsQuickPosOpen(true);
                                    }}
                                    className="p-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-lg flex items-center gap-1 font-bold text-[11px] cursor-pointer"
                                  >
                                    <span>💈</span>
                                    <span>Comanda PDV</span>
                                  </button>
                                </div>

                                <span className="text-[11px] font-bold text-zinc-400 hover:text-zinc-700">
                                  Ver Detalhes →
                                </span>
                              </div>
                            </div>
                          );
                        })}

                        {/* Atendimentos e Bloqueios em andamento cobrindo este horário (Gestão à Vista) */}
                        {coveringAppts.map(cov => {
                          const isCovBlocked = cov.status === 'blocked';
                          return (
                            <div
                              key={`cov_${cov.id}`}
                              onClick={() => setSelectedAppointment(cov)}
                              className={`p-2.5 rounded-2xl border border-dashed flex items-center justify-between gap-3 text-xs cursor-pointer transition-colors ${
                                isCovBlocked 
                                  ? 'bg-amber-50/70 border-amber-300/80 hover:bg-amber-100/70 text-amber-950' 
                                  : 'bg-zinc-50 border-zinc-200/80 hover:bg-zinc-100/70 text-zinc-800'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className={`w-12 font-black text-xs ${isCovBlocked ? 'text-amber-800' : 'text-zinc-500'}`}>
                                  {slot}
                                </span>
                                <span className="text-[11px] font-medium flex items-center gap-1.5 flex-wrap">
                                  {isCovBlocked ? (
                                    <>
                                      <Lock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                                      <span className="text-[10px] font-black uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">
                                        {cov.allDay || cov.durationMinutes >= 420 ? 'Folga' : 'Bloqueado'}
                                      </span>
                                      <strong className="font-bold text-amber-950">
                                        {cov.staffMemberName || 'Atendente'}:
                                      </strong>
                                      <span className="text-amber-800">
                                        {cov.serviceName}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Clock className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
                                      <span>Em andamento: <strong className="font-bold text-zinc-900">{cov.clientName}</strong> ({cov.serviceName}) - {cov.staffMemberName}</span>
                                    </>
                                  )}
                                </span>
                              </div>

                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${
                                isCovBlocked ? 'bg-amber-100 text-amber-900' : 'bg-zinc-200/70 text-zinc-700'
                              }`}>
                                Até {calcEndTime(cov.scheduledTime, cov.durationMinutes)}
                              </span>
                            </div>
                          );
                        })}

                        {/* Se houver outros atendentes livres na visão geral, exibe linha de agendamento aberta */}
                        {isAllStaffFilter && canScheduleFreeStaff && (
                          <div
                            onClick={() => openNewForSlot(slot, freeStaffInSlot[0]?.id || selectedStaffFilter)}
                            className="group p-2.5 rounded-2xl border border-dashed border-emerald-200/80 bg-emerald-50/20 hover:bg-emerald-50/50 transition-all cursor-pointer flex items-center justify-between gap-3 text-xs"
                          >
                            <div className="flex items-center gap-3">
                              <span className="w-12 font-black text-xs text-zinc-400 group-hover:text-emerald-700 transition-colors">
                                {slot}
                              </span>
                              <span className="text-[11px] text-emerald-800 font-bold flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                                <span>Horário livre disponível</span>
                                {freeStaffInSlot.length > 0 && (
                                  <span className="text-[10px] font-medium text-emerald-700">
                                    ({freeStaffInSlot.map(s => s.name).join(', ')})
                                  </span>
                                )}
                              </span>
                            </div>

                            <span className="text-[11px] font-bold text-emerald-700 bg-white px-2.5 py-1 rounded-xl border border-emerald-300 shadow-2xs group-hover:bg-emerald-600 group-hover:text-white transition-all flex items-center gap-1">
                              <Plus className="h-3 w-3 stroke-3" />
                              <span>Agendar</span>
                            </span>
                          </div>
                        )}

                        {/* Opção de Encaixe Administrativo se todos os atendentes do slot estiverem ocupados */}
                        {!canScheduleFreeStaff && (
                          <div
                            onClick={() => openNewForSlot(slot, selectedStaffFilter !== 'all' ? selectedStaffFilter : undefined)}
                            className="group p-2 rounded-xl border border-dashed border-zinc-200/70 hover:border-amber-400/80 hover:bg-amber-50/20 transition-all cursor-pointer flex items-center justify-between gap-3 text-xs opacity-75 hover:opacity-100"
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="w-12 font-bold text-xs text-zinc-400 group-hover:text-amber-700 transition-colors">
                                {slot}
                              </span>
                              <span className="text-[11px] text-zinc-500 group-hover:text-amber-900 font-medium flex items-center gap-1.5 transition-colors">
                                <Zap className="w-3 h-3 text-amber-500" />
                                <span>Horário preenchido • Clique para registrar encaixe</span>
                              </span>
                            </div>

                            <span className="text-[10px] font-black text-amber-900 bg-white px-2 py-0.5 rounded-lg border border-amber-300 shadow-2xs group-hover:bg-amber-500 group-hover:text-white transition-all flex items-center gap-1">
                              <Plus className="h-2.5 w-2.5 stroke-3" />
                              <span>+ Encaixe</span>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ────────────────────────────────────────────────────────────────── */}
            {/* MODO 2: VISÃO SALÃO EM COLUNAS LADO A LADO POR PROFISSIONAL        */}
            {/* ────────────────────────────────────────────────────────────────── */}
            {calendarViewMode === 'columns' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {staffList.map(staff => {
                  const staffAppts = dayAppointments.filter(a => a.staffMemberId === staff.id || (!a.staffMemberId && staff.id === 'st_1'));
                  const totalDayValue = staffAppts.reduce((acc, curr) => acc + curr.price, 0);
                  const totalStaffCommission = (totalDayValue * (staff.commissionPercent ?? 50)) / 100;

                  return (
                    <div key={staff.id} className="bg-white rounded-3xl border border-zinc-200/90 overflow-hidden shadow-2xs flex flex-col">
                      {/* Topo do Card da Atendente */}
                      <div className="p-3.5 border-b border-zinc-100 bg-zinc-50/80 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black text-xs">
                            {staff.name.charAt(0)}
                          </div>
                          <div>
                            <h3 className="font-black text-xs text-zinc-900">{staff.name}</h3>
                            <p className="text-[10px] text-zinc-500 font-medium">{staff.role}</p>
                          </div>
                        </div>
                        <span className="text-[10px] font-black bg-purple-100 text-purple-900 px-2 py-0.5 rounded-md">
                          {staff.commissionPercent ?? 50}% repasse
                        </span>
                      </div>

                      {/* Lista de Horários da Atendente */}
                      <div className="p-3 space-y-2 flex-1 min-h-55">
                        {staffAppts.length === 0 ? (
                          <div className="h-full flex flex-col items-center justify-center text-center p-6 text-zinc-400 space-y-2">
                            <Clock className={`h-6 w-6 ${!currentDaySchedule.isOpen ? 'text-amber-400' : 'text-zinc-300'}`} />
                            <div>
                              <p className="text-xs font-bold text-zinc-700">
                                {!currentDaySchedule.isOpen ? 'Sem horários disponíveis' : 'Nenhum horário hoje'}
                              </p>
                              <span className={`text-[10px] font-bold block mt-0.5 ${!currentDaySchedule.isOpen ? 'text-zinc-500' : 'text-emerald-600'}`}>
                                {!currentDaySchedule.isOpen 
                                  ? `${DAY_LABELS[getDayKeyFromDate(selectedDate)].name} sem grade aberta` 
                                  : 'Grade livre para o Chatbot'}
                              </span>
                            </div>
                            {currentDaySchedule.isOpen && (
                              <button
                                type="button"
                                onClick={() => openNewForSlot(currentDaySlots[0] || '09:00', staff.id)}
                                className="px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-[11px] rounded-xl transition-all cursor-pointer flex items-center gap-1"
                              >
                                <Plus className="h-3 w-3" />
                                <span>Agendar Aqui</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          staffAppts.map(appt => {
                            const isBlocked = appt.status === 'blocked';
                            const isAllDay = appt.durationMinutes >= 420;

                            if (isBlocked) {
                              return (
                                <div
                                  key={appt.id}
                                  onClick={() => setSelectedAppointment(appt)}
                                  className={`p-2.5 rounded-xl cursor-pointer hover:scale-[1.01] transition-transform space-y-1 text-xs ${
                                    isAllDay
                                      ? 'bg-amber-100/90 border-2 border-amber-300 shadow-2xs'
                                      : 'bg-amber-50/80 border border-amber-200'
                                  }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                                      <Lock className="h-3 w-3" />
                                      {isAllDay ? '🌴 Folga / Dia Todo' : appt.scheduledTime}
                                    </span>
                                    <span className="text-[10px] text-amber-800 font-bold">
                                      {appt.scheduledTime} às {calcEndTime(appt.scheduledTime, appt.durationMinutes)}
                                    </span>
                                  </div>
                                  <p className="text-xs font-black text-amber-950 truncate">{appt.serviceName}</p>
                                </div>
                              );
                            }

                            const isPaid = appt.paymentStatus === 'paid';
                            const isCompleted = appt.status === 'completed';

                            return (
                              <div
                                key={appt.id}
                                onClick={() => setSelectedAppointment(appt)}
                                className={`p-3 bg-white border rounded-2xl shadow-2xs cursor-pointer hover:scale-[1.01] transition-all space-y-1.5 group text-xs ${
                                  isPaid 
                                    ? 'border-emerald-200 hover:border-emerald-400' 
                                    : isCompleted 
                                      ? 'border-amber-300 bg-amber-50/20 hover:border-amber-400' 
                                      : 'border-zinc-200 hover:border-brand/70'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-black text-emerald-900 bg-emerald-50 px-2 py-0.5 rounded-lg border border-emerald-100">
                                    {appt.scheduledTime} ({appt.durationMinutes}m)
                                  </span>
                                  <strong className="text-xs font-black text-zinc-900">{formatCurrencyBRL(appt.price)}</strong>
                                </div>

                                <div className="flex items-center justify-between gap-1">
                                  <h4 className="font-extrabold text-zinc-900 text-xs truncate group-hover:text-brand transition-colors">
                                    {appt.clientName}
                                  </h4>
                                  {isPaid ? (
                                    <span className="text-[9px] font-black text-emerald-800 bg-emerald-100 px-1.5 py-0.2 rounded shrink-0">
                                      ✓ Pago
                                    </span>
                                  ) : isCompleted ? (
                                    <span className="text-[9px] font-black text-amber-900 bg-amber-100 px-1.5 py-0.2 rounded shrink-0">
                                      ⏳ Caixa
                                    </span>
                                  ) : null}
                                </div>
                                <p className="text-[11px] text-zinc-500 truncate">{appt.serviceName}</p>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Rodapé da Coluna com Fechamento do Dia */}
                      <div className="p-3 bg-zinc-50/80 border-t border-zinc-100 flex items-center justify-between text-[11px]">
                        <span className="text-zinc-500 font-bold">Produzido no dia:</span>
                        <strong className="text-brand font-black">{formatCurrencyBRL(totalDayValue)}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 2: HORÁRIOS FIXOS / RECORRENTES                                      */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'recurring' && (
          <div className="space-y-3.5 text-xs">
            <div className="bg-white p-5 rounded-3xl border border-zinc-200/90 shadow-2xs space-y-1">
              <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider flex items-center gap-1.5">
                <Repeat className="h-4 w-4 text-purple-600" />
                Clientes com Horário Fixo / Recorrentes
              </h3>
              <p className="text-[11px] text-zinc-500">
                Horários cativos garantidos semanalmente. O Chatbot da Vitrine bloqueia esses horários automaticamente.
              </p>
            </div>

            <div className="grid gap-3">
              {Array.from(recurringClientsMap.entries()).map(([key, appts]) => {
                const firstAppt = appts[0];
                const totalSessions = firstAppt.packageTotalSessions || appts.length;
                const completedSessions = appts.filter(a => a.status === 'completed').length;
                const isPackageEnding = completedSessions >= totalSessions;

                return (
                  <div key={key} className="p-4 sm:p-5 bg-white border border-zinc-200/90 rounded-3xl space-y-3 shadow-2xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-black text-zinc-900">{firstAppt.clientName}</h4>
                          <span className="text-[10px] font-black bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md">
                            Toda Semana às {firstAppt.scheduledTime} ({firstAppt.staffMemberName || 'Salão'})
                          </span>
                        </div>
                        <p className="text-xs text-brand font-bold">{firstAppt.serviceName}</p>
                      </div>

                      <div className="text-left sm:text-right">
                        <span className="text-[10px] text-zinc-400 uppercase font-bold block">Progresso do Pacote</span>
                        <strong className="text-xs font-black text-purple-800">
                          {completedSessions} de {totalSessions} sessões concluídas
                        </strong>
                      </div>
                    </div>

                    {isPackageEnding && (
                      <div className="p-3 bg-amber-50 rounded-2xl border border-amber-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-amber-900">
                        <span className="font-bold text-[11px]">Pacote concluído! Deseja renovar o horário fixo no WhatsApp?</span>
                        <a
                          href={`https://api.whatsapp.com/send?phone=${firstAppt.clientPhone?.replace(/\D/g, '')}&text=Olá ${encodeURIComponent(firstAppt.clientName)}! Você concluiu seu pacote de ${totalSessions} sessões com ${firstAppt.staffMemberName || 'nosso salão'}. Gostaria de renovar seu horário fixo das ${firstAppt.scheduledTime} para o próximo mês?`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-[11px] flex items-center justify-center gap-1 shrink-0"
                        >
                          <MessageCircle className="h-3.5 w-3.5" />
                          <span>Renovar no Zap</span>
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 3: EQUIPE DO SALÃO, ACESSO & COMISSÕES                               */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'staff' && (
          <div className="space-y-3.5 text-xs">
            <div className="bg-white p-5 rounded-3xl border border-zinc-200/90 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                    Equipe, Portal Exclusivo & Split de Repasse
                  </h3>
                  <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full border border-emerald-200">
                    Acesso Grátis do Profissional
                  </span>
                </div>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Cada colaborador tem um link exclusivo e seguro para ver sua agenda, agendar clientes, bloquear folgas e acompanhar seus ganhos no mês sem pagar nada.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setNewStaffName('');
                  setNewStaffRole('');
                  setNewStaffPhone('');
                  setNewStaffCommission(50);
                  setNewStaffServiceIds([]);
                  setIsNewStaffModalOpen(true);
                }}
                className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 flex items-center justify-center gap-1.5 cursor-pointer active:scale-95 shrink-0"
              >
                <Plus className="h-4 w-4" />
                <span>Adicionar Profissional</span>
              </button>
            </div>

            {staffList.length === 0 ? (
              <div className="bg-white p-8 sm:p-12 rounded-3xl border border-dashed border-zinc-200 text-center space-y-4 shadow-2xs">
                <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-brand flex items-center justify-center mx-auto border border-emerald-100">
                  <Users className="w-7 h-7 text-brand" />
                </div>
                <div className="space-y-1.5 max-w-md mx-auto">
                  <h4 className="text-sm font-black text-zinc-900 uppercase tracking-wide">
                    Nenhum profissional cadastrado na equipe
                  </h4>
                  <p className="text-xs text-zinc-500 leading-relaxed">
                    Cadastre os membros da sua equipe ou o seu próprio perfil de atendimento. Cada colaborador ganha um link exclusivo e seguro para consultar seus horários e comissões.
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 pt-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => {
                      setNewStaffName('');
                      setNewStaffRole('Profissional / Atendente');
                      setNewStaffPhone('');
                      setNewStaffCommission(50);
                      setNewStaffServiceIds([]);
                      setIsNewStaffModalOpen(true);
                    }}
                    className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Adicionar Primeiro Profissional</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLoadTutorialScenario()}
                    className="px-3.5 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold text-xs rounded-xl transition-all cursor-pointer active:scale-95 flex items-center gap-1.5"
                    title="Carregar exemplo completo para gravação de tutoriais"
                  >
                    <Zap className="h-3.5 w-3.5 text-amber-600" />
                    <span>Carregar Exemplo para Vídeo ({vertical === 'pet_vet' ? 'Pet' : vertical === 'technical_service' ? 'Oficina' : 'Salão'})</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {staffList.map(staff => {
                  const token = staff.accessToken || staff.id;
                  const portalUrl = `${window.location.origin}/equipe/${token}`;
                  const staffPin = staff.pinCode || (staff.phone ? staff.phone.replace(/\D/g, '').slice(-4) : '1234');
                  const encodedZapText = encodeURIComponent(
                    `Olá ${staff.name}! ⚡ Aqui está o seu link de acesso exclusivo à sua agenda e controle de comissões no *${providerName}*:\n\n🔗 ${portalUrl}\n\n🔒 *Seu PIN de Segurança:* ${staffPin}\n\n💡 *Dica:* Abra o link no navegador do celular e toque em "Adicionar à Tela de Início" para usar como um aplicativo gratuito!`
                  );
                  const zapLink = staff.phone 
                    ? `https://wa.me/55${staff.phone.replace(/\D/g, '')}?text=${encodedZapText}`
                    : `https://api.whatsapp.com/send?text=${encodedZapText}`;

                  return (
                    <div key={staff.id} className="bg-white p-5 rounded-3xl border border-zinc-200/90 shadow-2xs space-y-3.5 flex flex-col justify-between">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black text-sm shadow-xs">
                              {staff.name.charAt(0)}
                            </div>
                            <div>
                              <h4 className="text-sm font-black text-zinc-900">{staff.name}</h4>
                              <p className="text-[11px] text-zinc-500 font-bold">{staff.role}</p>
                            </div>
                          </div>

                          {staff.compensationType === 'owner_revenue' || (staff.commissionPercent ?? 0) === 0 ? (
                            <span className="text-xs font-black bg-emerald-100 text-emerald-900 px-2.5 py-1 rounded-xl flex items-center gap-1">
                              <span>👑</span> Dono (100% Salão)
                            </span>
                          ) : staff.compensationType === 'fixed_salary' ? (
                            <span className="text-xs font-black bg-blue-100 text-blue-900 px-2.5 py-1 rounded-xl">
                              Fixo CLT (Sem Comissão)
                            </span>
                          ) : (
                            <span className="text-xs font-black bg-purple-100 text-purple-900 px-2.5 py-1 rounded-xl">
                              {staff.commissionPercent ?? 50}% Repasse
                            </span>
                          )}
                        </div>

                        <div className="flex items-center justify-between text-[11px] text-zinc-500 flex-wrap gap-2">
                          {staff.phone ? (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3 text-zinc-400" />
                              <span>WhatsApp: <strong>{staff.phone}</strong></span>
                            </span>
                          ) : <span />}

                          <button
                            type="button"
                            onClick={() => setRevealedPins(prev => ({ ...prev, [staff.id]: !prev[staff.id] }))}
                            className="flex items-center gap-1.5 font-mono font-bold bg-zinc-100 hover:bg-zinc-200/80 text-zinc-700 px-2 py-0.5 rounded-lg border border-zinc-200 text-[10px] transition-colors cursor-pointer"
                            title={revealedPins[staff.id] ? "Ocultar PIN" : "Visualizar PIN"}
                          >
                            <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0" />
                            <span>PIN:</span>
                            <span className={revealedPins[staff.id] ? "font-mono font-black text-zinc-900" : "blur-xs select-none tracking-widest text-zinc-500 font-sans"}>
                              {revealedPins[staff.id] ? staffPin : '••••'}
                            </span>
                            {revealedPins[staff.id] ? (
                              <EyeOff className="w-3 h-3 text-zinc-400 hover:text-zinc-700 ml-0.5" />
                            ) : (
                              <Eye className="w-3 h-3 text-zinc-400 hover:text-zinc-700 ml-0.5" />
                            )}
                          </button>
                        </div>

                        {/* Repasse vs Retenção */}
                        {staff.compensationType === 'owner_revenue' || (staff.commissionPercent ?? 0) === 0 ? (
                          <div className="p-2.5 bg-emerald-50/80 rounded-2xl border border-emerald-200/80 text-[11px] flex items-center justify-between">
                            <span className="text-emerald-950 font-bold flex items-center gap-1.5">
                              <span>🏛️</span>
                              <span>Faturamento Direto do Dono / Pró-labore</span>
                            </span>
                            <strong className="text-emerald-800 font-black">100% Salão</strong>
                          </div>
                        ) : staff.compensationType === 'fixed_salary' ? (
                          <div className="p-2.5 bg-blue-50/80 rounded-2xl border border-blue-200/80 text-[11px] flex items-center justify-between">
                            <span className="text-blue-950 font-bold flex items-center gap-1.5">
                              <span>💼</span>
                              <span>Salário Fixo em Folha (Custo Operacional)</span>
                            </span>
                            <strong className="text-blue-800 font-black">0% Comissão</strong>
                          </div>
                        ) : (
                          <div className="p-2.5 bg-zinc-50 rounded-2xl border border-zinc-100 grid grid-cols-2 gap-2 text-[11px]">
                            <div>
                              <span className="text-zinc-500 block">Comissão Colaborador:</span>
                              <strong className="text-purple-800 font-black">{staff.commissionPercent ?? 50}%</strong>
                            </div>
                            <div>
                              <span className="text-zinc-500 block">Retenção Salão:</span>
                              <strong className="text-brand font-black">{100 - (staff.commissionPercent ?? 50)}%</strong>
                            </div>
                          </div>
                        )}

                        {/* Serviços Habilitados / Especialidades (ACIMA) */}
                        <div className="space-y-1">
                          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block">
                            Serviços Habilitados:
                          </span>
                          {staff.serviceIds && staff.serviceIds.length > 0 ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              {getStaffServiceNames(staff, calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).map(sName => (
                                <span key={sName} className="bg-purple-50 text-purple-800 border border-purple-200/80 font-bold px-2 py-0.5 rounded-md text-[10px]">
                                  {sName}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-[10px] font-bold text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-md border border-zinc-200 inline-block">
                              ✓ Atende todos os serviços
                            </span>
                          )}
                        </div>

                        {/* Permissões Ativas / Autonomia no Portal (EMBAIXO) */}
                        <div className="space-y-1">
                          <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block">
                            Autonomia no Portal:
                          </span>
                          <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                            <span className="bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-md border border-emerald-100">
                              ✓ Agenda Própria
                            </span>
                            {staff.permissions?.canCreateAppointments !== false && (
                              <span className="bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-md border border-emerald-100">
                                ✓ Agendar Manual
                              </span>
                            )}
                            {staff.permissions?.canBlockSlots !== false && (
                              <span className="bg-amber-50 text-amber-700 font-bold px-2 py-0.5 rounded-md border border-amber-100">
                                ✓ Bloquear Folga
                              </span>
                            )}
                            {staff.permissions?.showCommission !== false && (
                              <span className="bg-purple-50 text-purple-700 font-bold px-2 py-0.5 rounded-md border border-purple-100">
                                ✓ Ver Faturamento
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* BOTÕES DE GESTÃO DO LINK DO PROFISSIONAL */}
                      <div className="pt-2 border-t border-zinc-100 space-y-2">
                        <div className="grid grid-cols-2 gap-1.5">
                          <a
                            href={zapLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-[11px] flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer active:scale-95"
                            title="Enviar link exclusivo para o WhatsApp do profissional"
                          >
                            <MessageCircle className="w-3.5 h-3.5" />
                            <span>Enviar WhatsApp</span>
                          </a>

                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(portalUrl);
                              setCopiedStaffId(staff.id);
                              setTimeout(() => setCopiedStaffId(null), 2000);
                            }}
                            className="px-2.5 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl font-bold text-[11px] flex items-center justify-center gap-1.5 transition-all cursor-pointer active:scale-95"
                          >
                            {copiedStaffId === staff.id ? (
                              <>
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-emerald-700 font-black">Copiado!</span>
                              </>
                            ) : (
                              <>
                                <Copy className="w-3.5 h-3.5 text-zinc-500" />
                                <span>Copiar Link</span>
                              </>
                            )}
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingStaff(staff)}
                            className="px-2.5 py-1.5 text-zinc-600 hover:text-zinc-900 font-bold text-[11px] flex items-center gap-1 hover:bg-zinc-50 rounded-lg cursor-pointer"
                          >
                            <Settings className="w-3.5 h-3.5 text-zinc-400" />
                            <span>Configurar Permissões</span>
                          </button>

                          <a
                            href={portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2.5 py-1.5 text-brand hover:text-brand-dark font-black text-[11px] flex items-center gap-1 hover:bg-brand/5 rounded-lg cursor-pointer"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>Abrir Tela</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 4: CATÁLOGO DE SERVIÇOS & PREÇOS                                     */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'services' && (
          <div className="space-y-3.5 text-xs">
            {/* Banner de Link Direto com Horário de Funcionamento */}
            <div className="bg-emerald-50/80 border border-emerald-200/90 rounded-3xl p-4 sm:p-5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">
                    Seu Link de Agendamento Online
                  </span>
                </div>
                <p className="text-xs font-black text-zinc-900 break-all">
                  {typeof window !== 'undefined' ? window.location.origin : 'https://precificacao.resolvmax.com.br'}/agendar/{slug}
                </p>
                <div className="flex items-center gap-1.5 text-[11px] text-zinc-600 font-medium pt-0.5">
                  <Clock className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                  <span>Horário de Atendimento: <strong>{currentBusinessHours}</strong></span>
                  <button
                    type="button"
                    onClick={openBusinessHoursModal}
                    className="ml-1.5 text-emerald-800 hover:text-emerald-950 font-black underline cursor-pointer"
                  >
                    Alterar
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    const link = `${window.location.origin}/agendar/${slug}`;
                    navigator.clipboard.writeText(link);
                    setCopiedDirectLink(true);
                    setTimeout(() => setCopiedDirectLink(false), 3000);
                  }}
                  className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer active:scale-95"
                >
                  {copiedDirectLink ? <Check className="h-4 w-4 stroke-3" /> : <Copy className="h-4 w-4" />}
                  <span>{copiedDirectLink ? 'Copiado!' : 'Copiar Link'}</span>
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Olá! Faça seu agendamento online com antecedência pelo nosso link: ${window.location.origin}/agendar/${slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 rounded-xl transition-colors flex items-center justify-center cursor-pointer"
                  title="Compartilhar no WhatsApp"
                >
                  <Share2 className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="bg-white p-5 rounded-3xl border border-zinc-200/90 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                  Catálogo de Serviços da Agenda
                </h3>
                <p className="text-[11px] text-zinc-500">Defina o tempo total do slot redondo e o preço cobrado.</p>
              </div>

              <button
                type="button"
                onClick={handleOpenNewService}
                className="px-3.5 py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
              >
                <Plus className="h-4 w-4" />
                <span>Novo Serviço</span>
              </button>
            </div>

            {calendarServices.length === 0 ? (
              <div className="bg-white rounded-3xl border border-zinc-200/90 p-8 text-center shadow-2xs space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-zinc-100 flex items-center justify-center mx-auto text-zinc-400">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-zinc-800">Nenhum serviço cadastrado na agenda</h4>
                  <p className="text-xs text-zinc-500 max-w-md mx-auto mt-1">
                    Cadastre os serviços que seus clientes podem agendar pela sua página online ou selecione um modelo de exemplo para demonstração.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleOpenNewService}
                    className="px-4 py-2 bg-brand text-white font-bold text-xs rounded-xl hover:bg-brand-dark transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar Primeiro Serviço
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLoadTutorialScenario('salao')}
                    className="px-3.5 py-2 bg-zinc-100 text-zinc-700 font-bold text-xs rounded-xl hover:bg-zinc-200 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                    title="Carregar serviços de exemplo para tutoriais e vídeos"
                  >
                    <BookOpen className="w-4 h-4 text-zinc-500" />
                    Carregar Exemplo (Tutorial)
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-zinc-200/90 overflow-hidden shadow-2xs divide-y divide-zinc-100">
                {calendarServices.map(srv => {
                  const hasPromo = isPromoActive(srv.promo, selectedDate);
                  const discountPercent = hasPromo ? calculateDiscountPercent(srv.price, srv.promo!.promoPrice) : 0;
                  const urgencyText = hasPromo ? getPromoUrgencyText(srv.promo) : null;

                  return (
                    <div key={srv.id} className={`p-4 flex items-center justify-between transition-colors ${hasPromo ? 'bg-amber-50/20 hover:bg-amber-50/40' : 'hover:bg-zinc-50/50'}`}>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-zinc-900 text-xs">{srv.name}</h4>
                          {srv.category && (
                            <span className="text-[10px] font-black bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-md">
                              {srv.category}
                            </span>
                          )}
                          {hasPromo && (
                            <span className="text-[9px] font-black bg-linear-to-r from-amber-500 to-orange-500 text-white px-2 py-0.5 rounded-full shadow-2xs uppercase tracking-wider flex items-center gap-1">
                              <Flame className="h-3 w-3" />
                              {srv.promo?.title ? `${srv.promo.title} • ` : ''}{discountPercent}% OFF
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-400 flex flex-wrap items-center gap-2 font-medium">
                          <span className="flex items-center gap-1 text-brand font-bold">
                            <Clock className="h-3 w-3" />
                            {srv.durationMinutes} min ({srv.durationMinutes >= 60 ? `${(srv.durationMinutes / 60).toFixed(1)}h` : `${srv.durationMinutes}m`})
                          </span>
                          <span>• Horário Redondo</span>
                          {urgencyText && (
                            <span className="text-amber-700 font-bold bg-amber-100/80 px-2 py-0.5 rounded-md text-[10px]">
                              {urgencyText}
                            </span>
                          )}
                        </p>

                        {/* Profissionais Habilitados */}
                        {staffList.filter(s => s.id !== 'st_any').length > 1 && (
                          <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                            {srv.allowedStaffIds && srv.allowedStaffIds.length > 0 ? (
                              <span className="text-[10px] font-bold text-purple-700 bg-purple-50 border border-purple-200/80 px-2 py-0.5 rounded-md flex items-center gap-1">
                                <span>👤</span>
                                {staffList
                                  .filter(st => srv.allowedStaffIds?.includes(st.id))
                                  .map(st => st.name)
                                  .join(', ') || 'Equipe restrita'}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold text-zinc-500 bg-zinc-50 border border-zinc-200/60 px-2 py-0.5 rounded-md flex items-center gap-1">
                                <span>👥</span>
                                Atendido por toda a equipe
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        {hasPromo ? (
                          <div className="text-right">
                            <span className="text-[10px] text-zinc-400 line-through font-bold block">
                              De {formatCurrencyBRL(srv.price)}
                            </span>
                            <strong className="text-sm font-black text-amber-600">
                              Por {formatCurrencyBRL(srv.promo!.promoPrice)}
                            </strong>
                          </div>
                        ) : (
                          <strong className="text-sm font-black text-brand">{formatCurrencyBRL(srv.price)}</strong>
                        )}
                        <button
                          type="button"
                          onClick={() => handleOpenEditService(srv)}
                          className="p-1.5 text-zinc-400 hover:text-brand rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors"
                          title="Editar Serviço"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteCalendarService(srv.id)}
                          className="p-1.5 text-zinc-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 cursor-pointer"
                          title="Excluir Serviço"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 5: CLUBES DE ASSINATURA VIP                                          */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'club' && (
          <div className="space-y-4 text-xs">
            {subscriptionPlans.length === 0 ? (
              <div className="bg-white rounded-3xl border border-zinc-200/90 p-8 text-center shadow-2xs space-y-3">
                <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto text-amber-600">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-zinc-800">Nenhum clube de assinatura criado</h4>
                  <p className="text-xs text-zinc-500 max-w-md mx-auto mt-1">
                    Clubes VIP permitem criar planos de fidelidade com receita mensal recorrente garantida no início de cada mês para o seu negócio.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => handleLoadTutorialScenario('salao')}
                    className="px-3.5 py-2 bg-amber-500 text-white font-bold text-xs rounded-xl hover:bg-amber-600 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                    title="Carregar modelos de exemplo para tutoriais e vídeos"
                  >
                    <BookOpen className="w-4 h-4" />
                    Carregar Exemplo de Clubes (Tutorial)
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="bg-linear-to-br from-amber-500 to-amber-600 text-white p-4 rounded-3xl shadow-sm">
                    <span className="text-[10px] uppercase font-black tracking-wider text-amber-100 block">
                      Assinantes VIP Ativos
                    </span>
                    <span className="text-2xl font-black block mt-0.5">
                      {subscriptionPlans.reduce((acc, p) => acc + (p.activeSubscribersCount || 0), 0)} clientes
                    </span>
                    <span className="text-[10px] text-amber-100 mt-1 block">
                      Em {subscriptionPlans.length} planos de recorrência
                    </span>
                  </div>

                  <div className="bg-white p-4 rounded-3xl border border-zinc-200/90 shadow-2xs">
                    <span className="text-[10px] uppercase font-black tracking-wider text-zinc-400 block">
                      Receita Recorrente Garantida
                    </span>
                    <span className="text-2xl font-black text-brand block mt-0.5">
                      {formatCurrencyBRL(
                        subscriptionPlans.reduce((acc, p) => acc + (p.priceMonthly * (p.activeSubscribersCount || 0)), 0)
                      )}
                    </span>
                    <span className="text-[10px] text-emerald-600 font-bold mt-1 block">
                      Previsibilidade no início de cada mês
                    </span>
                  </div>

                  <div className="bg-white p-4 rounded-3xl border border-zinc-200/90 shadow-2xs flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-black tracking-wider text-zinc-400 block">
                        Fidelidade dos Clientes
                      </span>
                      <span className="text-xl font-black text-zinc-900 block mt-0.5">
                        94.2% ao mês
                      </span>
                      <span className="text-[10px] text-zinc-500 mt-1 block">
                        Nenhum cancelamento recente
                      </span>
                    </div>
                    <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                      <Zap className="h-6 w-6" />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-3.5">
                  {subscriptionPlans.map(plan => (
                    <div
                      key={plan.id}
                      className="bg-white rounded-3xl p-5 border border-zinc-200/90 hover:border-amber-400/60 transition-all shadow-2xs flex flex-col justify-between space-y-4"
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full">
                            👑 {plan.activeSubscribersCount} Assinantes
                          </span>
                          <span className="text-xs font-black text-zinc-900">
                            {formatCurrencyBRL(plan.priceMonthly)}<span className="text-[10px] text-zinc-400">/mês</span>
                          </span>
                        </div>

                        <div>
                          <h4 className="font-bold text-sm text-zinc-900">{plan.name}</h4>
                          <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{plan.description}</p>
                        </div>

                        <div className="space-y-1.5 pt-2 border-t border-zinc-100">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 block">Benefícios Inclusos:</span>
                          {plan.benefits.map((b, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-700">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                              <span>{b}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const text = `Fala amigo! 💈 Conheça o nosso *${plan.name}* por apenas *${formatCurrencyBRL(plan.priceMonthly)}/mês* com atendimentos livres! Assine no link: ${window.location.origin}/agendar/${slug}`;
                          window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
                        }}
                        className="w-full py-2.5 bg-zinc-900 hover:bg-neutral-950 text-white font-bold text-xs rounded-xl transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-95"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                        <span>Divulgar no WhatsApp</span>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 6: FECHAMENTO DE COMISSÕES                                           */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'commissions' && (() => {
          const commissionedStaff = staffList.filter(s => (s.compensationType === 'commission' || !s.compensationType) && (s.commissionPercent ?? 50) > 0);
          const ownerStaff = staffList.filter(s => s.compensationType === 'owner_revenue' || (s.commissionPercent ?? 0) === 0);
          const cltStaff = staffList.filter(s => s.compensationType === 'fixed_salary');

          const getStaffMetrics = (st: StaffMember) => {
            const stAppointments = appointments.filter(a => a.staffMemberId === st.id && a.status !== 'canceled' && a.status !== 'blocked');
            const paidAppointments = stAppointments.filter(a => a.paymentStatus === 'paid');
            const pendingAppointments = stAppointments.filter(a => a.paymentStatus !== 'paid');

            const paidGross = paidAppointments.reduce((acc, a) => acc + (a.price || 0), 0);
            const pendingGross = pendingAppointments.reduce((acc, a) => acc + (a.price || 0), 0);
            const totalGross = paidGross + pendingGross;

            const isOwner = st.compensationType === 'owner_revenue' || (st.commissionPercent ?? 0) === 0;
            const isClt = st.compensationType === 'fixed_salary';
            const effCommission = (isOwner || isClt) ? 0 : (st.commissionPercent ?? 50);

            const paidCommAmount = (paidGross * effCommission) / 100;
            const pendingCommAmount = (pendingGross * effCommission) / 100;
            const retainedAmount = isOwner ? paidGross : (paidGross - paidCommAmount);

            return {
              stAppointments,
              paidAppointments,
              pendingAppointments,
              paidGross,
              pendingGross,
              totalGross,
              effCommission,
              paidCommAmount,
              pendingCommAmount,
              retainedAmount,
              isOwner,
              isClt
            };
          };

          const totalPaidGrossAll = staffList.reduce((acc, st) => acc + getStaffMetrics(st).paidGross, 0);
          const totalPaidCommToPartners = commissionedStaff.reduce((acc, st) => acc + getStaffMetrics(st).paidCommAmount, 0);
          const totalRetainedSaloon = staffList.reduce((acc, st) => acc + getStaffMetrics(st).retainedAmount, 0);

          return (
            <div className="space-y-4 text-xs">
              <div className="bg-white p-5 rounded-3xl border border-zinc-200/90 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                    Demonstrativo Semanal de Comissões e Repasses
                  </h3>
                  <p className="text-[11px] text-zinc-500">Rateio automático com separação entre repasses de parceiros e faturamento direto do salão.</p>
                </div>

                <button
                  type="button"
                  onClick={() => alert('Demonstrativo de comissões exportado com sucesso!')}
                  className="px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-xl border border-emerald-200 transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
                >
                  <FileText className="h-4 w-4" />
                  <span>Exportar Demonstrativo</span>
                </button>
              </div>

              {/* Cards de Resumo Financeiro */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white p-4 rounded-2xl border border-zinc-200 shadow-2xs">
                  <span className="text-[10px] font-black uppercase text-zinc-400 block tracking-wider">Faturamento Confirmado (Caixa)</span>
                  <strong className="text-base font-black text-zinc-900 block mt-1">{formatCurrencyBRL(totalPaidGrossAll)}</strong>
                  <span className="text-[10px] text-zinc-500 font-medium">Total recebido em atendimentos pagos</span>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-purple-200 shadow-2xs">
                  <span className="text-[10px] font-black uppercase text-purple-700 block tracking-wider">Repasses a Pagar (Saída de Caixa)</span>
                  <strong className="text-base font-black text-purple-900 block mt-1">{formatCurrencyBRL(totalPaidCommToPartners)}</strong>
                  <span className="text-[10px] text-purple-600 font-medium">Comissões liberadas aos parceiros</span>
                </div>
                <div className="bg-white p-4 rounded-2xl border border-emerald-200 shadow-2xs">
                  <span className="text-[10px] font-black uppercase text-emerald-700 block tracking-wider">Retido no Salão (Margem + Dono)</span>
                  <strong className="text-base font-black text-emerald-900 block mt-1">{formatCurrencyBRL(totalRetainedSaloon)}</strong>
                  <span className="text-[10px] text-emerald-600 font-medium">Receita líquida da empresa</span>
                </div>
              </div>

              {/* TABELA 1: Parceiros Comissionados (Saída de Caixa) */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm">✂️</span>
                  <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                    Repasses a Parceiros Comissionados (Saída de Caixa)
                  </h4>
                </div>

                <div className="bg-white rounded-3xl border border-zinc-200/90 overflow-hidden shadow-2xs">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-zinc-50 text-[10px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-200/80">
                          <th className="py-3 px-4">Profissional</th>
                          <th className="py-3 px-3">Confirmado Caixa</th>
                          <th className="py-3 px-3">% Comissão</th>
                          <th className="py-3 px-3">Comissão Liberada</th>
                          <th className="py-3 px-3">Aguardando Caixa</th>
                          <th className="py-3 px-4 text-right">WhatsApp</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {commissionedStaff.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-zinc-400 font-medium text-[11px]">
                              Nenhum parceiro comissionado cadastrado na equipe.
                            </td>
                          </tr>
                        ) : (
                          commissionedStaff.map((st) => {
                            const {
                              paidAppointments,
                              pendingAppointments,
                              paidGross,
                              pendingGross,
                              totalGross,
                              effCommission,
                              paidCommAmount,
                              pendingCommAmount
                            } = getStaffMetrics(st);

                            const textZap = encodeURIComponent(
                              `Olá ${st.name}! 📊 Segue o fechamento das suas comissões no *${providerName}*:\n\n💎 *Comissão Confirmada no Caixa:* ${formatCurrencyBRL(paidCommAmount)} (${paidAppointments.length} atendimentos pagos)\n⏳ *Aguardando Baixa no Caixa:* ${formatCurrencyBRL(pendingCommAmount)} (${pendingAppointments.length} a receber)\n💇 *Total Bruto Produzido:* ${formatCurrencyBRL(totalGross)}\n\nConferido e emitido com segurança via R-Max.`
                            );
                            const zapLink = st.phone 
                              ? `https://wa.me/55${st.phone.replace(/\D/g, '')}?text=${textZap}`
                              : `https://api.whatsapp.com/send?text=${textZap}`;

                            return (
                              <tr key={st.id} className="hover:bg-zinc-50/60 transition-colors">
                                <td className="py-3.5 px-4">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-800 font-black text-xs flex items-center justify-center">
                                      {st.name.slice(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                      <span className="font-bold text-zinc-900 block">{st.name}</span>
                                      <span className="text-[10px] text-zinc-400 block">{st.role}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-3 font-bold text-emerald-800">
                                  {formatCurrencyBRL(paidGross)}
                                  <span className="text-[10px] text-zinc-400 block font-normal">{paidAppointments.length} pagos</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black bg-purple-50 text-purple-800 px-2 py-0.5 rounded-md text-[10px]">
                                    {effCommission}%
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-sm text-emerald-700 block">
                                    {formatCurrencyBRL(paidCommAmount)}
                                  </span>
                                  <span className="text-[9px] text-emerald-600 font-bold block">✓ Liberado para repasse</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-xs text-amber-900 block">
                                    {formatCurrencyBRL(pendingCommAmount)}
                                  </span>
                                  <span className="text-[9px] text-amber-700 font-bold block">⏳ {pendingAppointments.length} a conferir</span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <a
                                    href={zapLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-[11px] font-bold inline-flex items-center gap-1.5 transition-all shadow-xs cursor-pointer active:scale-95"
                                    title="Enviar comprovante de fechamento para o WhatsApp do profissional"
                                  >
                                    <MessageCircle className="w-3.5 h-3.5" />
                                    <span>Enviar WhatsApp</span>
                                  </a>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* TABELA 2: Faturamento Direto do Dono / Salão (100% Retido no Caixa) */}
              {ownerStaff.length > 0 && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">👑</span>
                    <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                      Faturamento Direto do Dono / Proprietário (100% Retido no Salão / Pró-labore)
                    </h4>
                  </div>

                  <div className="bg-white rounded-3xl border border-emerald-200/90 overflow-hidden shadow-2xs">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-emerald-50/60 text-[10px] font-black uppercase tracking-wider text-emerald-900 border-b border-emerald-100">
                            <th className="py-3 px-4">Proprietário</th>
                            <th className="py-3 px-3">Confirmado Caixa</th>
                            <th className="py-3 px-3">Repasse Devido</th>
                            <th className="py-3 px-3">Retido no Caixa</th>
                            <th className="py-3 px-3">Aguardando Caixa</th>
                            <th className="py-3 px-4 text-right">Demonstrativo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {ownerStaff.map((st) => {
                            const {
                              paidAppointments,
                              pendingAppointments,
                              paidGross,
                              pendingGross,
                              totalGross
                            } = getStaffMetrics(st);

                            const textZap = encodeURIComponent(
                              `Olá ${st.name}! 👑 Resumo de produção do Proprietário no *${providerName}*:\n\n🏛️ *Faturamento Confirmado no Caixa:* ${formatCurrencyBRL(paidGross)} (${paidAppointments.length} atendimentos pagos)\n⏳ *Aguardando Recebimento:* ${formatCurrencyBRL(pendingGross)} (${pendingAppointments.length} a receber)\n💰 *Total Produzido:* ${formatCurrencyBRL(totalGross)}\n\n💡 100% retido no caixa para custeio operacional e pró-labore.`
                            );
                            const zapLink = st.phone 
                              ? `https://wa.me/55${st.phone.replace(/\D/g, '')}?text=${textZap}`
                              : `https://api.whatsapp.com/send?text=${textZap}`;

                            return (
                              <tr key={st.id} className="hover:bg-emerald-50/30 transition-colors">
                                <td className="py-3.5 px-4">
                                  <div className="flex items-center gap-2.5">
                                    <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 font-black text-xs flex items-center justify-center">
                                      👑
                                    </div>
                                    <div>
                                      <span className="font-bold text-zinc-900 block">{st.name}</span>
                                      <span className="text-[10px] text-emerald-700 font-bold block">{st.role || 'Proprietário'}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-3 font-bold text-emerald-800">
                                  {formatCurrencyBRL(paidGross)}
                                  <span className="text-[10px] text-zinc-400 block font-normal">{paidAppointments.length} pagos</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded-md text-[10px]">
                                    R$ 0,00 (0%)
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-sm text-emerald-800 block">
                                    {formatCurrencyBRL(paidGross)}
                                  </span>
                                  <span className="text-[9px] text-emerald-700 font-bold block">100% Caixa Salão / Pró-labore</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-xs text-amber-900 block">
                                    {formatCurrencyBRL(pendingGross)}
                                  </span>
                                  <span className="text-[9px] text-amber-700 font-bold block">⏳ {pendingAppointments.length} a receber</span>
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <a
                                    href={zapLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-xl text-[11px] font-bold inline-flex items-center gap-1.5 transition-all shadow-xs cursor-pointer active:scale-95"
                                  >
                                    <MessageCircle className="w-3.5 h-3.5" />
                                    <span>Resumo WhatsApp</span>
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* TABELA 3: Profissionais CLT (Salário Fixo) */}
              {cltStaff.length > 0 && (
                <div className="space-y-2 pt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">💼</span>
                    <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider">
                      Profissionais Salário Fixo (CLT - Custo Operacional)
                    </h4>
                  </div>

                  <div className="bg-white rounded-3xl border border-blue-200/90 overflow-hidden shadow-2xs">
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead>
                          <tr className="bg-blue-50/60 text-[10px] font-black uppercase tracking-wider text-blue-900 border-b border-blue-100">
                            <th className="py-3 px-4">Profissional</th>
                            <th className="py-3 px-3">Confirmado Caixa</th>
                            <th className="py-3 px-3">Regime</th>
                            <th className="py-3 px-3">Faturamento Salão</th>
                            <th className="py-3 px-3">Aguardando Caixa</th>
                            <th className="py-3 px-4 text-right">Demonstrativo</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {cltStaff.map((st) => {
                            const {
                              paidAppointments,
                              pendingAppointments,
                              paidGross,
                              pendingGross
                            } = getStaffMetrics(st);

                            return (
                              <tr key={st.id} className="hover:bg-blue-50/30 transition-colors">
                                <td className="py-3.5 px-4">
                                  <span className="font-bold text-zinc-900 block">{st.name}</span>
                                  <span className="text-[10px] text-zinc-400 block">{st.role}</span>
                                </td>
                                <td className="py-3 px-3 font-bold text-emerald-800">
                                  {formatCurrencyBRL(paidGross)}
                                  <span className="text-[10px] text-zinc-400 block font-normal">{paidAppointments.length} pagos</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-bold bg-blue-100 text-blue-900 px-2 py-0.5 rounded-md text-[10px]">
                                    Salário CLT
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-sm text-blue-900 block">
                                    {formatCurrencyBRL(paidGross)}
                                  </span>
                                  <span className="text-[9px] text-blue-700 font-bold block">100% Salão</span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className="font-black text-xs text-amber-900 block">
                                    {formatCurrencyBRL(pendingGross)}
                                  </span>
                                  <span className="text-[9px] text-amber-700 font-bold block">⏳ {pendingAppointments.length} pendentes</span>
                                </td>
                                <td className="py-3 px-4 text-right text-[11px] text-zinc-400">
                                  Sem comissão variável
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* ──────────────────────────────────────────────────────────────────────── */}
        {/* ABA 7: LINK ONLINE & COMPARTILHAMENTO                                    */}
        {/* ──────────────────────────────────────────────────────────────────────── */}
        {activeTab === 'link' && (
          <div className="space-y-4">
            {calendarServices.length === 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-900 shadow-2xs">
                <span className="text-base">⚠️</span>
                <div className="space-y-1">
                  <p className="font-black">Catálogo de serviços ainda não possui procedimentos</p>
                  <p className="text-amber-800">
                    Seus clientes verão a vitrine online sem itens para selecionar. Cadastre seus procedimentos antes de enviar o link.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab('services')}
                    className="mt-1.5 px-3 py-1 bg-amber-200 hover:bg-amber-300 text-amber-950 font-bold rounded-lg transition-colors cursor-pointer text-[11px]"
                  >
                    Cadastrar Procedimentos Agora
                  </button>
                </div>
              </div>
            )}

            <div className="bg-linear-to-r from-emerald-50 via-teal-50/50 to-white text-zinc-900 rounded-3xl p-5 shadow-xs border border-emerald-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-emerald-100 border border-emerald-200 flex items-center justify-center text-emerald-700 shrink-0">
                  <Zap className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-black text-zinc-900">Link Direto de Agendamento Online</h2>
                    <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full">
                      Fricção Zero
                    </span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-0.5 font-medium">
                    Envie para clientes no WhatsApp ou Bio do Instagram. Eles agendam em 3 cliques.
                  </p>
                  <code className="text-[11px] font-mono text-emerald-800 bg-white px-2.5 py-1 rounded-lg border border-emerald-200 inline-block mt-1.5 font-bold shadow-2xs">
                    {typeof window !== 'undefined' ? window.location.host : 'precificacao.resolvmax.com.br'}/agendar/{slug}
                  </code>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => {
                    const link = `${window.location.origin}/agendar/${slug}`;
                    navigator.clipboard.writeText(link);
                    setCopiedDirectLink(true);
                    setTimeout(() => setCopiedDirectLink(false), 2500);
                  }}
                  className="flex-1 sm:flex-initial px-4 py-2.5 bg-brand hover:bg-brand-dark text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-sm transition-all cursor-pointer flex items-center justify-center gap-1.5 active:scale-95"
                >
                  {copiedDirectLink ? (
                    <>
                      <Check className="h-4 w-4" />
                      <span>Link Copiado!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      <span>Copiar Link</span>
                    </>
                  )}
                </button>

                {onOpenDirectBooking && (
                  <button
                    type="button"
                    onClick={onOpenDirectBooking}
                    className="flex-1 sm:flex-initial px-3.5 py-2.5 bg-white hover:bg-zinc-50 text-zinc-700 text-xs font-bold rounded-xl border border-zinc-200 transition-all cursor-pointer flex items-center justify-center gap-1.5 active:scale-95 shadow-xs"
                  >
                    <ExternalLink className="h-4 w-4 text-zinc-500" />
                    <span>Ver como Cliente</span>
                  </button>
                )}
              </div>
            </div>

            {/* Bloco de Horário de Atendimento no Link */}
            <div className="bg-white rounded-3xl border border-zinc-200/90 p-4 sm:p-5 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-sky-50 border border-sky-200 flex items-center justify-center text-sky-700 shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="text-xs font-black text-zinc-900 uppercase tracking-wider">Horário de Funcionamento Exibido</h4>
                  <p className="text-xs text-zinc-600 font-bold mt-0.5">{currentBusinessHours}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={openBusinessHoursModal}
                className="px-3.5 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-xs font-bold rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1.5 active:scale-95 shrink-0"
              >
                <Edit3 className="h-3.5 w-3.5" />
                <span>Alterar Horário</span>
              </button>
            </div>

            <div className="bg-white rounded-3xl border border-zinc-200/90 p-5 space-y-4 shadow-2xs">
              <h3 className="text-sm font-black text-zinc-900">Dicas para Divulgação da sua Agenda</h3>
              <div className="space-y-3">
                {[
                  { icon: '📱', title: 'WhatsApp', desc: 'Cole o link na sua mensagem ou coloque na Bio do Instagram/TikTok.' },
                  { icon: '📸', title: 'Stories e Reels', desc: 'Adicione o link no sticker de link nos stories com um desconto especial.' },
                  { icon: '🔗', title: 'Google Meu Negócio', desc: 'Adicione o link como botão de agendamento no seu perfil do Google Maps.' },
                  { icon: '🎫', title: 'QR Code no Balcão', desc: 'Imprima o link como QR Code e coloque na recepção para clientes agendarem a próxima visita.' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-3 p-3 bg-zinc-50 rounded-2xl border border-zinc-100">
                    <span className="text-xl mt-0.5">{item.icon}</span>
                    <div>
                      <p className="text-xs font-black text-zinc-900">{item.title}</p>
                      <p className="text-[11px] text-zinc-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* GAVETA LATERAL: MENU DE GESTÃO DO SALÃO (DESCONGESTIONAMENTO TOTAL)      */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isManagementDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          {/* Backdrop escuro com blur suave */}
          <div 
            className="fixed inset-0 bg-neutral-950/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-200 cursor-pointer"
            onClick={() => setIsManagementDrawerOpen(false)}
          />

          {/* Painel lateral direito */}
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl z-10 flex flex-col border-l border-zinc-200 animate-in slide-in-from-right duration-200">
            {/* Header do Drawer */}
            <div className="p-4 sm:p-5 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/70 shrink-0">
              <div className="flex items-center gap-2.5">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-black text-xs shrink-0 shadow-2xs"
                  style={{ backgroundColor: accentColor || '#059669' }}
                >
                  <Settings className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900 tracking-tight">
                    Gestão do Salão
                  </h3>
                  <p className="text-[11px] text-zinc-500 font-medium">
                    Serviços, Equipe, Horários & Comissões
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsManagementDrawerOpen(false)}
                className="p-2 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 rounded-xl transition-colors cursor-pointer"
                title="Fechar menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Lista de Módulos com Tipografia Sóbria e Ícones Neutros */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              <span className="text-[10px] font-black uppercase text-zinc-400 tracking-widest block mb-1 px-1">
                Administração Operacional
              </span>

              {/* Serviços e Procedimentos */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('services');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'services'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <Scissors className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Serviços & Procedimentos
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      {calendarServices.length} procedimentos cadastrados
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Equipe & Profissionais */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('staff');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'staff'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <Users className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Equipe & Atendentes
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      {staffList.length} profissionais e permissões
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Horário de Atendimento do Salão */}
              <button
                type="button"
                onClick={() => {
                  openBusinessHoursModal();
                  setIsManagementDrawerOpen(false);
                }}
                className="w-full p-3.5 rounded-2xl border bg-white hover:bg-zinc-50 border-zinc-200/80 transition-all text-left flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <Clock className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Horário de Funcionamento
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium truncate max-w-55 block">
                      {currentBusinessHours}
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Bloquear Agenda / Folgas */}
              <button
                type="button"
                onClick={() => {
                  setBlockDate(selectedDate);
                  setIsBlockModalOpen(true);
                  setIsManagementDrawerOpen(false);
                }}
                className="w-full p-3.5 rounded-2xl border bg-white hover:bg-zinc-50 border-zinc-200/80 transition-all text-left flex items-center justify-between group cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-amber-100/70 text-amber-800 flex items-center justify-center shrink-0">
                    <Lock className="h-4 w-4 text-amber-800" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Bloquear Horário / Folga
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      Intervalo, almoço, médico ou recesso
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              <span className="text-[10px] font-black uppercase text-zinc-400 tracking-widest block pt-3 mb-1 px-1">
                Comunicação & Finanças
              </span>

              {/* Link de Agendamento do Cliente */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('link');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'link'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <ExternalLink className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Link da Agenda & QR Code
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      Compartilhe no WhatsApp ou Instagram
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Fechamento de Comissões */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('commissions');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'commissions'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <DollarSign className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Fechamento de Comissões
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      Relatório de comissões por profissional
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Horários Fixos */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('recurring');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'recurring'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <Repeat className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Horários Fixos da Semana
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      {recurringClientsMap.size} clientes fixos
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>

              {/* Clubes VIP */}
              <button
                type="button"
                onClick={() => {
                  setActiveTab('club');
                  setIsManagementDrawerOpen(false);
                }}
                className={`w-full p-3.5 rounded-2xl border transition-all text-left flex items-center justify-between group cursor-pointer ${
                  activeTab === 'club'
                    ? 'bg-zinc-100 border-zinc-300 font-black'
                    : 'bg-white hover:bg-zinc-50 border-zinc-200/80'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-100 text-zinc-700 flex items-center justify-center shrink-0 group-hover:bg-zinc-200 transition-colors">
                    <Zap className="h-4 w-4 text-zinc-700" />
                  </div>
                  <div>
                    <span className="text-xs font-black text-zinc-900 block leading-tight">
                      Clubes VIP & Assinaturas
                    </span>
                    <span className="text-[11px] text-zinc-500 font-medium">
                      Planos recorrentes de clientes
                    </span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-zinc-400 group-hover:text-zinc-700 transition-colors" />
              </button>
            </div>

            {/* Footer do Drawer com ação direta de voltar para agenda */}
            <div className="p-4 border-t border-zinc-100 bg-zinc-50 flex items-center justify-between shrink-0">
              <button
                type="button"
                onClick={() => {
                  setActiveTab('calendar');
                  setIsManagementDrawerOpen(false);
                }}
                className="w-full py-2.5 px-3 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-black rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-xs"
              >
                <CalendarIcon className="h-4 w-4" />
                <span>Visualizar Agenda Diária</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL 1: NOVO AGENDAMENTO                                                */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isNewModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <div className="w-full sm:max-w-lg bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200/90 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center">
                  <Plus className="h-4 w-4 stroke-3" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900">Novo Agendamento</h3>
                  <span className="text-[10px] text-zinc-400 font-bold">{selectedDate} às {scheduledTime}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsNewModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3.5 overflow-y-auto overscroll-contain py-2 pr-1 flex-1 min-h-0">
              {/* Seleção do Atendente Responsável */}
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                  Selecione o Profissional *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {staffList.map(st => (
                    <button
                      key={st.id}
                      type="button"
                      onClick={() => setSelectedStaffId(st.id)}
                      className={`p-2.5 rounded-2xl border text-left transition-all cursor-pointer ${
                        selectedStaffId === st.id
                          ? 'bg-emerald-50 border-brand ring-2 ring-brand/20 shadow-2xs'
                          : 'bg-zinc-50 border-zinc-200/80 hover:bg-zinc-100'
                      }`}
                    >
                      <span className="font-bold text-xs text-zinc-900 block truncate">{st.name}</span>
                      <span className="text-[10px] text-zinc-500 block truncate font-medium">{st.role}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Seleção do Serviço */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">
                    Selecione o Serviço *
                  </label>
                  <button
                    type="button"
                    onClick={handleOpenNewService}
                    className="text-[10px] font-black text-brand hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="h-3 w-3" /> Cadastrar Novo
                  </button>
                </div>
                <select
                  value={selectedServiceId}
                  onChange={e => handleSelectServiceFromCatalog(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  <option value="">Selecione um serviço...</option>
                  {calendarServices.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.durationMinutes}m) — {formatCurrencyBRL(s.price)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                    Nome do Cliente *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Mariana Ferreira"
                    value={clientName}
                    onChange={e => setClientName(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                    WhatsApp
                  </label>
                  <input
                    type="text"
                    placeholder="(11) 98765-4321"
                    value={clientPhone}
                    onChange={e => setClientPhone(maskPhone(e.target.value))}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2.5">
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Data</label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                    Horário ({currentDaySchedule.isOpen ? `${currentDaySchedule.start}-${currentDaySchedule.end}` : 'Sem horários'})
                  </label>
                  <select
                    value={scheduledTime}
                    onChange={e => setScheduledTime(e.target.value)}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  >
                    {Array.from(new Set([
                      ...(currentDaySlots.length > 0 ? currentDaySlots : TIME_SLOTS_30MIN),
                      scheduledTime
                    ])).filter(Boolean).sort().map(slot => (
                      <option key={slot} value={slot}>
                        {slot}
                        {currentDaySlots.length === 0 ? ' (Fora do expediente)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Duração (min)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="30"
                    value={durationMinutes || ''}
                    onFocus={e => e.currentTarget.select()}
                    onClick={e => e.currentTarget.select()}
                    onChange={e => {
                      const raw = e.target.value.replace(/\D/g, '');
                      const clean = raw.replace(/^0+/, '');
                      setDurationMinutes(clean === '' ? 0 : parseInt(clean, 10));
                    }}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                  <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                    {[15, 30, 45, 60].map(m => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setDurationMinutes(m)}
                        className={`text-[10px] px-2 py-0.5 rounded-lg border font-bold cursor-pointer transition-all active:scale-95 ${
                          durationMinutes === m 
                            ? 'bg-brand text-white border-brand font-black shadow-2xs' 
                            : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-zinc-200'
                        }`}
                      >
                        {m}m
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Bloco de Recorrência */}
              <div className="p-3.5 bg-purple-50/70 rounded-2xl border border-purple-200 space-y-2.5">
                <label className="flex items-center gap-2 text-xs text-purple-900 font-black cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={e => setIsRecurring(e.target.checked)}
                    className="rounded text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                  />
                  <span>🔁 Horário Fixo / Repetir Toda Semana</span>
                </label>

                {isRecurring && (
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div>
                      <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Frequência</label>
                      <select
                        value={recurrenceFrequency}
                        onChange={e => setRecurrenceFrequency(e.target.value as any)}
                        className="w-full bg-white border border-purple-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none"
                      >
                        <option value="weekly">Semanal (Toda semana)</option>
                        <option value="biweekly">Quinzenal (A cada 15 dias)</option>
                        <option value="monthly">Mensal (1 vez por mês)</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Duração do Pacote</label>
                      <select
                        value={recurrenceWeeksCount}
                        onChange={e => setRecurrenceWeeksCount(parseInt(e.target.value, 10) || 4)}
                        className="w-full bg-white border border-purple-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none"
                      >
                        <option value={4}>4 semanas (1 mês / 4 sessões)</option>
                        <option value={8}>8 semanas (2 meses / 8 sessões)</option>
                        <option value={12}>12 semanas (3 meses / 12 sessões)</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setIsNewModalOpen(false)}
                className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateAppointment}
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
              >
                Confirmar Agendamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL 2: ADICIONAR NOVO PROFISSIONAL NA EQUIPE                           */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isNewStaffModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <form onSubmit={handleAddStaffMember} className="w-full sm:max-w-md bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-zinc-900">Cadastrar Profissional</h3>
                <p className="text-[11px] text-zinc-500">Gera acesso grátis para agenda e faturamento.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsNewStaffModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto overscroll-contain py-2 pr-1 flex-1 min-h-0">
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Nome Completo *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Beatriz Lima"
                  value={newStaffName}
                  onChange={e => setNewStaffName(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Cargo / Especialidade</label>
                <input
                  type="text"
                  placeholder="Ex: Manicure, Barbeiro, Esteticista"
                  value={newStaffRole}
                  onChange={e => setNewStaffRole(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">WhatsApp (DDD + Número)</label>
                  <input
                    type="tel"
                    placeholder="Ex: 11988887777"
                    value={newStaffPhone}
                    onChange={e => {
                      const phone = e.target.value;
                      setNewStaffPhone(phone);
                      const digits = phone.replace(/\D/g, '');
                      if (digits.length >= 4) {
                        setNewStaffPin(digits.slice(-4));
                      }
                    }}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>PIN de Segurança</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="8754"
                    value={newStaffPin}
                    onFocus={e => e.currentTarget.select()}
                    onClick={e => e.currentTarget.select()}
                    onChange={e => setNewStaffPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-mono font-black text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>

              {/* Modelo de Remuneração */}
              <div className="space-y-1.5">
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                  Modelo de Remuneração
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setNewStaffCompensationType('commission');
                      if (!newStaffCommission || Number(newStaffCommission) === 0) {
                        setNewStaffCommission(50);
                      }
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      newStaffCompensationType === 'commission'
                        ? 'bg-purple-50 border-purple-300 text-purple-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">✂️ Comissionado</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Parceiro com % por atendimento</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setNewStaffCompensationType('owner_revenue');
                      setNewStaffCommission(0);
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      newStaffCompensationType === 'owner_revenue'
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">👑 Dono (100% Salão)</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Pró-labore / Sem comissão a repassar</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setNewStaffCompensationType('fixed_salary');
                      setNewStaffCommission(0);
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      newStaffCompensationType === 'fixed_salary'
                        ? 'bg-blue-50 border-blue-300 text-blue-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">💼 Fixo CLT</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Salário fixo em folha de pagamento</span>
                  </button>
                </div>
              </div>

              {newStaffCompensationType === 'commission' ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                      Comissão do Profissional (%)
                    </label>
                    <span className="text-xs font-bold text-zinc-500">
                      Salão retém: <strong className="text-emerald-700 font-black">{Math.max(0, 100 - (Number(newStaffCommission) || 0))}%</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="relative w-24 shrink-0">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="50"
                        value={newStaffCommission}
                        onFocus={e => e.currentTarget.select()}
                        onClick={e => e.currentTarget.select()}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          if (val === '') {
                            setNewStaffCommission('');
                          } else {
                            const num = Number(val);
                            setNewStaffCommission(Math.min(100, num));
                          }
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-black text-center text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 font-black text-xs pointer-events-none">%</span>
                    </div>

                    {/* Atalhos Rápidos de Comissão */}
                    <div className="flex items-center gap-1 flex-wrap flex-1">
                      {[30, 40, 50, 60, 70, 100].map(pct => (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setNewStaffCommission(pct)}
                          className={`px-2 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer border active:scale-95 ${
                            Number(newStaffCommission) === pct
                              ? 'bg-brand text-white border-brand shadow-xs'
                              : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-zinc-200'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : newStaffCompensationType === 'owner_revenue' ? (
                <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
                  <span className="text-base">🏛️</span>
                  <div>
                    <strong className="block font-black">Faturamento Direto do Salão (100%)</strong>
                    <span className="text-[11px] text-emerald-700">
                      Os atendimentos deste profissional entram 100% no caixa da empresa (sem gerar comissão de repasse). Destinado ao Dono/Proprietário do estabelecimento.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 rounded-2xl border border-blue-200 text-xs text-blue-900 flex items-center gap-2">
                  <span className="text-base">💼</span>
                  <div>
                    <strong className="block font-black">Profissional CLT / Salário Fixo Mensal</strong>
                    <span className="text-[11px] text-blue-700">
                      Este colaborador recebe remuneração fixa cadastrada nas despesas operacionais da empresa. Nenhum repasse variável é descontado por atendimento.
                    </span>
                  </div>
                </div>
              )}

              {/* Vínculo de Serviços Atendidos pelo Profissional */}
              <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-700 block">
                      Serviços que este profissional atende:
                    </span>
                    <p className="text-[10px] text-zinc-500 font-medium">
                      {newStaffServiceIds.length > 0 
                        ? `${newStaffServiceIds.length} de ${(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length} serviços selecionados`
                        : 'Nenhum serviço selecionado (atenderá todos por padrão)'}
                    </p>
                  </div>
                  {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNewStaffServiceIds((calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).map(s => s.id))}
                        className="text-[10px] font-bold text-brand hover:underline cursor-pointer"
                      >
                        Marcar Todos
                      </button>
                      <span className="text-zinc-300">•</span>
                      <button
                        type="button"
                        onClick={() => setNewStaffServiceIds([])}
                        className="text-[10px] font-bold text-zinc-500 hover:text-zinc-800 cursor-pointer"
                      >
                        Limpar
                      </button>
                    </div>
                  )}
                </div>

                {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length === 0 ? (
                  <p className="text-[11px] text-zinc-400 italic py-1">
                    Nenhum serviço cadastrado no catálogo ainda. O profissional poderá atender todos os procedimentos.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto py-1 pr-1">
                    {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).map(srv => {
                      const isChecked = newStaffServiceIds.includes(srv.id);
                      return (
                        <label
                          key={srv.id}
                          className={`p-2 rounded-xl border transition-all flex items-center gap-2 cursor-pointer ${
                            isChecked
                              ? 'bg-emerald-50/70 border-emerald-300 text-emerald-950 font-bold'
                              : 'bg-white border-zinc-200/80 hover:bg-zinc-100 text-zinc-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              setNewStaffServiceIds(prev =>
                                isChecked ? prev.filter(id => id !== srv.id) : [...prev, srv.id]
                              );
                            }}
                            className="rounded border-zinc-300 text-brand focus:ring-brand w-3.5 h-3.5 cursor-pointer"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs truncate font-bold">{srv.name}</p>
                            <span className="text-[10px] text-zinc-400 block font-normal">
                              {formatCurrencyBRL(srv.price)} • {srv.durationMinutes} min
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Permissões do Profissional */}
              <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block">
                  Permissões de Acesso do Profissional:
                </span>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newStaffPermissions.canCreateAppointments !== false}
                    onChange={e => setNewStaffPermissions(prev => ({ ...prev, canCreateAppointments: e.target.checked }))}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir que o profissional agende clientes manualmente</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newStaffPermissions.canBlockSlots !== false}
                    onChange={e => setNewStaffPermissions(prev => ({ ...prev, canBlockSlots: e.target.checked }))}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir que o profissional bloqueie horários e folgas</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newStaffPermissions.showCommission !== false}
                    onChange={e => setNewStaffPermissions(prev => ({ ...prev, showCommission: e.target.checked }))}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir ver painel de faturamento e comissões do mês</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newStaffPermissions.canComplete !== false}
                    onChange={e => setNewStaffPermissions(prev => ({ ...prev, canComplete: e.target.checked }))}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir marcar atendimento como finalizado</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setIsNewStaffModalOpen(false)}
                className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
              >
                Salvar e Gerar Acesso
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL 2.5: EDITAR PROFISSIONAL & CONFIGURAR PERMISSÕES                   */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {editingStaff && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-end sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
          <form onSubmit={handleSaveEditedStaff} className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-zinc-900">Editar {editingStaff.name}</h3>
                <p className="text-[11px] text-zinc-500">Configurações de acesso e permissões.</p>
              </div>
              <button
                type="button"
                onClick={() => setEditingStaff(null)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto py-2 pr-1 flex-1">
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Nome Completo *</label>
                <input
                  type="text"
                  required
                  value={editingStaff.name}
                  onChange={e => setEditingStaff({ ...editingStaff, name: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Cargo / Especialidade</label>
                <input
                  type="text"
                  value={editingStaff.role}
                  onChange={e => setEditingStaff({ ...editingStaff, role: e.target.value })}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">WhatsApp</label>
                  <input
                    type="tel"
                    placeholder="Ex: 11988887777"
                    value={editingStaff.phone || ''}
                    onChange={e => setEditingStaff({ ...editingStaff, phone: e.target.value })}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                <div>
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest mb-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>PIN de Acesso</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="8754"
                    value={editingStaff.pinCode || ''}
                    onFocus={e => e.currentTarget.select()}
                    onClick={e => e.currentTarget.select()}
                    onChange={e => setEditingStaff({ ...editingStaff, pinCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-mono font-black text-sm text-center focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>
              </div>

              {/* Modelo de Remuneração */}
              <div className="space-y-1.5">
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                  Modelo de Remuneração
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingStaff({
                        ...editingStaff,
                        compensationType: 'commission',
                        commissionPercent: (editingStaff.commissionPercent && editingStaff.commissionPercent > 0) ? editingStaff.commissionPercent : 50
                      });
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      (editingStaff.compensationType === 'commission' || (!editingStaff.compensationType && (editingStaff.commissionPercent ?? 0) > 0))
                        ? 'bg-purple-50 border-purple-300 text-purple-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">✂️ Comissionado</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Parceiro com % por atendimento</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setEditingStaff({
                        ...editingStaff,
                        compensationType: 'owner_revenue',
                        commissionPercent: 0
                      });
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      (editingStaff.compensationType === 'owner_revenue' || (editingStaff.commissionPercent ?? 0) === 0)
                        ? 'bg-emerald-50 border-emerald-300 text-emerald-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">👑 Dono (100% Salão)</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Pró-labore / Sem comissão a repassar</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setEditingStaff({
                        ...editingStaff,
                        compensationType: 'fixed_salary',
                        commissionPercent: 0
                      });
                    }}
                    className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                      editingStaff.compensationType === 'fixed_salary'
                        ? 'bg-blue-50 border-blue-300 text-blue-900 shadow-2xs'
                        : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="text-xs font-black block">💼 Fixo CLT</span>
                    <span className="text-[10px] text-zinc-500 mt-0.5">Salário fixo em folha de pagamento</span>
                  </button>
                </div>
              </div>

              {(editingStaff.compensationType === 'commission' || (!editingStaff.compensationType && (editingStaff.commissionPercent ?? 0) > 0)) ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                      Comissão do Profissional (%)
                    </label>
                    <span className="text-xs font-bold text-zinc-500">
                      Salão retém: <strong className="text-emerald-700 font-black">{Math.max(0, 100 - (editingStaff.commissionPercent || 0))}%</strong>
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="relative w-24 shrink-0">
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="50"
                        value={editingStaff.commissionPercent ?? 50}
                        onFocus={e => e.currentTarget.select()}
                        onClick={e => e.currentTarget.select()}
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '');
                          const num = val === '' ? 0 : Number(val);
                          setEditingStaff({
                            ...editingStaff,
                            commissionPercent: Math.min(100, num)
                          });
                        }}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-black text-center text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 font-black text-xs pointer-events-none">%</span>
                    </div>

                    {/* Atalhos Rápidos de Comissão */}
                    <div className="flex items-center gap-1 flex-wrap flex-1">
                      {[30, 40, 50, 60, 70, 100].map(pct => (
                        <button
                          key={pct}
                          type="button"
                          onClick={() => setEditingStaff({ ...editingStaff, commissionPercent: pct })}
                          className={`px-2 py-1.5 rounded-xl text-xs font-black transition-all cursor-pointer border active:scale-95 ${
                            editingStaff.commissionPercent === pct
                              ? 'bg-brand text-white border-brand shadow-xs'
                              : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-zinc-200'
                          }`}
                        >
                          {pct}%
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : editingStaff.compensationType === 'owner_revenue' || (editingStaff.commissionPercent ?? 0) === 0 ? (
                <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-200 text-xs text-emerald-900 flex items-center gap-2">
                  <span className="text-base">🏛️</span>
                  <div>
                    <strong className="block font-black">Faturamento Direto do Salão (100%)</strong>
                    <span className="text-[11px] text-emerald-700">
                      Os atendimentos deste profissional entram 100% no caixa da empresa (sem gerar comissão de repasse). Destinado ao Dono/Proprietário do estabelecimento.
                    </span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-blue-50 rounded-2xl border border-blue-200 text-xs text-blue-900 flex items-center gap-2">
                  <span className="text-base">💼</span>
                  <div>
                    <strong className="block font-black">Profissional CLT / Salário Fixo Mensal</strong>
                    <span className="text-[11px] text-blue-700">
                      Este colaborador recebe remuneração fixa cadastrada nas despesas operacionais da empresa. Nenhum repasse variável é descontado por atendimento.
                    </span>
                  </div>
                </div>
              )}

              {/* Vínculo de Serviços Atendidos pelo Profissional */}
              <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-zinc-700 block">
                      Serviços que este profissional atende:
                    </span>
                    <p className="text-[10px] text-zinc-500 font-medium">
                      {editingStaff.serviceIds && editingStaff.serviceIds.length > 0
                        ? `${editingStaff.serviceIds.length} de ${(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length} serviços selecionados`
                        : 'Atende todos os serviços (nenhuma restrição)'}
                    </p>
                  </div>
                  {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingStaff({
                          ...editingStaff,
                          serviceIds: (calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).map(s => s.id)
                        })}
                        className="text-[10px] font-bold text-brand hover:underline cursor-pointer"
                      >
                        Marcar Todos
                      </button>
                      <span className="text-zinc-300">•</span>
                      <button
                        type="button"
                        onClick={() => setEditingStaff({
                          ...editingStaff,
                          serviceIds: []
                        })}
                        className="text-[10px] font-bold text-zinc-500 hover:text-zinc-800 cursor-pointer"
                      >
                        Limpar
                      </button>
                    </div>
                  )}
                </div>

                {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).length === 0 ? (
                  <p className="text-[11px] text-zinc-400 italic py-1">
                    Nenhum serviço cadastrado no catálogo ainda.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-40 overflow-y-auto py-1 pr-1">
                    {(calendarServices.length > 0 ? calendarServices : userServicesAsCalendarItems).map(srv => {
                      const isChecked = editingStaff.serviceIds?.includes(srv.id) ?? false;
                      return (
                        <label
                          key={srv.id}
                          className={`p-2 rounded-xl border transition-all flex items-center gap-2 cursor-pointer ${
                            isChecked
                              ? 'bg-emerald-50/70 border-emerald-300 text-emerald-950 font-bold'
                              : 'bg-white border-zinc-200/80 hover:bg-zinc-100 text-zinc-700'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              const currentIds = editingStaff.serviceIds || [];
                              const nextIds = isChecked
                                ? currentIds.filter(id => id !== srv.id)
                                : [...currentIds, srv.id];
                              setEditingStaff({
                                ...editingStaff,
                                serviceIds: nextIds
                              });
                            }}
                            className="rounded border-zinc-300 text-brand focus:ring-brand w-3.5 h-3.5 cursor-pointer"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs truncate font-bold">{srv.name}</p>
                            <span className="text-[10px] text-zinc-400 block font-normal">
                              {formatCurrencyBRL(srv.price)} • {srv.durationMinutes} min
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Link de Acesso */}
              <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block">
                    Link de Acesso Exclusivo:
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newToken = typeof crypto !== 'undefined' && crypto.randomUUID 
                        ? crypto.randomUUID().replace(/-/g, '') 
                        : Math.random().toString(36).substring(2) + Date.now().toString(36);
                      setEditingStaff({ ...editingStaff, accessToken: newToken });
                      alert('Novo link gerado! Salve para invalidar o link antigo.');
                    }}
                    className="text-[10px] font-bold text-amber-700 hover:text-amber-800 flex items-center gap-1 cursor-pointer"
                    title="Gera um novo link e invalida o link anterior caso tenha sido compartilhado indevidamente"
                  >
                    🔄 Resetar Link
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/equipe/${editingStaff.accessToken || editingStaff.id}`}
                    className="w-full bg-white border border-zinc-200 rounded-xl px-2.5 py-2 text-[11px] font-mono text-zinc-600 focus:outline-none select-all"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/equipe/${editingStaff.accessToken || editingStaff.id}`);
                      alert('Link copiado!');
                    }}
                    className="px-3 py-2 bg-brand text-white rounded-xl font-bold text-xs shrink-0 cursor-pointer"
                  >
                    Copiar
                  </button>
                </div>
              </div>

              {/* Permissões do Profissional */}
              <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-600 block">
                  Permissões de Acesso:
                </span>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaff.permissions?.canCreateAppointments !== false}
                    onChange={e => setEditingStaff({
                      ...editingStaff,
                      permissions: { ...editingStaff.permissions, canCreateAppointments: e.target.checked }
                    })}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir agendar clientes manualmente</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaff.permissions?.canBlockSlots !== false}
                    onChange={e => setEditingStaff({
                      ...editingStaff,
                      permissions: { ...editingStaff.permissions, canBlockSlots: e.target.checked }
                    })}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir bloquear horários e folgas</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaff.permissions?.showCommission !== false}
                    onChange={e => setEditingStaff({
                      ...editingStaff,
                      permissions: { ...editingStaff.permissions, showCommission: e.target.checked }
                    })}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir ver painel de faturamento e comissões</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingStaff.permissions?.canComplete !== false}
                    onChange={e => setEditingStaff({
                      ...editingStaff,
                      permissions: { ...editingStaff.permissions, canComplete: e.target.checked }
                    })}
                    className="rounded text-brand focus:ring-0 cursor-pointer"
                  />
                  <span className="text-xs text-zinc-700">Permitir marcar atendimento como finalizado</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-zinc-100 shrink-0 bg-white">
              {onDeleteStaffMember && (
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`Tem certeza que deseja remover ${editingStaff.name} da equipe?`)) {
                      onDeleteStaffMember(editingStaff.id);
                      setEditingStaff(null);
                    }
                  }}
                  className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl font-bold text-xs cursor-pointer flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Excluir</span>
                </button>
              )}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={() => setEditingStaff(null)}
                  className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
                >
                  Salvar Alterações
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL 3: BLOQUEAR HORÁRIO PESSOAL / FOLGA                                */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isBlockModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <div className="w-full sm:max-w-md bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                  <Lock className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900">Bloquear Horário Pessoal</h3>
                  <span className="text-[10px] text-zinc-400 font-bold">Controle interno</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsBlockModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3.5 overflow-y-auto overscroll-contain py-3 pr-1 flex-1 min-h-0">
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Qual Profissional?</label>
                <select
                  value={blockStaffId}
                  onChange={e => setBlockStaffId(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20"
                >
                  {staffList.map(st => (
                    <option key={st.id} value={st.id}>{st.name} ({st.role})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Motivo do Bloqueio:</label>
                <div className="flex flex-wrap gap-1.5">
                  {PRESET_BLOCK_REASONS.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setBlockReason(preset.label);
                        if (preset.isAllDay) {
                          setBlockPeriodType('all_day');
                        } else {
                          setBlockPeriodType('specific_time');
                          setBlockDurationMinutes(preset.defaultDuration);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-colors cursor-pointer flex items-center gap-1 active:scale-95 ${
                        blockReason === preset.label
                          ? 'bg-amber-100 text-amber-950 border-amber-300 shadow-2xs'
                          : 'bg-zinc-50 text-zinc-700 border-zinc-200 hover:bg-amber-50'
                      }`}
                    >
                      <span>{preset.icon}</span>
                      <span>{preset.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Descrição / Anotação *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Folga semanal, médico, intervalo..."
                  value={blockReason}
                  onChange={e => setBlockReason(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-medium focus:outline-none focus:ring-2 focus:ring-brand/20 text-xs"
                />
              </div>

              {/* Seletor de Período: O Dia Todo vs Horário Específico */}
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">Período de Ausência</label>
                <div className="grid grid-cols-2 gap-1.5 bg-zinc-100 p-1 rounded-2xl text-xs font-bold">
                  <button
                    type="button"
                    onClick={() => setBlockPeriodType('all_day')}
                    className={`py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                      blockPeriodType === 'all_day'
                        ? 'bg-amber-500 text-white shadow-xs font-black'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <span>🌴</span>
                    <span>O Dia Todo (Folga)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBlockPeriodType('specific_time')}
                    className={`py-2 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                      blockPeriodType === 'specific_time'
                        ? 'bg-white text-zinc-900 shadow-xs font-black'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    <span>⏱️</span>
                    <span>Horário Específico</span>
                  </button>
                </div>
              </div>

              {/* Data */}
              <div>
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">Data</label>
                <input
                  type="date"
                  value={blockDate}
                  onChange={e => setBlockDate(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2 text-zinc-900 font-bold focus:outline-none text-xs"
                />
              </div>

              {/* Condicional: Se for Dia Todo, exibe aviso claro sem exigir horas */}
              {blockPeriodType === 'all_day' ? (
                <div className="p-3 bg-amber-50 rounded-2xl border border-amber-200/80 flex items-center gap-2.5">
                  <Palmtree className="h-5 w-5 text-amber-700 shrink-0" />
                  <div>
                    <strong className="text-xs font-black text-amber-950 block">Bloqueio Completo do Dia</strong>
                    <span className="text-[11px] text-amber-800">
                      Nenhum horário estará disponível para agendamento nesta data (indisponível para clientes e chatbot).
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">Horário de Início</label>
                      <select
                        value={blockTime}
                        onChange={e => setBlockTime(e.target.value)}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none text-xs"
                      >
                        {generateDaySlots(weeklyHours, blockDate, 30).length > 0 ? (
                          generateDaySlots(weeklyHours, blockDate, 30).map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ))
                        ) : (
                          TIME_SLOTS_30MIN.map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ))
                        )}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block mb-1">Duração</label>
                      <select
                        value={blockDurationMinutes}
                        onChange={e => setBlockDurationMinutes(parseInt(e.target.value, 10) || 60)}
                        className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-2 text-zinc-900 font-bold focus:outline-none text-xs"
                      >
                        <option value={30}>30 minutos</option>
                        <option value={60}>1 hora (60 min)</option>
                        <option value={90}>1h30 (90 min)</option>
                        <option value={120}>2 horas (120 min)</option>
                        <option value={180}>3 horas (180 min)</option>
                        <option value={240}>4 horas (Meio Período)</option>
                      </select>
                    </div>
                  </div>

                  {/* Resumo visual do horário final / término */}
                  <div className="flex items-center justify-between px-3 py-2 bg-amber-50/80 border border-amber-200/80 rounded-xl text-amber-950 text-[11px]">
                    <span className="text-zinc-600 font-medium">Horário Final (Término):</span>
                    <span className="font-bold">
                      ⏱️ {blockTime} às {minutesToTimeString(timeStringToMinutes(blockTime) + blockDurationMinutes)} ({blockDurationMinutes >= 60 ? `${Math.floor(blockDurationMinutes / 60)}h${blockDurationMinutes % 60 ? ` ${blockDurationMinutes % 60}min` : ''}` : `${blockDurationMinutes} min`})
                    </span>
                  </div>
                </div>
              )}

              {/* Repetição Semanal de Folga Fixa */}
              <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-2xl space-y-2">
                <label className="flex items-center justify-between cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Repeat className="h-4 w-4 text-amber-700 shrink-0" />
                    <div>
                      <span className="text-xs font-black text-amber-950 block">Repetir Toda Semana (Folga Fixa)</span>
                      <span className="text-[10px] text-amber-800">Bloquear toda semana neste dia (ex: toda segunda-feira)</span>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={isBlockRecurring}
                    onChange={e => setIsBlockRecurring(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500 cursor-pointer"
                  />
                </label>

                {isBlockRecurring && (
                  <div className="pt-2 border-t border-amber-200/60 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-bold text-amber-900">Por quantas semanas?</span>
                    <select
                      value={blockRecurrenceWeeks}
                      onChange={e => setBlockRecurrenceWeeks(parseInt(e.target.value, 10) || 8)}
                      className="bg-white border border-amber-300 rounded-xl px-2.5 py-1 text-xs font-bold text-amber-950 focus:outline-none"
                    >
                      <option value={4}>4 semanas (1 mês)</option>
                      <option value={8}>8 semanas (2 meses)</option>
                      <option value={12}>12 semanas (3 meses)</option>
                      <option value={24}>24 semanas (6 meses)</option>
                      <option value={52}>52 semanas (1 ano)</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setIsBlockModalOpen(false)}
                className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleCreateBlock}
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-2xs cursor-pointer active:scale-95 transition-all"
              >
                Bloquear Horário
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL 4: NOVO SERVIÇO NO CATÁLOGO                                        */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isNewServiceModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <form onSubmit={handleAddServiceToCatalog} className="w-full sm:max-w-md bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-zinc-900">
                  {editingServiceId ? 'Editar Serviço na Agenda' : 'Novo Serviço na Agenda'}
                </h3>
                <p className="text-[11px] text-zinc-500">Defina o nome, tempo do atendimento e preço cobrado.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsNewServiceModalOpen(false);
                  setEditingServiceId(null);
                }}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto overscroll-contain py-3 pr-1 flex-1 min-h-0">
              {/* Nome do Serviço */}
              <div>
                <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block mb-1">
                  Nome do Serviço *
                </label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Corte de Cabelo, Barba, Manicure..."
                  value={newServiceName}
                  onChange={e => setNewServiceName(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3.5 py-2.5 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20 text-xs"
                />

                {/* Sugestões rápidas de serviço */}
                {!editingServiceId && !newServiceName && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    <span className="text-[10px] text-zinc-400 font-bold">Exemplos:</span>
                    {SERVICE_NAME_SUGGESTIONS.slice(0, 4).map(sug => (
                      <button
                        key={sug}
                        type="button"
                        onClick={() => {
                          setNewServiceName(sug);
                          if (!newServiceDuration || newServiceDuration === '60') {
                            setNewServiceDuration('30');
                          }
                          if (!newServicePrice) {
                            setNewServicePrice('45');
                          }
                        }}
                        className="text-[10px] px-2 py-0.5 rounded-lg border border-zinc-200 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-medium cursor-pointer transition-all active:scale-95"
                      >
                        {sug}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Caixinhas de Duração: 15, 30, 45 e 60 minutos */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">
                    Duração do Atendimento *
                  </label>
                  <span className="text-xs font-black text-brand bg-emerald-50 px-2 py-0.5 rounded-md">
                    {newServiceDuration ? `${newServiceDuration} minutos` : 'Escolha a duração'}
                  </span>
                </div>

                {/* 4 Caixinhas Principais: 15, 30, 45, 60 min */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: '15 min', val: '15' },
                    { label: '30 min', val: '30' },
                    { label: '45 min', val: '45' },
                    { label: '60 min', val: '60' },
                  ].map(box => {
                    const isSelected = newServiceDuration === box.val;
                    return (
                      <button
                        key={box.val}
                        type="button"
                        onClick={() => setNewServiceDuration(box.val)}
                        className={`py-2.5 px-1 rounded-2xl text-center font-black transition-all border cursor-pointer flex flex-col items-center justify-center gap-0.5 active:scale-95 ${
                          isSelected
                            ? 'bg-brand text-white border-brand shadow-sm ring-2 ring-brand/20'
                            : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700 border-zinc-200'
                        }`}
                      >
                        <Clock className={`h-3.5 w-3.5 ${isSelected ? 'text-white' : 'text-zinc-400'}`} />
                        <span className="text-xs">{box.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Opções estendidas ou digitação livre de minutos */}
                <div className="flex items-center gap-2 pt-1">
                  <div className="flex items-center gap-1">
                    {[
                      { label: '1h30 (90m)', val: '90' },
                      { label: '2h (120m)', val: '120' },
                    ].map(ext => (
                      <button
                        key={ext.val}
                        type="button"
                        onClick={() => setNewServiceDuration(ext.val)}
                        className={`text-[10px] px-2 py-1 rounded-xl border font-bold transition-all cursor-pointer ${
                          newServiceDuration === ext.val
                            ? 'bg-brand text-white border-brand font-black'
                            : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-600 border-zinc-200'
                        }`}
                      >
                        {ext.label}
                      </button>
                    ))}
                  </div>

                  <div className="relative flex-1">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="Outro tempo"
                      value={newServiceDuration}
                      onFocus={e => e.currentTarget.select()}
                      onClick={e => e.currentTarget.select()}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        setNewServiceDuration(val);
                      }}
                      className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-3 py-1.5 text-zinc-900 font-bold focus:outline-none focus:ring-2 focus:ring-brand/20 pr-14 text-xs text-center"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 font-medium text-[10px] pointer-events-none">
                      min
                    </span>
                  </div>
                </div>
              </div>

              {/* Preço (R$) sem zero na frente */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">
                    Preço (R$) *
                  </label>
                  {newServicePrice && (
                    <span className="text-[11px] text-zinc-500 font-medium">
                      Cobrado: <strong className="text-zinc-900 font-black">{formatCurrencyBRL(parseFloat(newServicePrice.replace(',', '.')) || 0)}</strong>
                    </span>
                  )}
                </div>

                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 font-black text-xs pointer-events-none">
                    R$
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="45,00"
                    value={newServicePrice}
                    onFocus={e => e.currentTarget.select()}
                    onClick={e => e.currentTarget.select()}
                    onChange={e => {
                      let val = e.target.value.replace(/[^0-9.,]/g, '');
                      // Remove zero à esquerda imediatamente se o usuário digitar outro número (ex: 045 -> 45)
                      if (/^0+[0-9]/.test(val)) {
                        val = val.replace(/^0+/, '');
                      }
                      setNewServicePrice(val);
                    }}
                    className="w-full bg-zinc-50 border border-zinc-200 rounded-xl pl-9 pr-3.5 py-2.5 text-zinc-900 font-black text-sm focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                </div>

                {/* Caixinhas Rápidas de Preço */}
                <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                  <span className="text-[10px] text-zinc-400 font-bold">Valores rápidos:</span>
                  {[30, 35, 40, 45, 50, 60, 80].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNewServicePrice(String(p))}
                      className={`text-[10px] px-2 py-0.5 rounded-lg border font-bold transition-all cursor-pointer active:scale-95 ${
                        newServicePrice === String(p)
                          ? 'bg-emerald-600 text-white border-emerald-600 font-black shadow-2xs'
                          : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border-zinc-200'
                      }`}
                    >
                      R$ {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seleção de Profissionais Habilitados para o Serviço */}
              {staffList.filter(s => s.id !== 'st_any').length > 0 && (
                <div className="space-y-2 pt-2 border-t border-zinc-100">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">
                      Profissionais que Realizam este Serviço:
                    </label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setNewServiceAllowedStaffIds(staffList.filter(s => s.id !== 'st_any').map(s => s.id))}
                        className="text-[10px] text-brand hover:underline font-bold cursor-pointer"
                      >
                        Marcar Todos
                      </button>
                      <span className="text-zinc-300 text-xs">•</span>
                      <button
                        type="button"
                        onClick={() => setNewServiceAllowedStaffIds([])}
                        className="text-[10px] text-zinc-400 hover:text-zinc-600 font-bold cursor-pointer"
                      >
                        Limpar
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-36 overflow-y-auto pr-1">
                    {staffList.filter(s => s.id !== 'st_any').map(staff => {
                      const isChecked = newServiceAllowedStaffIds.includes(staff.id);
                      return (
                        <label
                          key={staff.id}
                          className={`p-2 rounded-xl border flex items-center justify-between gap-2 cursor-pointer transition-all ${
                            isChecked
                              ? 'bg-purple-50/70 border-purple-300 text-purple-950 font-bold shadow-2xs'
                              : 'bg-zinc-50 border-zinc-200 text-zinc-700 hover:bg-zinc-100/60'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={e => {
                                if (e.target.checked) {
                                  setNewServiceAllowedStaffIds(prev => [...prev, staff.id]);
                                } else {
                                  setNewServiceAllowedStaffIds(prev => prev.filter(id => id !== staff.id));
                                }
                              }}
                              className="rounded border-zinc-300 text-purple-600 focus:ring-purple-500 h-3.5 w-3.5 shrink-0"
                            />
                            <span className="text-xs truncate">{staff.name}</span>
                          </div>
                          <span className="text-[9px] text-zinc-400 font-normal shrink-0">{staff.role}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-zinc-400">
                    {newServiceAllowedStaffIds.length === 0
                      ? '✓ Quando nenhum estiver marcado, todos os profissionais atendem este serviço.'
                      : `${newServiceAllowedStaffIds.length} profissional(is) selecionado(s) para este procedimento.`}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => {
                  setIsNewServiceModalOpen(false);
                  setEditingServiceId(null);
                }}
                className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmittingService || isSavingService}
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer"
              >
                {isSubmittingService || isSavingService
                  ? 'Salvando no banco...'
                  : editingServiceId
                    ? 'Salvar Alterações'
                    : 'Salvar Serviço'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* DRAWER / DETALHES DO ATENDIMENTO SELECIONADO                             */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {selectedAppointment && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <div className="w-full sm:max-w-lg bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div>
                <h3 className="text-base font-black text-zinc-900">{selectedAppointment.clientName}</h3>
                <p className="text-xs text-brand font-bold">{selectedAppointment.serviceName}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedAppointment(null)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto overscroll-contain py-2 pr-1 flex-1 min-h-0">
              <div className="p-3.5 bg-zinc-50 rounded-2xl border border-zinc-200/80 flex items-center justify-between shadow-2xs">
                <div>
                  <span className="text-[10px] text-zinc-400 font-bold uppercase block">Data & Horário ({selectedAppointment.durationMinutes} min)</span>
                  <strong className="text-zinc-800">{selectedAppointment.scheduledDate} às {selectedAppointment.scheduledTime}</strong>
                  {selectedAppointment.staffMemberName && (
                    <span className="text-[10px] text-brand font-bold block mt-0.5">
                      Profissional: {selectedAppointment.staffMemberName}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-zinc-400 font-bold uppercase block">Valor Total</span>
                  <strong className="text-brand text-sm font-black">{formatCurrencyBRL(selectedAppointment.price)}</strong>
                </div>
              </div>

              {/* Split Financeiro Salão Parceiro */}
              {selectedAppointment.status !== 'blocked' && (() => {
                const assignedStaff = staffList.find(s => s.id === selectedAppointment.staffMemberId);
                const isOwner = assignedStaff?.compensationType === 'owner_revenue' || (selectedAppointment.commissionPercent ?? 50) === 0;
                const isClt = assignedStaff?.compensationType === 'fixed_salary';
                const commPct = (isOwner || isClt) ? 0 : (selectedAppointment.commissionPercent ?? 50);
                const repasseVal = (selectedAppointment.price * commPct) / 100;
                const salaoVal = selectedAppointment.price - repasseVal;

                if (isOwner) {
                  return (
                    <div className="p-3.5 bg-emerald-50/80 rounded-2xl border border-emerald-200 space-y-1.5 shadow-2xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-emerald-900 uppercase tracking-wider flex items-center gap-1">
                          <span>👑</span> Faturamento Direto do Dono / Salão:
                        </span>
                        <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md">
                          100% Retido no Caixa
                        </span>
                      </div>
                      <p className="text-[11px] text-emerald-800">
                        Atendimento realizado pelo Proprietário. Não há comissão a repassar — o valor integral de <strong>{formatCurrencyBRL(selectedAppointment.price)}</strong> é receita direta da empresa / pró-labore.
                      </p>
                    </div>
                  );
                }

                if (isClt) {
                  return (
                    <div className="p-3.5 bg-blue-50/80 rounded-2xl border border-blue-200 space-y-1.5 shadow-2xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-blue-900 uppercase tracking-wider flex items-center gap-1">
                          <span>💼</span> Profissional Fixo CLT:
                        </span>
                        <span className="text-[10px] font-black bg-blue-100 text-blue-800 px-2 py-0.5 rounded-md">
                          Salário em Folha
                        </span>
                      </div>
                      <p className="text-[11px] text-blue-800">
                        Colaborador com salário fixo registrado nas despesas da empresa. Sem comissão variável por atendimento.
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="p-3.5 bg-purple-50/60 rounded-2xl border border-purple-100 space-y-2 shadow-2xs">
                    <span className="text-[10px] font-black text-purple-900 uppercase tracking-wider block">
                      Split Financeiro (Salão Parceiro):
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white p-2.5 rounded-xl border border-purple-100">
                        <span className="text-[10px] text-zinc-500 font-bold block">Repasse ({commPct}%):</span>
                        <strong className="text-purple-900 font-black">
                          {formatCurrencyBRL(repasseVal)}
                        </strong>
                      </div>
                      <div className="bg-white p-2.5 rounded-xl border border-purple-100">
                        <span className="text-[10px] text-zinc-500 font-bold block">Salão ({100 - commPct}%):</span>
                        <strong className="text-brand font-black">
                          {formatCurrencyBRL(salaoVal)}
                        </strong>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Status do Atendimento & Caixa */}
              {selectedAppointment.status !== 'blocked' && (
                <div className="space-y-2">
                  {selectedAppointment.paymentStatus === 'paid' ? (() => {
                    const assignedStaff = staffList.find(s => s.id === selectedAppointment.staffMemberId);
                    const isOwner = assignedStaff?.compensationType === 'owner_revenue' || (selectedAppointment.commissionPercent ?? 50) === 0;
                    const isClt = assignedStaff?.compensationType === 'fixed_salary';
                    const commPct = (isOwner || isClt) ? 0 : (selectedAppointment.commissionPercent ?? 50);

                    return (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between text-xs">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <div>
                            <strong className="text-emerald-950 font-black block">
                              Recebimento Confirmado no Caixa ({selectedAppointment.paymentMethod?.toUpperCase() || 'PIX'})
                            </strong>
                            <span className="text-[10px] text-emerald-700 font-medium">
                              {isOwner
                                ? 'Faturamento direto do salão / Pró-labore. 100% retido no caixa da empresa.'
                                : isClt
                                ? 'Profissional CLT remunerado por salário fixo. 100% retido no caixa da empresa.'
                                : `Comissão de ${formatCurrencyBRL((selectedAppointment.price * commPct) / 100)} liberada no extrato do profissional.`
                              }
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            onUpdateStatus(selectedAppointment.id, 'confirmed', { 
                              paymentStatus: 'pending',
                              paidAt: null,
                              paymentMethod: null
                            });
                            alert('Status financeiro reaberto como pendente.');
                            setSelectedAppointment(null);
                          }}
                          className="text-[11px] font-bold text-zinc-500 hover:text-zinc-800 underline cursor-pointer shrink-0 ml-2"
                        >
                          Reabrir
                        </button>
                      </div>
                    );
                  })() : selectedAppointment.status === 'completed' ? (
                    <div className="p-3 bg-amber-50 border border-amber-300 rounded-2xl flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-600 shrink-0" />
                        <div>
                          <strong className="text-amber-950 font-black block">
                            Serviço Executado pelo Profissional (Aguardando Caixa)
                          </strong>
                          <span className="text-[10px] text-amber-800 font-medium">
                            Confirme o recebimento abaixo para liberar a comissão.
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {selectedAppointment.notes && (
                <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-100 text-[11px] text-zinc-600">
                  <span className="font-bold text-zinc-800 block mb-0.5">Anotações:</span>
                  {selectedAppointment.notes}
                </div>
              )}

              {/* Botões de Ação */}
              <div className="space-y-2 pt-2">
                {selectedAppointment.status === 'blocked' ? (
                  <button
                    type="button"
                    onClick={() => {
                      onUpdateStatus(selectedAppointment.id, 'canceled');
                      alert('Horário desbloqueado com sucesso!');
                      setSelectedAppointment(null);
                    }}
                    className="w-full py-2.5 px-3 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-95 transition-all"
                  >
                    <Unlock className="h-4 w-4" />
                    <span>Desbloquear e Liberar Horário</span>
                  </button>
                ) : (
                  <>
                    {selectedAppointment.paymentStatus !== 'paid' && selectedAppointment.status !== 'canceled' && (
                      <button
                        type="button"
                        onClick={() => {
                          setCheckoutAppointment(selectedAppointment);
                        }}
                        className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl flex items-center justify-center gap-2 cursor-pointer shadow-sm shadow-emerald-600/20 active:scale-95 transition-all"
                      >
                        <DollarSign className="w-4 h-4" />
                        <span>Confirmar Recebimento / Baixar Caixa</span>
                      </button>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      {selectedAppointment.packageTotalSessions && (
                        <button
                          type="button"
                          onClick={() => {
                            onDeductPackageSession(selectedAppointment.id);
                            alert('Sessão baixada com sucesso!');
                            setSelectedAppointment(null);
                          }}
                          className="py-2.5 px-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-95 transition-all"
                        >
                          <CheckSquare className="h-4 w-4" />
                          <span>Baixar Sessão</span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPosAppointment(selectedAppointment);
                          setIsQuickPosOpen(true);
                        }}
                        className="py-2.5 px-3 bg-neutral-900 hover:bg-black text-white font-black rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-95 transition-all"
                      >
                        <span>💈</span>
                        <span>Comanda & PDV</span>
                      </button>

                      {onOpenReceipt && (
                        <button
                          type="button"
                          onClick={() => {
                            onOpenReceipt(selectedAppointment);
                            setSelectedAppointment(null);
                          }}
                          className="py-2.5 px-3 bg-brand hover:bg-brand-dark text-white font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer shadow-xs active:scale-95 transition-all"
                        >
                          <FileText className="h-4 w-4" />
                          <span>Gerar Recibo PIX</span>
                        </button>
                      )}

                      {selectedAppointment.status !== 'no_show' && selectedAppointment.paymentStatus !== 'paid' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('Deseja marcar este agendamento como Não Compareceu (No-Show)?')) {
                              onUpdateStatus(selectedAppointment.id, 'no_show');
                              setSelectedAppointment(null);
                            }
                          }}
                          className="py-2.5 px-3 bg-rose-50 hover:bg-rose-100 text-rose-800 font-bold rounded-xl border border-rose-200 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-95"
                        >
                          <X className="h-4 w-4 text-rose-600" />
                          <span>No-Show</span>
                        </button>
                      )}

                      {selectedAppointment.status !== 'canceled' && selectedAppointment.paymentStatus !== 'paid' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm('Tem certeza que deseja cancelar este agendamento?')) {
                              onUpdateStatus(selectedAppointment.id, 'canceled');
                              setSelectedAppointment(null);
                            }
                          }}
                          className="py-2.5 px-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold rounded-xl flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-95"
                        >
                          <Trash2 className="h-4 w-4 text-zinc-500" />
                          <span>Cancelar</span>
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL: BAIXA DE CAIXA / CONFIRMAÇÃO DE RECEBIMENTO ANTI-FRAUDE           */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {checkoutAppointment && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto">
          <div className="w-full sm:max-w-md bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900">Baixa de Caixa / Recebimento</h3>
                  <p className="text-[11px] text-zinc-500">Confirmação financeira para liberação de comissão.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setCheckoutAppointment(null)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto overscroll-contain py-2 pr-1 flex-1 min-h-0">
              <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] text-zinc-400 uppercase font-black tracking-wider block">Cliente</span>
                  <strong className="text-sm text-zinc-900 font-black">{checkoutAppointment.clientName}</strong>
                  <span className="text-xs text-brand font-bold block mt-0.5">{checkoutAppointment.serviceName}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-zinc-400 uppercase font-black tracking-wider block">Total a Receber</span>
                  <strong className="text-xl text-emerald-700 font-black">{formatCurrencyBRL(checkoutAppointment.price)}</strong>
                </div>
              </div>

              <div className="pt-2 border-t border-zinc-200/60 grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-white p-2 rounded-xl border border-zinc-100">
                  <span className="text-zinc-400 block text-[10px]">Profissional</span>
                  <strong className="text-zinc-800 font-bold">{checkoutAppointment.staffMemberName || 'Profissional'}</strong>
                </div>
                <div className="bg-white p-2 rounded-xl border border-zinc-100 text-right">
                  <span className="text-zinc-400 block text-[10px]">Comissão ({checkoutAppointment.commissionPercent || 50}%)</span>
                  <strong className="text-purple-800 font-black">
                    {formatCurrencyBRL((checkoutAppointment.price * (checkoutAppointment.commissionPercent || 50)) / 100)}
                  </strong>
                </div>
              </div>
            </div>

            {/* SELEÇÃO DA FORMA DE PAGAMENTO */}
            <div className="space-y-2">
              <span className="text-[10px] text-zinc-400 uppercase font-black tracking-wider block">
                Forma de Recebimento no Caixa:
              </span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'pix', label: 'PIX', icon: '⚡', desc: 'À vista' },
                  { id: 'dinheiro', label: 'Dinheiro', icon: '💵', desc: 'Espécie' },
                  { id: 'cartao_debito', label: 'Cartão Débito', icon: '💳', desc: 'Maquininha' },
                  { id: 'cartao_credito', label: 'Cartão Crédito', icon: '💳', desc: 'Maquininha' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCheckoutPaymentMethod(opt.id as any)}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex items-center gap-2.5 ${
                      checkoutPaymentMethod === opt.id
                        ? 'bg-emerald-50 border-emerald-500 text-emerald-950 ring-2 ring-emerald-500/20 shadow-xs'
                        : 'bg-white border-zinc-200 hover:bg-zinc-50 text-zinc-700'
                    }`}
                  >
                    <span className="text-lg">{opt.icon}</span>
                    <div>
                      <strong className="text-xs font-black block">{opt.label}</strong>
                      <span className="text-[10px] text-zinc-400 font-medium">{opt.desc}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setCheckoutAppointment(null)}
                className="px-4 py-2.5 text-zinc-500 hover:text-zinc-800 font-bold cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  onUpdateStatus(checkoutAppointment.id, 'completed', {
                    paymentStatus: 'paid',
                    paymentMethod: checkoutPaymentMethod,
                    paidAt: new Date().toISOString(),
                    executedAt: checkoutAppointment.executedAt || new Date().toISOString()
                  });
                  alert(`Recebimento de ${formatCurrencyBRL(checkoutAppointment.price)} via ${checkoutPaymentMethod.toUpperCase()} confirmado no caixa! Comissão liberada para ${checkoutAppointment.staffMemberName || 'o profissional'}.`);
                  setCheckoutAppointment(null);
                  setSelectedAppointment(null);
                }}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-emerald-600/20 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5"
              >
                <Check className="w-4 h-4" />
                <span>Confirmar Recebimento</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comanda Rápida e Cross-Selling PDV */}
      <BarberQuickPosModal
        isOpen={isQuickPosOpen}
        onClose={() => {
          setIsQuickPosOpen(false);
          setSelectedPosAppointment(null);
        }}
        appointment={selectedPosAppointment}
        providerName={providerName}
        providerPixKey={providerPixKey || ''}
        providerCity={providerCity || 'São Paulo'}
        onCompleteWithReceipt={(comanda) => {
          alert(`Comanda de ${comanda.clientName} finalizada com sucesso! Total: ${formatCurrencyBRL(comanda.totalAmount)}`);
          if (selectedPosAppointment) {
            const extraItemsText = comanda.items && comanda.items.length > 0
              ? ` | Itens Comanda: ${comanda.items.map(it => `${it.quantity}x ${it.name} (${formatCurrencyBRL(it.price * it.quantity)})`).join(', ')}`
              : '';
            const mergedNotes = selectedPosAppointment.notes 
              ? `${selectedPosAppointment.notes}${extraItemsText}`
              : (extraItemsText.replace(/^ \| /, '') || undefined);

            onUpdateStatus(selectedPosAppointment.id, 'completed', {
              paymentStatus: 'paid',
              paymentMethod: comanda.paymentMethod,
              paidAt: new Date().toISOString(),
              executedAt: new Date().toISOString(),
              price: comanda.totalAmount,
              notes: mergedNotes
            });

            if (onOpenReceipt) {
              onOpenReceipt({
                ...selectedPosAppointment,
                price: comanda.totalAmount,
                paymentStatus: 'paid',
                paymentMethod: comanda.paymentMethod,
                notes: mergedNotes
              });
            }
          }
          setIsQuickPosOpen(false);
          setSelectedPosAppointment(null);
          setSelectedAppointment(null);
        }}
      />

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* MODAL: CONFIGURAÇÃO DE HORÁRIO DE ATENDIMENTO DO SALÃO (FLEXÍVEL)        */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {isBusinessHoursModalOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-950/75 backdrop-blur-xs flex items-center justify-center p-2.5 sm:p-4 overflow-y-auto animate-in fade-in duration-150">
          <div className="w-full sm:max-w-xl bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border border-zinc-200/90 text-xs max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col overflow-hidden my-auto">
            {/* Cabeçalho */}
            <div className="flex items-center justify-between border-b border-zinc-100 pb-3 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl bg-sky-100 text-sky-800 flex items-center justify-center shrink-0">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-black text-zinc-900">Horário de Funcionamento do Salão</h3>
                  <p className="text-[10px] text-zinc-500 font-medium">
                    Restringe a grade da agenda, atendentes e agendamento dos clientes
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsBusinessHoursModalOpen(false)}
                className="p-1.5 text-zinc-400 hover:text-zinc-700 rounded-full cursor-pointer hover:bg-zinc-100 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto overscroll-contain py-2 pr-1 flex-1 min-h-0">
              {/* Presets Rápidos de Salão */}
              <div className="space-y-1.5">
              <label className="text-[10px] text-zinc-400 uppercase font-black tracking-widest block">
                Sugestões Rápidas (1 Toque):
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {[
                  {
                    title: 'Padrão Salão',
                    desc: 'Ter a Sex 09h-19h • Sáb 08h-17h',
                    days: {
                      seg: { isOpen: false, start: '09:00', end: '19:00' },
                      ter: { isOpen: true, start: '09:00', end: '19:00' },
                      qua: { isOpen: true, start: '09:00', end: '19:00' },
                      qui: { isOpen: true, start: '09:00', end: '19:00' },
                      sex: { isOpen: true, start: '09:00', end: '19:00' },
                      sab: { isOpen: true, start: '08:00', end: '17:00' },
                      dom: { isOpen: false, start: '08:00', end: '14:00' }
                    }
                  },
                  {
                    title: 'Terça a Sábado',
                    desc: '08:00 às 19:00 (Dom e Seg Fechado)',
                    days: {
                      seg: { isOpen: false, start: '08:00', end: '19:00' },
                      ter: { isOpen: true, start: '08:00', end: '19:00' },
                      qua: { isOpen: true, start: '08:00', end: '19:00' },
                      qui: { isOpen: true, start: '08:00', end: '19:00' },
                      sex: { isOpen: true, start: '08:00', end: '19:00' },
                      sab: { isOpen: true, start: '08:00', end: '19:00' },
                      dom: { isOpen: false, start: '08:00', end: '14:00' }
                    }
                  },
                  {
                    title: 'Segunda a Sábado',
                    desc: '08:00 às 19:00 (Dom Fechado)',
                    days: {
                      seg: { isOpen: true, start: '08:00', end: '19:00' },
                      ter: { isOpen: true, start: '08:00', end: '19:00' },
                      qua: { isOpen: true, start: '08:00', end: '19:00' },
                      qui: { isOpen: true, start: '08:00', end: '19:00' },
                      sex: { isOpen: true, start: '08:00', end: '19:00' },
                      sab: { isOpen: true, start: '08:00', end: '19:00' },
                      dom: { isOpen: false, start: '08:00', end: '14:00' }
                    }
                  },
                  {
                    title: 'Horário Comercial',
                    desc: 'Seg a Sex 08h-18h • Sáb 08h-13h',
                    days: {
                      seg: { isOpen: true, start: '08:00', end: '18:00' },
                      ter: { isOpen: true, start: '08:00', end: '18:00' },
                      qua: { isOpen: true, start: '08:00', end: '18:00' },
                      qui: { isOpen: true, start: '08:00', end: '18:00' },
                      sex: { isOpen: true, start: '08:00', end: '18:00' },
                      sab: { isOpen: true, start: '08:00', end: '13:00' },
                      dom: { isOpen: false, start: '08:00', end: '12:00' }
                    }
                  }
                ].map(preset => (
                  <button
                    key={preset.title}
                    type="button"
                    onClick={() => {
                      const newWeekly: WeeklyBusinessHours = {
                        version: 2,
                        summary: generateHumanSummary(preset.days as any),
                        days: preset.days as any
                      };
                      setTempWeeklyHours(newWeekly);
                      setManualSummaryInput(newWeekly.summary);
                    }}
                    className="p-2.5 rounded-xl border border-zinc-200 bg-zinc-50 hover:bg-sky-50/60 hover:border-sky-300 text-left transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-black text-[11px] text-zinc-900 group-hover:text-sky-950">
                        {preset.title}
                      </span>
                      <Zap className="h-3 w-3 text-zinc-400 group-hover:text-sky-600" />
                    </div>
                    <span className="text-[10px] text-zinc-500 block truncate mt-0.5">
                      {preset.desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Alternador de Modo de Ajuste */}
            <div className="flex bg-zinc-100 p-1 rounded-xl gap-1">
              <button
                type="button"
                onClick={() => setModalHoursTab('grouped')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  modalHoursTab === 'grouped'
                    ? 'bg-white text-zinc-900 shadow-2xs font-black'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                Por Blocos (Semana vs Sábado)
              </button>
              <button
                type="button"
                onClick={() => setModalHoursTab('detailed')}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  modalHoursTab === 'detailed'
                    ? 'bg-white text-zinc-900 shadow-2xs font-black'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                Dia a Dia (Detalhado)
              </button>
            </div>

            {/* MODO 1: CONFIGURAÇÃO POR BLOCOS (SEMANA VS FINAL DE SEMANA) */}
            {modalHoursTab === 'grouped' && (
              <div className="space-y-3">
                {/* Bloco 1: Terça a Sexta */}
                <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <strong className="text-xs font-black text-zinc-900 block">Terça a Sexta-feira</strong>
                      <span className="text-[10px] text-zinc-500 font-medium">Dias úteis principais do salão</span>
                    </div>
                    <span className="text-[10px] font-black text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-md">
                      Aberto
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <div>
                      <span className="text-[10px] text-zinc-400 font-bold block mb-1">Horário de Início:</span>
                      <input
                        type="time"
                        value={tempWeeklyHours.days.ter.start}
                        onChange={e => {
                          const val = e.target.value;
                          setTempWeeklyHours(prev => {
                            const days = { ...prev.days };
                            ['ter', 'qua', 'qui', 'sex'].forEach(k => {
                              days[k as DayKey] = { ...days[k as DayKey], isOpen: true, start: val };
                            });
                            const summary = generateHumanSummary(days);
                            setManualSummaryInput(summary);
                            return { ...prev, days, summary };
                          });
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800"
                      />
                    </div>
                    <div>
                      <span className="text-[10px] text-zinc-400 font-bold block mb-1">Horário de Término:</span>
                      <input
                        type="time"
                        value={tempWeeklyHours.days.ter.end}
                        onChange={e => {
                          const val = e.target.value;
                          setTempWeeklyHours(prev => {
                            const days = { ...prev.days };
                            ['ter', 'qua', 'qui', 'sex'].forEach(k => {
                              days[k as DayKey] = { ...days[k as DayKey], isOpen: true, end: val };
                            });
                            const summary = generateHumanSummary(days);
                            setManualSummaryInput(summary);
                            return { ...prev, days, summary };
                          });
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800"
                      />
                    </div>
                  </div>
                </div>

                {/* Bloco 2: Sábado */}
                <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <strong className="text-xs font-black text-zinc-900 block">Sábado (Fim de Semana)</strong>
                      <span className="text-[10px] text-zinc-500 font-medium">Horário especial de maior movimento</span>
                    </div>
                    <div className="inline-flex p-0.5 bg-zinc-200/80 rounded-xl shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setTempWeeklyHours(prev => {
                            const days = { ...prev.days, sab: { ...prev.days.sab, isOpen: true } };
                            const summary = generateHumanSummary(days);
                            setManualSummaryInput(summary);
                            return { ...prev, days, summary };
                          });
                        }}
                        className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                          tempWeeklyHours.days.sab.isOpen
                            ? 'bg-emerald-600 text-white shadow-xs'
                            : 'text-zinc-600 hover:text-zinc-900'
                        }`}
                      >
                        Aberto
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setTempWeeklyHours(prev => {
                            const days = { ...prev.days, sab: { ...prev.days.sab, isOpen: false } };
                            const summary = generateHumanSummary(days);
                            setManualSummaryInput(summary);
                            return { ...prev, days, summary };
                          });
                        }}
                        className={`px-3 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                          !tempWeeklyHours.days.sab.isOpen
                            ? 'bg-zinc-700 text-white shadow-xs'
                            : 'text-zinc-600 hover:text-zinc-900'
                        }`}
                      >
                        Fechado
                      </button>
                    </div>
                  </div>

                  {tempWeeklyHours.days.sab.isOpen ? (
                    <div className="grid grid-cols-2 gap-2 pt-1">
                      <div>
                        <span className="text-[10px] text-zinc-400 font-bold block mb-1">Horário de Início:</span>
                        <input
                          type="time"
                          value={tempWeeklyHours.days.sab.start}
                          onChange={e => {
                            const val = e.target.value;
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, sab: { ...prev.days.sab, start: val } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800"
                        />
                      </div>
                      <div>
                        <span className="text-[10px] text-zinc-400 font-bold block mb-1">Horário de Término:</span>
                        <input
                          type="time"
                          value={tempWeeklyHours.days.sab.end}
                          onChange={e => {
                            const val = e.target.value;
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, sab: { ...prev.days.sab, end: val } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className="w-full px-2.5 py-1.5 bg-white border border-zinc-200 rounded-xl text-xs font-bold text-zinc-800"
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-zinc-500 font-medium italic pt-1 flex items-center gap-1.5">
                      <span>🌴</span> Folga da equipe (Sem atendimento no sábado)
                    </p>
                  )}
                </div>

                {/* Bloco 3: Segunda-feira e Domingo */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {/* Segunda-feira */}
                  <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong className="text-xs font-black text-zinc-900 block">Segunda-feira</strong>
                        <span className="text-[10px] text-zinc-500 font-medium">Início de semana</span>
                      </div>
                      <div className="inline-flex p-0.5 bg-zinc-200/80 rounded-xl shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, seg: { ...prev.days.seg, isOpen: true } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                            tempWeeklyHours.days.seg.isOpen
                              ? 'bg-emerald-600 text-white shadow-xs'
                              : 'text-zinc-600 hover:text-zinc-900'
                          }`}
                        >
                          Aberto
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, seg: { ...prev.days.seg, isOpen: false } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                            !tempWeeklyHours.days.seg.isOpen
                              ? 'bg-zinc-700 text-white shadow-xs'
                              : 'text-zinc-600 hover:text-zinc-900'
                          }`}
                        >
                          Fechado
                        </button>
                      </div>
                    </div>
                    {tempWeeklyHours.days.seg.isOpen ? (
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <div>
                          <span className="text-[10px] text-zinc-400 font-bold block mb-1">Início:</span>
                          <input
                            type="time"
                            value={tempWeeklyHours.days.seg.start}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, seg: { ...prev.days.seg, start: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="w-full px-2 py-1 bg-white border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-zinc-400 font-bold block mb-1">Término:</span>
                          <input
                            type="time"
                            value={tempWeeklyHours.days.seg.end}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, seg: { ...prev.days.seg, end: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="w-full px-2 py-1 bg-white border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-500 font-medium italic pt-1 flex items-center gap-1.5">
                        <span>🌴</span> Folga (Sem expediente)
                      </p>
                    )}
                  </div>

                  {/* Domingo */}
                  <div className="p-3 bg-zinc-50 rounded-2xl border border-zinc-200/80 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <strong className="text-xs font-black text-zinc-900 block">Domingo</strong>
                        <span className="text-[10px] text-zinc-500 font-medium">Fim de semana</span>
                      </div>
                      <div className="inline-flex p-0.5 bg-zinc-200/80 rounded-xl shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, dom: { ...prev.days.dom, isOpen: true } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                            tempWeeklyHours.days.dom.isOpen
                              ? 'bg-emerald-600 text-white shadow-xs'
                              : 'text-zinc-600 hover:text-zinc-900'
                          }`}
                        >
                          Aberto
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTempWeeklyHours(prev => {
                              const days = { ...prev.days, dom: { ...prev.days.dom, isOpen: false } };
                              const summary = generateHumanSummary(days);
                              setManualSummaryInput(summary);
                              return { ...prev, days, summary };
                            });
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-black transition-all cursor-pointer ${
                            !tempWeeklyHours.days.dom.isOpen
                              ? 'bg-zinc-700 text-white shadow-xs'
                              : 'text-zinc-600 hover:text-zinc-900'
                          }`}
                        >
                          Fechado
                        </button>
                      </div>
                    </div>
                    {tempWeeklyHours.days.dom.isOpen ? (
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <div>
                          <span className="text-[10px] text-zinc-400 font-bold block mb-1">Início:</span>
                          <input
                            type="time"
                            value={tempWeeklyHours.days.dom.start}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, dom: { ...prev.days.dom, start: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="w-full px-2 py-1 bg-white border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                        </div>
                        <div>
                          <span className="text-[10px] text-zinc-400 font-bold block mb-1">Término:</span>
                          <input
                            type="time"
                            value={tempWeeklyHours.days.dom.end}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, dom: { ...prev.days.dom, end: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="w-full px-2 py-1 bg-white border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-500 font-medium italic pt-1 flex items-center gap-1.5">
                        <span>🌴</span> Folga (Sem expediente)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* MODO 2: CONFIGURAÇÃO DIA A DIA INDIVIDUAL */}
            {modalHoursTab === 'detailed' && (
              <div className="space-y-2">
                {DAY_KEYS_ORDERED.map(key => {
                  const daySched = tempWeeklyHours.days[key];
                  return (
                    <div
                      key={key}
                      className={`p-2.5 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
                        daySched.isOpen
                          ? 'bg-white border-zinc-200 shadow-2xs'
                          : 'bg-zinc-50/80 border-zinc-100 opacity-75'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-36">
                        <div className="inline-flex p-0.5 bg-zinc-200/80 rounded-xl shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, [key]: { ...prev.days[key], isOpen: true } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className={`px-2 py-1 rounded-lg text-[9px] font-black transition-all cursor-pointer ${
                              daySched.isOpen
                                ? 'bg-emerald-600 text-white shadow-xs'
                                : 'text-zinc-600 hover:text-zinc-900'
                            }`}
                          >
                            Aberto
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, [key]: { ...prev.days[key], isOpen: false } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className={`px-2 py-1 rounded-lg text-[9px] font-black transition-all cursor-pointer ${
                              !daySched.isOpen
                                ? 'bg-zinc-700 text-white shadow-xs'
                                : 'text-zinc-600 hover:text-zinc-900'
                            }`}
                          >
                            Fechado
                          </button>
                        </div>
                        <strong className="text-xs font-black text-zinc-900">
                          {DAY_LABELS[key].name}
                        </strong>
                      </div>

                      {daySched.isOpen ? (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-zinc-400 font-bold">Das</span>
                          <input
                            type="time"
                            value={daySched.start}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, [key]: { ...prev.days[key], start: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                          <span className="text-[10px] text-zinc-400 font-bold">às</span>
                          <input
                            type="time"
                            value={daySched.end}
                            onChange={e => {
                              const val = e.target.value;
                              setTempWeeklyHours(prev => {
                                const days = { ...prev.days, [key]: { ...prev.days[key], end: val } };
                                const summary = generateHumanSummary(days);
                                setManualSummaryInput(summary);
                                return { ...prev, days, summary };
                              });
                            }}
                            className="px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-bold text-zinc-800"
                          />
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold text-zinc-500 italic flex items-center gap-1">
                          <span>🌴</span> Sem expediente (Folga)
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Resumo Final Formatado para o Cliente */}
            <div className="space-y-1.5 pt-1 border-t border-zinc-100">
              <div className="flex items-center justify-between">
                <label className="text-[10px] text-zinc-500 uppercase font-black tracking-widest block">
                  Texto exibido na agenda e para os clientes:
                </label>
                <button
                  type="button"
                  onClick={() => setManualSummaryInput(generateHumanSummary(tempWeeklyHours.days))}
                  className="text-[10px] text-brand hover:underline font-bold cursor-pointer"
                >
                  Regenerar automático
                </button>
              </div>
              <input
                type="text"
                value={manualSummaryInput}
                onChange={e => setManualSummaryInput(e.target.value)}
                placeholder="Ex: Terça a Sexta: 09:00 às 19:00 • Sábado: 08:00 às 17:00"
                className="w-full px-3.5 py-2.5 bg-zinc-50 border border-zinc-300 rounded-xl text-xs font-black text-zinc-900 focus:bg-white focus:ring-2 focus:ring-brand focus:border-brand transition-all"
              />
              <p className="text-[10px] text-zinc-400 font-medium">
                Sincronizado automaticamente com os horários selecionados acima.
              </p>
            </div>
            </div>

            {/* Ações / Botões */}
            <div className="flex items-center justify-end gap-2 border-t border-zinc-100 pt-3 shrink-0 bg-white">
              <button
                type="button"
                onClick={() => setIsBusinessHoursModalOpen(false)}
                className="px-4 py-2 text-zinc-500 hover:text-zinc-800 font-bold rounded-xl cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isSavingHours || !manualSummaryInput.trim()}
                onClick={() => handleSaveWeeklyHours(tempWeeklyHours, manualSummaryInput)}
                className="px-5 py-2.5 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 active:scale-95 transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
              >
                {isSavingHours ? (
                  <span>Salvando...</span>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Salvar Horário</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
